import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Modal, Spin, Switch, App } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  PaperClipOutlined,
  FormOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import styles from "./OrderForm.module.css";
import { OrderSteps } from "../components/OrderSteps";
import type { OrderStepItem } from "../components/OrderSteps";
import { ProductListContent } from "../components/ProductListContent";
import { PersonForm, PersonFormRef } from "./OrderForms/PersonForm";
import {
  BeneficiaryForm,
  BeneficiaryFormRef,
} from "./OrderForms/BeneficiaryForm";
import { FinancialForm, FinancialFormRef } from "./OrderForms/FinancialForm";
import {
  ProductInsuranceForm,
  ProductInsuranceFormRef,
} from "./OrderForms/ProductInsuranceForm";
import { OrderAttachmentForm } from "./OrderForms/OrderAttachmentForm";
import { OrderSignatureForm } from "./OrderForms/OrderSignatureForm";
import type { OrderSignatureFormRef } from "./OrderForms/OrderSignatureForm";
import type {
  OrderVO,
  PolicyholderVO,
  BeneficiaryVO,
  OrderFinancialVO,
} from "./OrderForms/orders";
import CustomButton from "../components/CustomButton";
import { useIOSConfirm } from "../hooks";
import {
  createOrder,
  getCustomerDetail,
  getOrderDetail,
  getProductInfo,
  updatePolicyholder,
  updateSecondaryPolicyholder,
  updateInsured,
  updateSecondInsured,
  updateFinancial,
  updateFinancialAnalysis,
  updateOrderProduct,
  confirmOrderSigned,
  OrderStatus,
  OrderAuditStatus,
  submitOrder,
  convertEmptyStringToNull,
  useMetadataStore,
  getSignatureConfig,
  downloadOrderFormPdf,
  useCountryStore,
  locate,
  checkNeedLocate,
  IdType,
  getUserInfo,
  updateAdvisorOrderReferrer,
  useConfigStore,
} from "@ifa/utils";
import type { UserInfo } from "@ifa/utils";
import { cloneDeep, isEqual } from "lodash";
import {
  INITIAL_PRODUCT_PLAN,
  createInitialPolicyholder,
  createInitialInsured,
} from "./OrderForms/constants";
import type {
  OrderBasicDTO,
  CustomerDTO,
  InsuredDTO,
  ProductListVO,
  SignatureConfigItem,
  SignatureConfigResponse,
} from "@ifa/utils";
import { parseIdCard } from "../utils/idCardParser";
import { shouldAutoFillSalutationFromGender } from "../utils/salutationRules";
import { useAdvisorStore } from "../stores/useAdvisorStore";
import {
  cacheOrderPageStatus,
  getOrderDetailForPage,
  readCachedOrderPageStatus,
} from "../services/orderDetail";
import { isZuuFinancialStepVariant } from "./order-fna/fnaVariant";
import { normalizeZuuFnaQuestionList } from "./order-fna/zuuFnaFieldMap";
import { normalizeOrderFinancialForDisplay } from "./order-fna/zuuFnaDetailData.js";
import { getQuestionValueFromListByVariant } from "./order-fna/fnaQuestionAccess";
import {
  extractFnaDataByVariant,
  sanitizeLegacyFnaData,
  sanitizeZuuFnaData,
} from "./order-fna/fnaPayloadIsolation";
import { ensureZuuStep2DefaultValues } from "./order-fna/zuuStep2Defaults";
import {
  shouldShowZuuRetirementFields,
  validateZuuFinancialInfoStep2,
  validateZuuPremiumFinancing,
} from "./order-fna/zuuValidation";
import {
  getRequestedOrderStep,
  ORDER_FORM_SIGNATURE_STEP_INDEX,
} from "./orderFormStepUtils";
import { deriveOrderEditTopStep } from "./orderProgress.js";

let activeOrderFormLocationSessionId = 0;
let activeOrderFormLocationModalDestroy: (() => void) | null = null;

export function OrderForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const locationState = (location.state || {}) as Record<string, any>;
  const readOnly = location.state?.readOnly || false;
  const { allCountries, fetchCountryList } = useCountryStore();
  const { confirm } = useIOSConfirm();
  const { modal, message } = App.useApp();
  const brandId = useConfigStore((state) => state.brandId);
  const tenantCode = useConfigStore((state) => state.tenantProfile?.tenantCode);
  const isZuuFinancialStepMode = isZuuFinancialStepVariant({
    brandId,
    tenantCode,
  });
  const shouldAutoFillSalutation = shouldAutoFillSalutationFromGender({
    brandId,
    tenantCode,
  });

  useEffect(() => {
    fetchCountryList();
  }, []);

  // 获取当前登录用户信息（用于业务代表签名姓名）
  useEffect(() => {
    const uid = localStorage.getItem("uid");
    if (uid) {
      getUserInfo({ uid: Number(uid) }).then(setCurrentUser).catch(console.error);
    }
  }, []);

  const getCountryName = (code?: string) => {
    if (!code) return "N/A";
    const country = allCountries.find((c) => c.countryCode === code);
    return country?.cname || country?.name || code;
  };

  // Centralized State for the entire Order Form
  const [orderData, setOrderData] = useState<OrderVO>();

  // 当前登录用户信息（用于业务代表签名姓名）
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [savedAdvisorUid, setSavedAdvisorUid] = useState<number | undefined>();
  const {
    advisors,
    loading: advisorLoading,
    fetchCurrentTeamAdvisors,
  } = useAdvisorStore();

  const getAdvisorUid = useCallback((data?: Partial<OrderVO> | null) => {
    const uid =
      data?.referrerUid ??
      data?.advisorUid ??
      (data as any)?.referrerId;

    return uid == null ? undefined : Number(uid);
  }, []);

  const syncSavedAdvisorUid = useCallback(
    (data?: Partial<OrderVO> | null) => {
      setSavedAdvisorUid(getAdvisorUid(data));
    },
    [getAdvisorUid],
  );

  useEffect(() => {
    fetchCurrentTeamAdvisors(true);
  }, [fetchCurrentTeamAdvisors]);

  // 计算各签名类型对应的姓名
  const signatureNames = useMemo(() => {
    const names: { [key: string]: string } = {};

    // 投保人姓名
    if (orderData?.policyholder) {
      const ph = orderData.policyholder;
      const chineseName = (ph.basic?.chineseSurname || '') + (ph.basic?.chineseGivenName || '');
      names.POLICYHOLDER = chineseName || ph.basic?.name || '';
    }

    // 被保人姓名
    if (orderData?.insured) {
      const ins = orderData.insured;
      const chineseName = (ins.basic?.chineseSurname || '') + (ins.basic?.chineseGivenName || '');
      names.INSURED = chineseName || ins.basic?.name || '';
    }

    // 业务代表姓名（当前登录用户）
    if (currentUser) {
      names.ADVISOR = currentUser.name || '';
    }

    // 见证人姓名（如果有）
    if ((orderData as any)?.referrerName || orderData?.advisorName) {
      names.WITNESS =
        (orderData as any)?.referrerName || orderData?.advisorName || "";
    }

    return names;
  }, [orderData, currentUser]);

  const currentAdvisorUid = getAdvisorUid(orderData);
  const advisorFallbackLabel =
    (orderData as any)?.referrerName || orderData?.advisorName;
  const advisorOptions = useMemo(() => {
    const options = advisors
      .filter((advisor) => advisor.uid != null)
      .map((advisor) => ({
        label: `${advisor.name || "-"}${advisor.iaCode ? ` (${advisor.iaCode})` : ""}`,
        value: advisor.uid as number,
      }));

    if (
      currentAdvisorUid != null &&
      !options.some((option) => option.value === currentAdvisorUid)
    ) {
      if (advisorFallbackLabel) {
        options.push({
          label: advisorFallbackLabel,
          value: currentAdvisorUid,
        });
      }
    }

    return options;
  }, [
    advisors,
    currentAdvisorUid,
    advisorFallbackLabel,
  ]);

  useEffect(() => {
    if (orderId || !currentUser || currentAdvisorUid != null) {
      return;
    }

    setOrderData((prev) => ({
      ...prev,
      advisorUid: currentUser.uid,
      advisorName: currentUser.name || prev?.advisorName || "",
      advisorTeamName: currentUser.teamName || prev?.advisorTeamName || "",
      advisorTid: currentUser.teamId,
      referrerUid: currentUser.uid,
      referrerId: currentUser.uid,
      referrerName: currentUser.name || (prev as any)?.referrerName || "",
      referrerTeamName:
        currentUser.teamName || (prev as any)?.referrerTeamName || "",
      referrerTid: currentUser.teamId,
    }));
    setSavedAdvisorUid(currentUser.uid);
  }, [currentAdvisorUid, currentUser, orderId]);

  // 批改模式：从 OrderCorrectionList 进入的编辑流程，只展示到产品投保步骤
  const isCorrectionMode = location.state?.from === "correction";

  useEffect(() => {
    console.log("[OrderForm] route state on entry", {
      pathname: location.pathname,
      from: location.state?.from,
      hasLocationState: !!location.state,
      isCorrectionMode,
      locationState: location.state,
    });
  }, [isCorrectionMode, location.pathname, location.state]);
  const isCorrectionEditRoute =
    location.pathname.includes("/correction/") && location.pathname.endsWith("/edit");
  // 草稿模式：订单状态为草稿
  const isDraftMode = orderData?.orderStatus === OrderStatus.Draft;
  const isPendingSignatureMode =
    orderData?.orderStatus === OrderStatus.PendingSignature;
  // 订单审核模式：订单状态为订单审核
  const isOrderAuditMode = orderData?.orderStatus === OrderStatus.OrderAudit;
  const canEditAdvisor =
    !readOnly && (!orderId || isDraftMode || isPendingSignatureMode);
  const cachedPageStatus = orderId ? readCachedOrderPageStatus(orderId) : {};
  const pageOrderStatus =
    orderData?.orderStatus ??
    locationState.orderStatus ??
    cachedPageStatus.orderStatus;
  const pageOrderAuditStatus =
    orderData?.orderAuditStatus ??
    locationState.orderAuditStatus ??
    cachedPageStatus.orderAuditStatus;
  const allowMainProductSelectionInOrderAuditEdit =
    !readOnly &&
    pageOrderStatus === OrderStatus.OrderAudit &&
    pageOrderAuditStatus === OrderAuditStatus.PendingModification;

  // 添加标志来跟踪是否正在初始化（加载数据或应用默认值）
  const isInitializingRef = useRef(false);
  // 追踪初始化调用次数，用于确保所有子组件的 useEffect 都已完成
  const initCallCountRef = useRef(0);

  const [enablePolicyholder2, setEnablePolicyholder2] = useState(false);
  const [enableInsured2, setEnableInsured2] = useState(false);

  useEffect(() => {
    if (!orderId) return;

    if (locationState.orderStatus || locationState.orderAuditStatus) {
      cacheOrderPageStatus(orderId, {
        orderStatus: locationState.orderStatus,
        orderAuditStatus: locationState.orderAuditStatus,
      });
    }
  }, [orderId, locationState.orderAuditStatus, locationState.orderStatus]);

  // Change status tracking for each section
  const [sectionChanges, setSectionChanges] = useState<{
    policyholder: boolean;
    policyholder2: boolean;
    insured: boolean;
    insured2: boolean;
    beneficiary: boolean;
    financial: boolean;
  }>({
    policyholder: false,
    policyholder2: false,
    insured: false,
    insured2: false,
    beneficiary: false,
    financial: false,
  });

  // Helper to mark section as changed
  const markSectionChanged = useCallback(
    (
      section: keyof typeof sectionChanges,
      changed: boolean = true,
    ) => {
      setSectionChanges((prev) => ({ ...prev, [section]: changed }));
    },
    [],
  );

  // Helper to reset all change states
  const resetAllChanges = () => {
    setSectionChanges({
      policyholder: false,
      policyholder2: false,
      insured: false,
      insured2: false,
      beneficiary: false,
      financial: false,
    });
  };

  // Financial Step Active Key (for Sidebar)
  const [currentFinancialStep, setCurrentFinancialStep] = useState<
    "financial-info" | "fna"
  >("financial-info");

  // Product Plan Active Key (for Sidebar)
  const [activeProductKey, setActiveProductKey] = useState("0");
  const [originalProductPlan, setOriginalProductPlan] = useState<any>(null);

  // Update baseline when switching tabs
  useEffect(() => {
    if (
      orderData?.productPlans &&
      orderData.productPlans[Number(activeProductKey)]
    ) {
      setOriginalProductPlan(
        cloneDeep(orderData.productPlans[Number(activeProductKey)]),
      );
    }
  }, [activeProductKey]);

  // Initial load baseline
  useEffect(() => {
    if (
      !originalProductPlan &&
      orderData?.productPlans &&
      orderData.productPlans[Number(activeProductKey)]
    ) {
      setOriginalProductPlan(
        cloneDeep(orderData.productPlans[Number(activeProductKey)]),
      );
    }
  }, [orderData]);

  const personFormRef = useRef<PersonFormRef>(null);
  const beneficiaryFormRef = useRef<BeneficiaryFormRef>(null);
  const financialFormRef = useRef<FinancialFormRef>(null);
  const productInsuranceFormRef = useRef<ProductInsuranceFormRef>(null);

  // Initialize enable state when orderData is loaded
  useEffect(() => {
    if (orderData) {
      if (!isEmptyPolicyholder(orderData.secondPolicyholder)) {
        setEnablePolicyholder2(true);
      }
      if (!isEmptyPolicyholder(orderData.secondInsured)) {
        setEnableInsured2(true);
      }
    }
  }, [orderData?.secondPolicyholder, orderData?.secondInsured]);

  // Product Selector Modal State
  const [productSelectorVisible, setProductSelectorVisible] = useState(false);
  const [selectorContext, setSelectorContext] = useState<{
    type: string;
    index?: number;
    rIndex?: number;
  } | null>(null);
  const [selectorProductType, setSelectorProductType] = useState<
    string | undefined
  >(undefined);
  const financialSelectorOrderFnaDTO = useMemo(() => {
    if (selectorContext?.type !== "financial" || !orderData?.financial) {
      return undefined;
    }

    return {
      ...orderData.financial,
      orderId: Number(orderId),
    };
  }, [orderData?.financial, orderId, selectorContext?.type]);

  const handleOpenSelector = (
    context: { type: string; index?: number; rIndex?: number },
    productType?: string,
  ) => {
    setSelectorContext(context);
    setSelectorProductType(productType);
    setProductSelectorVisible(true);
  };

  const handleAddProduct = () => {
    handleOpenSelector({ type: "productPlan" });
  };

  const removeProductPlan = (index: number) => {
    setOrderData((prev) => {
      if (!prev) return prev;
      const newPlans = (prev.productPlans || []).filter((_, i) => i !== index);
      return { ...prev, productPlans: newPlans };
    });
    const currentActive = Number(activeProductKey);
    if (currentActive === index) {
      setActiveProductKey("0");
    } else if (currentActive > index) {
      setActiveProductKey(String(currentActive - 1));
    }
  };

  const handleFinancialRequirementToggle = useCallback(
    async (value: boolean) => {
      updateOrderData("isFinancialInfoRequired", value);
      if (!value) {
        setCurrentFinancialStep("financial-info");
      }

      if (!orderId) {
        return;
      }

      try {
        const numericOrderId = parseInt(orderId, 10);
        const financialBasicData =
          value && isZuuFinancialStepMode
            ? ensureZuuStep2DefaultValues(excludeFnaData(orderData?.financial), {
                showRetirementFields: shouldShowZuuRetirementFields(
                  orderData?.policyholder,
                ),
              })
            : excludeFnaData(orderData?.financial);
        await updateFinancial(
          numericOrderId,
          convertEmptyStringToNull({
            ...financialBasicData,
            isFinancialInfoRequired: value,
            orderId: numericOrderId,
            draft: false,
            submit: false,
          }) as any,
        );
        await refreshOrder();
      } catch (error) {
        console.error("更新财务资料需求状态失败:", error);
      }
    },
    [isZuuFinancialStepMode, orderData?.financial, orderData?.policyholder, orderId],
  );

  const handleModalSelect = async (selectedProduct: ProductListVO) => {
    if (!selectedProduct) return;

    if (selectorContext?.type === "financial") {
      const index = selectorContext.index!;

      // Check for duplicate product selection before updating state
      const currentFinancial = orderData?.financial || {};
      const currentChooseProducts = currentFinancial.chooseProducts || [];
      const isDuplicate = currentChooseProducts.some(
        (item: any, i: number) =>
          i !== index && item.productCode === selectedProduct.productCode,
      );

      if (isDuplicate) {
        message.error("不能選擇重複的產品");
        setProductSelectorVisible(false);
        return;
      }

      setOrderData((prev) => {
        const data = prev || initialState?.orderData || ({} as any);
        const financial = data.financial || {};
        // Deep copy array and ensure objects are new
        const chooseProducts = (financial.chooseProducts || []).map(
          (item: any) => ({ ...item }),
        );

        // Ensure array size
        while (chooseProducts.length <= index) {
          chooseProducts.push({ productCode: null, choose: false });
        }

        // Update the selected product (保持原有勾选状态)
        chooseProducts[index] = {
          ...chooseProducts[index],
          productCode: selectedProduct.productCode,
          productName:
            selectedProduct.productName || selectedProduct.productCode,
          companyCode: selectedProduct.companyCode,
          region: selectedProduct.region || "",
          // 保持原有的勾选状态，如果之前已勾选则保持勾选
          choose: chooseProducts[index]?.choose || false,
        };

        return {
          ...data,
          financial: {
            ...financial,
            chooseProducts,
          },
        };
      });
    } else if (selectorContext?.type === "rider") {
      const { index, rIndex } = selectorContext;
      if (index !== undefined && rIndex !== undefined) {
        // 将产品详情添加到 ProductInsuranceForm 的 productDetailsMap 中
        productInsuranceFormRef.current?.addProductDetail(
          selectedProduct.productCode,
          selectedProduct,
        );

        setOrderData((prev) => {
          const data = prev || initialState?.orderData || ({} as any);
          const productPlans = (data.productPlans || []).map((p: any) => ({
            ...p,
          }));
          if (
            productPlans[index] &&
            productPlans[index].riders &&
            productPlans[index].riders[rIndex]
          ) {
            const riders = [...productPlans[index].riders];

            // 获取附加险产品可选项以实现自动填充
            const riderOptions = {
              currencies: selectedProduct.currencies || [],
              protectionPeriods: selectedProduct.policyPeriods || [],
              paymentTerms: selectedProduct.paymentTerm || [],
              frequencies: selectedProduct.frequencies || [],
            };

            riders[rIndex] = {
              ...riders[rIndex],
              productCode: selectedProduct.productCode,
              productName: selectedProduct.productName,
              // 附加险的保险公司必须与主险保险公司相同
              companyCode: productPlans[index].companyCode,
              companyName:
                productPlans[index].companyName ||
                productPlans[index].companyCode ||
                "",
              // 自动填充只有一个选项的字段
              currency:
                riderOptions.currencies.length === 1
                  ? riderOptions.currencies[0]
                  : undefined,
              protectionPeriod:
                riderOptions.protectionPeriods.length === 1
                  ? riderOptions.protectionPeriods[0]
                  : undefined,
              contributionYears:
                riderOptions.paymentTerms.length === 1
                  ? riderOptions.paymentTerms[0]
                  : undefined,
              paymentFrequency:
                riderOptions.frequencies.length === 1
                  ? riderOptions.frequencies[0]
                  : undefined,
            };
            productPlans[index].riders = riders;
          }
          return {
            ...data,
            productPlans,
          };
        });
      }
    } else if (
      selectorContext?.type === "productPlan" &&
      selectorContext.index !== undefined
    ) {
      // 编辑现有主险产品
      const index = selectorContext.index;

      // 检查是否选择了重复的产品（排除当前正在编辑的产品）
      const existingPlans = orderData?.productPlans || [];
      const isDuplicate = existingPlans.some(
        (plan, i) =>
          i !== index && plan.productCode === selectedProduct.productCode,
      );

      if (isDuplicate) {
        message.error("不能選擇重複的產品");
        setProductSelectorVisible(false);
        return;
      }

      // 将产品详情添加到 ProductInsuranceForm 的 productDetailsMap 中
      productInsuranceFormRef.current?.addProductDetail(
        selectedProduct.productCode,
        selectedProduct,
      );

      let region = "";
      if (selectedProduct.productCode) {
        try {
          const productInfo = await getProductInfo(selectedProduct.productCode);
          region = productInfo?.region || "";
        } catch (error) {
          console.error("Failed to fetch product info for region:", error);
        }
      }

      // 获取产品可选项以实现自动填充
      const productOptions = {
        currencies: selectedProduct.currencies || [],
        protectionPeriods: selectedProduct.policyPeriods || [],
        paymentTerms: selectedProduct.paymentTerm || [],
        frequencies: selectedProduct.frequencies || [],
      };

      setOrderData((prev) => {
        const data = prev || initialState?.orderData || ({} as any);
        const productPlans = [...(data.productPlans || [])];

        if (productPlans[index]) {
          // 更新现有产品，保留原有数据但更新产品信息
          productPlans[index] = {
            ...productPlans[index],
            productCode: selectedProduct.productCode,
            productName: selectedProduct.productName,
            companyCode: selectedProduct.companyCode,
            companyName:
              selectedProduct.companyName || selectedProduct.companyCode || "",
            region: region,
            // 自动填充只有一个选项的字段
            currency:
              productOptions.currencies.length === 1
                ? productOptions.currencies[0]
                : productPlans[index].currency,
            protectionPeriod:
              productOptions.protectionPeriods.length === 1
                ? productOptions.protectionPeriods[0]
                : productPlans[index].protectionPeriod,
            contributionYears:
              productOptions.paymentTerms.length === 1
                ? productOptions.paymentTerms[0]
                : productPlans[index].contributionYears,
            paymentFrequency:
              productOptions.frequencies.length === 1
                ? productOptions.frequencies[0]
                : productPlans[index].paymentFrequency,
          };
        }

        return {
          ...data,
          productPlans,
        };
      });
    } else {
      // Default logic (adding new productPlan)
      // Check for duplicate product in existing plans
      const existingPlans = orderData?.productPlans || [];
      const isDuplicate = existingPlans.some(
        (plan) => plan.productCode === selectedProduct.productCode,
      );

      if (isDuplicate) {
        message.error("不能選擇重複的產品");
        setProductSelectorVisible(false);
        return;
      }

      // 将产品详情添加到 ProductInsuranceForm 的 productDetailsMap 中
      productInsuranceFormRef.current?.addProductDetail(
        selectedProduct.productCode,
        selectedProduct,
      );

      let region = "";
      if (selectedProduct.productCode) {
        try {
          const productInfo = await getProductInfo(selectedProduct.productCode);
          region = productInfo?.region || "";
        } catch (error) {
          console.error("Failed to fetch product info for region:", error);
        }
      }

      // 获取产品可选项以实现自动填充
      const productOptions = {
        currencies: selectedProduct.currencies || [],
        protectionPeriods: selectedProduct.policyPeriods || [],
        paymentTerms: selectedProduct.paymentTerm || [],
        frequencies: selectedProduct.frequencies || [],
      };

      const newPlan = {
        ...INITIAL_PRODUCT_PLAN,
        productCode: selectedProduct.productCode,
        productName: selectedProduct.productName,
        companyCode: selectedProduct.companyCode,
        companyName:
          selectedProduct.companyName || selectedProduct.companyCode || "",
        region: region,
        // 自动填充只有一个选项的字段
        currency:
          productOptions.currencies.length === 1
            ? productOptions.currencies[0]
            : undefined,
        protectionPeriod:
          productOptions.protectionPeriods.length === 1
            ? productOptions.protectionPeriods[0]
            : undefined,
        contributionYears:
          productOptions.paymentTerms.length === 1
            ? productOptions.paymentTerms[0]
            : undefined,
        paymentFrequency:
          productOptions.frequencies.length === 1
            ? productOptions.frequencies[0]
            : undefined,
      };

      setOrderData((prev) => {
        const data = prev || initialState?.orderData || ({} as any);
        const existingPlans = data.productPlans || [];
        // Filter out empty plans (plans with no productCode) to avoid ghost entries
        const validPlans = existingPlans.filter((p: any) => p.productCode);

        // Switch to the new plan
        setTimeout(() => setActiveProductKey(String(validPlans.length)), 0);

        return {
          ...data,
          productPlans: [...validPlans, newPlan],
        };
      });
    }
    setProductSelectorVisible(false);
  };

  // 關閉按鈕 - 直接退出到訂單列表或來源頁面
  const handleClose = () => {
    // Check location.state for source information first
    if (location.state?.from === "audit-log") {
      navigate(`/orders/${orderId}/audit-log`);
      return;
    }
    if (location.state?.from === "correction") {
      navigate(`/orders/${orderId}/correction`);
      return;
    }

    // Check URL params for source information (legacy support)
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get("from");
    const customerId = searchParams.get("customerId");
    const viewMode = searchParams.get("viewMode");

    if (from === "customer-list" && customerId) {
      // Return to customer list with selected customer and view mode
      navigate("/customers", {
        state: {
          selectedCustomerId: Number(customerId),
          viewMode: viewMode || "detail",
        },
      });
    } else if (from === "customer-detail" && customerId) {
      // Return to customer detail page
      navigate(`/customers/${customerId}`);
    } else {
      // Return to order list
      navigate("/orders");
    }
  };

  // 返回上一頁按鈕 - 按照步驟層級回退
  const handleBack = () => {
    if (loading) return;

    // Step 0: 訂單資料 - 在側邊欄項目之間回退
    if (currentTopStep === 0) {
      // 計算當前啟用的側邊欄項目
      const currentEnabledItems = sidebarItems.filter((item) => {
        if (item.key === "policyholder2") return enablePolicyholder2;
        if (item.key === "insured2") return enableInsured2;
        return true;
      });

      // 找到當前項目在啟用項目列表中的索引
      const currentIndex = currentEnabledItems.findIndex(
        (item) => item.key === currentSidebarStep,
      );

      if (currentIndex > 0) {
        // 回退到上一個側邊欄項目
        const prevItem = currentEnabledItems[currentIndex - 1];
        handleSidebarClick(prevItem.key);
      } else {
        // 已經是第一個項目，無法再回退
        message.info("已經是第一頁");
      }
      return;
    }

    // Step 1: 財務需求分析 - 在子頁面之間回退
    if (currentTopStep === 1) {
      if (
        currentFinancialStep === "fna" &&
        orderData?.isFinancialInfoRequired
      ) {
        // 從 FNA 回退到基本財務資料
        setCurrentFinancialStep("financial-info");
        if (formAreaRef.current) {
          formAreaRef.current.scrollTop = 0;
        }
      } else {
        // 已經在基本財務資料頁面，回退到上一個大步驟
        handleTopStepChange(0);
        // 回到訂單資料的最後一個啟用項目
        const currentEnabledItems = sidebarItems.filter((item) => {
          if (item.key === "policyholder2") return enablePolicyholder2;
          if (item.key === "insured2") return enableInsured2;
          return true;
        });
        if (currentEnabledItems.length > 0) {
          const lastItem = currentEnabledItems[currentEnabledItems.length - 1];
          setCurrentSidebarStep(lastItem.key);
        }
      }
      return;
    }

    // Step 2: 產品投保 - 回退到財務需求分析
    if (currentTopStep === 2) {
      handleTopStepChange(1);
      // 如果有 FNA，回到 FNA 頁面，否則回到基本財務資料
      if (orderData?.isFinancialInfoRequired) {
        setCurrentFinancialStep("fna");
      } else {
        setCurrentFinancialStep("financial-info");
      }
      return;
    }

    // Step 3: 訂單附件 - 回退到產品投保
    if (currentTopStep === 3) {
      handleTopStepChange(2);
      // 回到產品投保的最後一個產品
      if (orderData?.productPlans && orderData.productPlans.length > 0) {
        setActiveProductKey(String(orderData.productPlans.length - 1));
      }
      return;
    }

    // Step 4: 簽署文件 - 回退到訂單附件
    if (currentTopStep === 4) {
      handleTopStepChange(3);
      return;
    }

    // 默認：已經是第一步，無法回退
    message.info("已經是第一頁");
  };

  // 下载所有已签署文件
  const handleDownloadAllSignedFiles = async () => {
    if (!sigDocuments || sigDocuments.length === 0) {
      return;
    }

    // 遍历所有文档并下载
    for (const doc of sigDocuments) {
      if (doc._fileUrl) {
        try {
          // 创建一个临时的 a 标签来触发下载
          const link = document.createElement("a");
          link.href = doc._fileUrl;
          link.download = doc.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // 添加延迟以避免浏览器阻止多个下载
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`下載文件失敗: ${doc.name}`, error);
        }
      }
    }
  };

  // Initialize state from sessionStorage if available to survive page refreshes
  const initialState = useMemo(() => {
    // If it's a new order (no orderId), we should NOT restore from session storage
    // UNLESS we are in a refresh scenario (which is hard to detect perfectly).
    // But per user request: "exit and enter again, form still has cache".
    // So for new order, we should probably clear it on mount if not refreshing?
    // Actually, simply NOT restoring for "new" key unless we are sure might be better.
    // Or we clear the session storage when user "exits" (unmounts).

    // However, user wants to clear cache when "exiting and entering again".
    // If we clear on unmount, refresh might lose data.
    // So maybe we check if we are navigating FROM a different page?
    // A simpler approach: use a navigation state flag or check history?
    // Or just don't cache "new" order in session storage?
    // If we don't cache "new", refresh will lose data.

    // Let's look at how caching is implemented:
    const key = `order_step_${orderId || "new"}`;
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error("Failed to parse saved step:", e);
    }
    return null;
  }, [orderId]);

  // Clear session cache for "new" order when mounting ONLY if it's a fresh entry
  // We can use a ref to track if we've cleared it, but that resets on remount.
  // Ideally, we clear it when leaving the page explicitly (e.g. back button).
  // But we can't easily detect "back" vs "refresh".

  // Strategy: Clear cache when component unmounts.
  // This means refresh will LOSE data for new orders, which might be acceptable if "cache persistence" is not a hard requirement for refresh on new orders.
  // BUT usually refresh persistence is desired.

  // Alternative Strategy: Clear cache when navigating to the route via MENU click (which usually pushes new history).
  // But we are inside the component.

  // Let's try clearing on unmount for now, as that's the most direct way to "clear when exit".
  useEffect(() => {
    return () => {
      // Only clear for new orders to avoid losing work on existing orders?
      // Or clear for all? User specifically mentioned "/orders/new".
      if (!orderId) {
        sessionStorage.removeItem(`order_step_new`);
        sessionStorage.removeItem(`order_scroll_new`);
      }
    };
  }, [orderId]);

  // Restore orderData from session for new orders
  useEffect(() => {
    if (!orderId && initialState?.orderData && !orderData) {
      setOrderData(initialState.orderData);
      syncSavedAdvisorUid(initialState.orderData);
    }
  }, [orderId, initialState, orderData, syncSavedAdvisorUid]);

  // Handle return from ProductList with selected product
  useEffect(() => {
    const processSelectedProduct = async () => {
      const selectedProduct = location.state?.selectedProduct as ProductListVO;
      const selectionContext = location.state?.selectionContext;
      if (selectedProduct) {
        try {
          // If context is financial, update financial.chooseProducts
          if (selectionContext?.type === "financial") {
            const index = selectionContext.index;

            // Check for duplicate product selection before updating state
            const currentFinancial = orderData?.financial || {};
            const currentChooseProducts = currentFinancial.chooseProducts || [];
            const isDuplicate = currentChooseProducts.some(
              (item: any, i: number) =>
                i !== index && item.productCode === selectedProduct.productCode,
            );

            if (isDuplicate) {
              message.error("不能選擇重複的產品");
              // Clear selectedProduct from state
              const newState = { ...location.state };
              delete newState.selectedProduct;
              delete newState.selectionContext;
              navigate(location.pathname, { replace: true, state: newState });
              return;
            }

            setOrderData((prev) => {
              const data = prev || initialState?.orderData || ({} as any);
              const financial = data.financial || {};
              // Deep copy array and ensure objects are new
              const chooseProducts = (financial.chooseProducts || []).map(
                (item) => ({ ...item }),
              );

              // Ensure array size
              while (chooseProducts.length <= index) {
                chooseProducts.push({ productCode: null, choose: false });
              }

              // Update the selected product (保持原有勾选状态)
              chooseProducts[index] = {
                ...chooseProducts[index],
                productCode: selectedProduct.productCode,
                productName:
                  selectedProduct.productName || selectedProduct.productCode,
                companyCode: selectedProduct.companyCode,
                // 保持原有的勾选状态，如果之前已勾选则保持勾选
                choose: chooseProducts[index]?.choose || false,
              };

              return {
                ...data,
                financial: {
                  ...financial,
                  chooseProducts,
                },
              };
            });

            // Clear selectedProduct from state
            const newState = { ...location.state };
            delete newState.selectedProduct;
            delete newState.selectionContext;
            navigate(location.pathname, { replace: true, state: newState });
            return;
          }

          // If context is rider, update productPlans[index].riders[rIndex]
          if (selectionContext?.type === "rider") {
            const { index, rIndex } = selectionContext;
            setOrderData((prev) => {
              const data = prev || initialState?.orderData || ({} as any);
              const productPlans = (data.productPlans || []).map((p: any) => ({
                ...p,
              }));
              if (
                productPlans[index] &&
                productPlans[index].riders &&
                productPlans[index].riders[rIndex]
              ) {
                const riders = [...productPlans[index].riders];
                riders[rIndex] = {
                  ...riders[rIndex],
                  productCode: selectedProduct.productCode,
                  productName: selectedProduct.productName,
                  companyCode: selectedProduct.companyCode,
                  companyName:
                    selectedProduct.companyName ||
                    selectedProduct.companyCode ||
                    "",
                  // Reset other fields as needed
                  currency: undefined,
                  protectionPeriod: undefined,
                  contributionYears: undefined,
                  paymentFrequency: undefined,
                };
                productPlans[index].riders = riders;
              }
              return {
                ...data,
                productPlans,
              };
            });

            // Clear selectedProduct from state
            const newState = { ...location.state };
            delete newState.selectedProduct;
            delete newState.selectionContext;
            navigate(location.pathname, { replace: true, state: newState });
            return;
          }

          // Default logic (adding to productPlans)
          // Check for duplicate product in existing plans
          const currentData = orderData || initialState?.orderData;
          const existingPlans = currentData?.productPlans || [];
          const isDuplicate = existingPlans.some(
            (plan: any) => plan.productCode === selectedProduct.productCode,
          );

          if (isDuplicate) {
            message.error("不能選擇重複的產品");
            // Clear selectedProduct from state
            const newState = { ...location.state };
            delete newState.selectedProduct;
            navigate(location.pathname, { replace: true, state: newState });
            return;
          }

          const newPlan = {
            ...INITIAL_PRODUCT_PLAN,
            productCode: selectedProduct.productCode,
            productName: selectedProduct.productName,
            companyCode: selectedProduct.companyCode,
            companyName:
              selectedProduct.companyName || selectedProduct.companyCode || "",
            // Reset other fields
            currency: undefined,
            protectionPeriod: undefined,
            contributionYears: undefined,
            paymentFrequency: undefined,
          };

          setOrderData((prev) => {
            // If prev is undefined, try to use initialState (restored synchronously if possible, or wait)
            // But since this effect runs after render, if we restored in another effect, it might be racey.
            // However, we can trust functional update to get latest.
            // If prev is still undefined, we might be in trouble.
            // But for new order, we restored it. For existing order, we restored it (see below).
            const data = prev || initialState?.orderData || ({} as any);
            const existingPlans = data.productPlans || [];
            // Filter out empty plans (plans with no productCode) to avoid ghost entries
            const validPlans = existingPlans.filter((p: any) => p.productCode);
            return {
              ...data,
              productPlans: [...validPlans, newPlan],
            };
          });

          // Clear selectedProduct from state to prevent re-adding
          const newState = { ...location.state };
          delete newState.selectedProduct;
          navigate(location.pathname, { replace: true, state: newState });
        } catch (err) {
          console.error("Failed to process selected product", err);
        }
      }
    };

    processSelectedProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Signature Documents State (Lifted from OrderSignatureForm)
  const [sigDocuments, setSigDocuments] = useState<SignatureConfigItem[]>([]);
  const [sigLoadingConfig, setSigLoadingConfig] = useState(false);
  const [sigReadDocs, setSigReadDocs] = useState<Set<string>>(new Set());
  const [sigActiveDocKey, setSigActiveDocKey] = useState<string>("");
  const [sigTypes, setSigTypes] = useState<string[]>([]); // 签名类型列表
  const [sigPhoto, setSigPhoto] = useState<string>(""); // 签名照片URL（用于显示）
  const [sigOssPhoto, setSigOssPhoto] = useState<string>(""); // 签名照片OSS路径（用于提交）

  // 追踪用户是否已手动切换过步骤
  const hasManuallyChangedStepRef = useRef(false);

  // Helper to calculate max allowed step based on order completion status
  const calculateMaxAllowedStep = (data: OrderVO) => {
    return deriveOrderEditTopStep(data, {
      isCorrectionMode: location.state?.from === "correction",
    });
  };

  const [currentTopStep, setCurrentTopStep] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    let step =
      getRequestedOrderStep({
        urlInitialStep: searchParams.get("initialStep"),
        stateInitialStep: location.state?.initialStep,
        cachedTopStep: initialState?.topStep,
      }) ?? 0;

    // Immediately clamp step if initial data is available to prevent flash of wrong step
    if (initialState?.orderData) {
      const maxStep = calculateMaxAllowedStep(initialState.orderData);
      step = Math.min(step, maxStep);
    }

    return step;
  });

  // 提取 fetchConfig 为独立函数，以便在多处调用
  const fetchSignatureConfig = useCallback(async () => {
    if (!orderId) return;

    setSigLoadingConfig(true);
    try {
      const response = (await getSignatureConfig(
        parseInt(orderId, 10),
      )) as unknown as SignatureConfigResponse;
      const configList = response.configs || [];
      const typesList = response.types || [];
      const signInfo = response.signInfo || {};

      // Process files to get signed URLs - 并行下载所有 PDF
      const processedConfig = await Promise.all(
        configList.map(async (doc: SignatureConfigItem, index: number) => {
          let fileUrl = "";
          if (doc.formType) {
            try {
              const pdfResult = await downloadOrderFormPdf({
                orderId: parseInt(orderId, 10),
                formType: doc.formType,
              });
              if (pdfResult && pdfResult.blob) {
                fileUrl = URL.createObjectURL(pdfResult.blob);
              }
            } catch (e) {
              console.error(
                `❌ PDF 加載失敗 ${index + 1}/${configList.length}: ${doc.formType}`,
                e,
              );
            }
          }
          return {
            ...doc,
            key: doc.key || doc.formType || doc.type,
            _fileUrl: fileUrl,
          };
        }),
      );

      setSigDocuments(processedConfig);
      setSigTypes(typesList); // 保存签名类型列表

      // 处理照片路径
      let ossPhoto = signInfo.ossPhoto || "";
      const photoUrl = signInfo.photo || "";

      // 防御性编程：如果 photoUrl 看起来像 OSS 路径（不含 http/data），且 ossPhoto 为空，则将其作为 ossPhoto
      if (
        !ossPhoto &&
        photoUrl &&
        !photoUrl.startsWith("http") &&
        !photoUrl.startsWith("data:")
      ) {
        ossPhoto = photoUrl;
      }

      setSigOssPhoto(ossPhoto); // 保存 OSS 路径用于提交
      setSigPhoto(photoUrl); // 保存预览 URL 用于显示（如果后端返回了）

      // Reset read status when config is reloaded
      setSigReadDocs(new Set());

      // 处理已有的签名数据并回显
      if (signInfo.signatures && Array.isArray(signInfo.signatures)) {
        const signatureTypeKeys: Record<string, string> = {
          POLICYHOLDER: "policyholderSignData",
          INSURED: "insuredSignData",
          ADVISOR: "advisorSignData",
          WITNESS: "witnessSignData",
        };

        const uiSignatures: Record<string, string> = {};
        const ossSignatures: Record<string, string> = {}; // 保存 OSS 路径

        signInfo.signatures.forEach((sig: any) => {
          if (sig.type && sig.signature) {
            const dataKey =
              signatureTypeKeys[sig.type] ||
              `${sig.type.toLowerCase()}SignData`;
            // sig.signatureUrl 是预览 URL，sig.signature 是 OSS 路径（或者相反，取决于后端实现）
            // 如果后端返回了 signatureUrl，使用它作为显示 URL，否则使用 signature
            uiSignatures[dataKey] = sig.signature; // 显示用 URL
            // 保存 OSS 路径用于提交（如果没有重新签署）
            ossSignatures[dataKey] = sig.ossSignature || ""; // OSS 路径
          }
        });

        // 只在有签名数据时才更新 orderData
        if (Object.keys(uiSignatures).length > 0) {
          setOrderData((prev) => ({
            ...prev,
            _uiSignatures: {
              ...prev?._uiSignatures,
              ...uiSignatures,
            },
            _ossSignatures: {
              ...prev?._ossSignatures,
              ...ossSignatures,
            },
          }));

          // 如果已经有了签署数据，标记所有文档为已读（跳过强制阅读流程）
          const allKeys = new Set(
            processedConfig.map((doc) => doc.key || doc.formType || doc.type),
          );
          setSigReadDocs(allKeys);
        }
      }

      if (processedConfig.length > 0) {
        const firstKey =
          processedConfig[0].key ||
          processedConfig[0].formType ||
          processedConfig[0].type ||
          "";
        setSigActiveDocKey(firstKey);
      }
    } catch (error) {
      console.error("Failed to fetch signature config:", error);
      message.error("獲取簽署檔案配置失敗");
    } finally {
      setSigLoadingConfig(false);
    }
  }, [orderId]);

  // Fetch Signature Config
  useEffect(() => {

    if (!orderId || currentTopStep !== ORDER_FORM_SIGNATURE_STEP_INDEX) return;
    // if (sigDocuments.length > 0) return; // Avoid refetching if already loaded 暂时注释此逻辑，因为修改了财务需求分析，会导致签署文件没更新

    fetchSignatureConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, currentTopStep]); // 移除 sigDocuments.length 依赖，避免循环更新

  const handleSigMarkRead = (docKey: string) => {
    setSigReadDocs((prev) => {
      const newSet = new Set(prev);
      newSet.add(docKey);
      return newSet;
    });
  };

  useEffect(() => {
    if (location.state?.initialStep !== undefined) {
      setCurrentTopStep(location.state.initialStep);
    } else if (initialState?.topStep !== undefined) {
      setCurrentTopStep(initialState.topStep);
    }
  }, [location.state, initialState]);
  const [currentSidebarStep, setCurrentSidebarStep] = useState(() => {
    if (location.state?.initialStep === 0) {
      return "policyholder";
    }
    return initialState?.sidebarStep ?? "policyholder";
  });

  // Persist steps and data to sessionStorage whenever they change
  useEffect(() => {
    const key = `order_step_${orderId || "new"}`;
    const stateToSave = {
      topStep: currentTopStep,
      sidebarStep: currentSidebarStep,
      orderData: orderData,
    };
    sessionStorage.setItem(key, JSON.stringify(stateToSave));
  }, [currentTopStep, currentSidebarStep, orderId, orderData]);

  // Restore scroll position logic
  useEffect(() => {
    const key = `order_scroll_${orderId || "new"}`;

    const saveScroll = () => {
      if (formAreaRef.current) {
        sessionStorage.setItem(key, formAreaRef.current.scrollTop.toString());
      }
    };

    // Save scroll on unmount (navigation away)
    return () => {
      saveScroll();
    };
  }, [orderId]);

  // Restore scroll on mount/update when content is ready
  useEffect(() => {
    if (orderData && formAreaRef.current) {
      const key = `order_scroll_${orderId || "new"}`;
      const saved = sessionStorage.getItem(key);
      if (saved) {
        // Use setTimeout to allow layout to settle
        setTimeout(() => {
          if (formAreaRef.current) {
            formAreaRef.current.scrollTop = parseInt(saved, 10);
          }
        }, 300);
      }
    }
  }, [orderData, orderId]);

  const [loading, setLoading] = useState(false);
  const [cachedInsureds, setCachedInsureds] = useState<InsuredDTO[]>([]);
  const [cachedBeneficiaries, setCachedBeneficiaries] = useState<
    BeneficiaryVO[]
  >([]);
  const [customerDetailCache, setCustomerDetailCache] = useState<CustomerDTO | null>(
    null,
  );
  const [isSignatureUploaded, setIsSignatureUploaded] = useState(false);
  const signatureFormRef = useRef<OrderSignatureFormRef>(null);
  const [signatureState, setSignatureState] = useState<{
    canSign: boolean;
    countdown: number;
    isLastDoc: boolean;
    isCurrentDocRead: boolean;
    allDocsRead: boolean;
    isPdfLoaded: boolean;
  }>({
    canSign: false,
    countdown: 0,
    isLastDoc: false,
    isCurrentDocRead: false,
    allDocsRead: false,
    isPdfLoaded: false,
  });

  const [locationData, setLocationData] = useState<string>("");
  const [locationLoading, setLocationLoading] = useState(false);
  const formAreaRef = useRef<HTMLDivElement>(null);
  const locationRequestedRef = useRef(false); // 追踪是否已请求定位，防止重复
  const [hasLocationSucceeded, setHasLocationSucceeded] = useState(false); // 每次进入页面都重新定位
  const [needLocate, setNeedLocate] = useState<boolean | null>(null); // null 表示定位配置尚未加载

  // 检查是否可以进入需要定位的模块（FNA 和签署文件）
  const canEnterLocationRequiredModule = useCallback(() => {
    // 只读模式不需要定位检查
    if (readOnly) return true;
    // 检查是否已成功定位（无论 needLocate 配置如何，都需要完成弹窗流程）
    if (
      hasLocationSucceeded &&
      locationData &&
      locationData !== "定位失敗" &&
      locationData !== "瀏覽器不支援定位"
    ) {
      return true;
    }
    return false;
  }, [readOnly, hasLocationSucceeded, locationData]);

  // 存储定位等待的 resolve 函数
  const locationPromiseRef = useRef<Promise<boolean> | null>(null);
  const locationWaitersRef = useRef<Array<(success: boolean) => void>>([]);
  const locationModalRef = useRef<{
    destroy: () => void;
    update: (config: Parameters<typeof modal.info>[0]) => void;
  } | null>(null);
  const locationWatchIdRef = useRef<number | null>(null);
  const locationTimeoutRef = useRef<number | null>(null);
  const locationDecisionTimerRef = useRef<number | null>(null);
  const locationWatchWarmupTimeoutRef = useRef<number | null>(null);
  const locationSessionIdRef = useRef(0);
  const locationSettledRef = useRef(false);
  const locationLocateInFlightRef = useRef(false);
  const locationBestPositionRef = useRef<GeolocationPosition | null>(null);
  const locationSampleCountRef = useRef(0);

  const hasActiveLocationTracking = () =>
    locationWatchIdRef.current !== null ||
    locationTimeoutRef.current !== null ||
    locationDecisionTimerRef.current !== null ||
    locationWatchWarmupTimeoutRef.current !== null ||
    locationLocateInFlightRef.current;

  const resolvePendingLocationRequest = useCallback((success: boolean) => {
    const waiters = locationWaitersRef.current;
    locationWaitersRef.current = [];
    locationPromiseRef.current = null;

    waiters.forEach((resolve) => resolve(success));
  }, []);

  const destroyActiveLocationModal = useCallback(() => {
    if (activeOrderFormLocationModalDestroy) {
      activeOrderFormLocationModalDestroy();
      activeOrderFormLocationModalDestroy = null;
    }

    if (locationModalRef.current) {
      console.info("[OrderForm][location] destroyActiveLocationModal called", {
        orderId,
        currentTopStep,
        timestamp: new Date().toISOString(),
      });
      locationModalRef.current.destroy();
      locationModalRef.current = null;
    }
  }, [currentTopStep, orderId]);

  const clearLocationTracking = useCallback(() => {
    console.info("[OrderForm][location] clearLocationTracking called", {
      orderId,
      currentTopStep,
      hasWatchId: locationWatchIdRef.current !== null,
      hasTimeout: locationTimeoutRef.current !== null,
      hasDecisionTimer: locationDecisionTimerRef.current !== null,
      hasWarmupTimer: locationWatchWarmupTimeoutRef.current !== null,
      timestamp: new Date().toISOString(),
    });

    if (
      locationWatchIdRef.current !== null &&
      typeof navigator !== "undefined" &&
      navigator.geolocation
    ) {
      navigator.geolocation.clearWatch(locationWatchIdRef.current);
      locationWatchIdRef.current = null;
    }

    if (locationTimeoutRef.current !== null) {
      window.clearTimeout(locationTimeoutRef.current);
      locationTimeoutRef.current = null;
    }

    if (locationDecisionTimerRef.current !== null) {
      window.clearTimeout(locationDecisionTimerRef.current);
      locationDecisionTimerRef.current = null;
    }

    if (locationWatchWarmupTimeoutRef.current !== null) {
      console.info("[OrderForm][location] clearing warmup timeout", {
        orderId,
        currentTopStep,
        timestamp: new Date().toISOString(),
      });
      window.clearTimeout(locationWatchWarmupTimeoutRef.current);
      locationWatchWarmupTimeoutRef.current = null;
    }
  }, []);

  // 组件卸载时清理定位状态
  useEffect(() => {
    // 组件挂载时重置定位相关状态（但要等待 needLocate 配置加载完成后才重置）

    // 组件卸载时也清理（可选）
    return () => {
      console.info("[OrderForm][location] component cleanup called", {
        orderId,
        currentTopStep,
        timestamp: new Date().toISOString(),
      });
      activeOrderFormLocationSessionId += 1;
      locationSessionIdRef.current += 1;
      locationSettledRef.current = true;
      locationLocateInFlightRef.current = false;
      locationWaitersRef.current = [];
      locationPromiseRef.current = null;
      clearLocationTracking();
      destroyActiveLocationModal();
      sessionStorage.removeItem("ifa_location_succeeded");
    };
  }, [
    clearLocationTracking,
    destroyActiveLocationModal,
    orderId,
  ]); // 依赖 orderId，切换订单时也重置

  // 组件挂载时检查是否需要定位
  useEffect(() => {
    let cancelled = false;

    setNeedLocate(null);

    const fetchNeedLocate = async () => {
      try {
        const result = await checkNeedLocate();
        if (cancelled) {
          return;
        }
        const shouldLocate = result ?? true; // 默认需要定位
        setNeedLocate(shouldLocate);

        const shouldPreserveLocationState =
          hasLocationSucceeded || locationLoading || hasActiveLocationTracking();

        if (shouldPreserveLocationState) {
          console.info("[OrderForm][location] preserve location state after needLocate resolved", {
            orderId,
            shouldLocate,
            hasLocationSucceeded,
            locationLoading,
            hasActiveTracking: hasActiveLocationTracking(),
            locationData,
            timestamp: new Date().toISOString(),
          });
          return;
        }

        setHasLocationSucceeded(false);
        locationRequestedRef.current = false;
        setLocationData("");
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("❌ 检查定位配置失败:", error);
        // 失败时默认需要定位
        setNeedLocate(true);

        const shouldPreserveLocationState =
          hasLocationSucceeded || locationLoading || hasActiveLocationTracking();

        if (shouldPreserveLocationState) {
          console.info("[OrderForm][location] preserve location state after needLocate failed", {
            orderId,
            hasLocationSucceeded,
            locationLoading,
            hasActiveTracking: hasActiveLocationTracking(),
            locationData,
            timestamp: new Date().toISOString(),
          });
          return;
        }

        setHasLocationSucceeded(false);
        locationRequestedRef.current = false;
        setLocationData("");
      }
    };

    fetchNeedLocate();

    return () => {
      cancelled = true;
    };
  }, [hasLocationSucceeded, locationData, locationLoading, orderId]); // 依赖 orderId，切换订单时重新检查

  // 根据订单完成状态自动判断当前步骤
  useEffect(() => {
    if (!orderId || !orderData) return;

    // 如果用户已经手动切换过步骤,不再自动判断
    if (hasManuallyChangedStepRef.current) {
      return;
    }

    const maxAllowedStep = calculateMaxAllowedStep(orderData);

    // 获取请求的步骤（URL 或 State）
    const searchParams = new URLSearchParams(location.search);
    const requestedStep = getRequestedOrderStep({
      urlInitialStep: searchParams.get("initialStep"),
      stateInitialStep: location.state?.initialStep,
      cachedTopStep: initialState?.topStep,
      ignoreZeroStateInitialStep: true,
    });

    // 计算最终步骤
    // 如果有明确请求，则尝试使用请求步骤，但不能超过最大允许步骤
    // 如果没有请求，则自动跳转到最大允许步骤（即继续未完成的步骤）
    let finalStep = maxAllowedStep;
    if (requestedStep !== null) {
      finalStep = Math.min(requestedStep, maxAllowedStep);
    }

    // 📍 定位提示：如果目标步骤需要定位且还未定位，记录日志
    // 允许跳转，由步骤内的定位逻辑自动触发定位流程
    if (!readOnly && !hasLocationSucceeded) {
      // Step 4 (签署文件) 需要定位
      if (finalStep === ORDER_FORM_SIGNATURE_STEP_INDEX) {
        // 即将进入签署文件步骤，将触发定位验证
      }
      // Step 1 且开启了 FNA 也需要定位
      else if (finalStep === 1 && orderData?.isFinancialInfoRequired) {
        // 即将进入财务需要分析步骤，将触发定位验证
      }
    }

    // 只在目标步骤与当前步骤不同时才更新
    if (finalStep !== currentTopStep) {
      // 设置初始化标志，防止步骤切换时触发修改标记
      isInitializingRef.current = true;
      initCallCountRef.current = 0;

      setCurrentTopStep(finalStep);

      // 延迟重置初始化标志
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isInitializingRef.current = false;
          initCallCountRef.current = 0;
        });
      });
    }
  }, [
    orderId,
    orderData,
    location.search,
    location.state?.initialStep,
    currentTopStep,
    readOnly,
    needLocate,
    hasLocationSucceeded,
  ]);

  // Reset scroll on step change
  useEffect(() => {
    if (formAreaRef.current) {
      formAreaRef.current.scrollTop = 0;
    }
  }, [
    currentTopStep,
    currentSidebarStep,
    currentFinancialStep,
    activeProductKey,
    sigActiveDocKey,
  ]);

  // Location Logic
  const handleGetLocation = useCallback(
    (options?: { forceRestart?: boolean }) => {
      console.info("[OrderForm][location] handleGetLocation called", {
        orderId,
        currentTopStep,
        currentFinancialStep,
        needLocate,
        readOnly,
        isCorrectionMode,
        hasLocationSucceeded,
        locationData,
        locationLoading,
        locationRequested: locationRequestedRef.current,
        timestamp: new Date().toISOString(),
      });

      if (needLocate === null) {
        console.info("[OrderForm][location] handleGetLocation skipped because needLocate is still loading", {
          orderId,
          currentTopStep,
          currentFinancialStep,
          timestamp: new Date().toISOString(),
        });
        return Promise.resolve(false);
      }

      if (readOnly || isCorrectionMode) {
        return Promise.resolve(true);
      }

      // 防止重复请求：如果正在加载或已经有成功的定位数据，直接返回
      const isSuccessLocation =
        locationData === "香港" || locationData === "白名單區域";
      const hasActiveTracking = hasActiveLocationTracking();

      if (isSuccessLocation) {
        return Promise.resolve(true);
      }

      if (locationPromiseRef.current && !options?.forceRestart) {
        console.info("[OrderForm][location] join existing pending location request", {
          orderId,
          currentTopStep,
          currentFinancialStep,
          timestamp: new Date().toISOString(),
        });
        return locationPromiseRef.current;
      }

      const pendingPromise =
        locationPromiseRef.current ||
        new Promise<boolean>((resolve) => {
          locationWaitersRef.current.push(resolve);
        });

      locationPromiseRef.current = pendingPromise;

      if (options?.forceRestart) {
        console.info("[OrderForm][location] force restarting location request", {
          orderId,
          currentTopStep,
          currentFinancialStep,
          timestamp: new Date().toISOString(),
        });
        clearLocationTracking();
        destroyActiveLocationModal();
        locationLocateInFlightRef.current = false;
        locationSettledRef.current = false;
      }

      if (locationLoading && !hasActiveTracking) {
        console.warn("[OrderForm][location] recovering stale loading state before locate", {
          orderId,
          currentTopStep,
          locationRequested: locationRequestedRef.current,
          timestamp: new Date().toISOString(),
        });
      }

      if (locationLoading && hasActiveTracking) {
        console.warn("[OrderForm][location] recovering orphaned active location tracking before locate", {
          orderId,
          currentTopStep,
          locationData,
          locationLoading,
          hasActiveTracking,
          timestamp: new Date().toISOString(),
        });
        clearLocationTracking();
      }

      const sessionId = activeOrderFormLocationSessionId + 1;
      activeOrderFormLocationSessionId = sessionId;
      locationSessionIdRef.current = sessionId;
      const isCurrentLocationSession = () =>
        locationSessionIdRef.current === sessionId &&
        activeOrderFormLocationSessionId === sessionId;

      setLocationLoading(true);
      setLocationData(""); // Clear previous status
      clearLocationTracking();
      locationSettledRef.current = false;
      locationLocateInFlightRef.current = false;
      locationBestPositionRef.current = null;
      locationSampleCountRef.current = 0;

      // 标记已请求，防止重复触发（但不要在函数开始时就设置，而是在真正发起请求时设置）
      locationRequestedRef.current = true;

      destroyActiveLocationModal();

      // 显示定位中的 Modal
      const locationModal = modal.info({
        title: null,
        icon: null,
        closable: false,
        maskClosable: false,
        keyboard: false,
        centered: true,
        width: 420,
        content: (
          <div
            style={{
              textAlign: "center",
              padding: "32px 24px 24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Spin size="large" />
            <div
              style={{
                fontSize: 16,
                color: "#333",
                fontWeight: 600,
                marginTop: "8px",
              }}
            >
              正在定位...
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#333",
                lineHeight: "1.8",
                textAlign: "center",
                fontWeight: 500,
                marginTop: "4px",
              }}
            >
              根據香港保險業監管要求
              <br />
              保險投保及簽署程序須於香港境內完成
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 16px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                width: "100%",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r="8"
                    stroke="#999"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <path
                    d="M10 6V10.5M10 13.5H10.01"
                    stroke="#999"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#666",
                  lineHeight: "1.6",
                  flex: 1,
                }}
              >
                系統將核實您處身香港，以確保相關保單受香港法律及保險業監管局（IA）監管。
              </div>
            </div>
          </div>
        ),
        footer: null,
      });
      locationModalRef.current = locationModal;
      const closeLocationModal = () => {
        locationModal.destroy();
        if (locationModalRef.current === locationModal) {
          locationModalRef.current = null;
        }
        if (activeOrderFormLocationModalDestroy === closeLocationModal) {
          activeOrderFormLocationModalDestroy = null;
        }
      };
      activeOrderFormLocationModalDestroy = closeLocationModal;

      const startTime = Date.now();

      // 如果配置为不需要定位，弹窗后直接返回成功
      if (needLocate === false) {
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, 1500 - elapsed);

        setTimeout(() => {
          if (!isCurrentLocationSession()) return;
          closeLocationModal();
          setLocationData("香港");
          setHasLocationSucceeded(true);
          locationRequestedRef.current = true;
          setLocationLoading(false);
          message.success("定位成功：香港");
          resolvePendingLocationRequest(true);
        }, remainingTime);
        return pendingPromise;
      }

      // 检查是否在安全上下文中(HTTPS或localhost)
      const isSecureContext = window.isSecureContext;
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      console.info("[OrderForm][location] environment check", {
        orderId,
        currentTopStep,
        isSecureContext,
        isLocalhost,
        hasGeolocation: typeof navigator !== "undefined" && !!navigator.geolocation,
        href: window.location.href,
        timestamp: new Date().toISOString(),
      });

      if (!isSecureContext && !isLocalhost) {
        console.warn("[OrderForm][location] blocked by insecure context", {
          orderId,
          currentTopStep,
          href: window.location.href,
          timestamp: new Date().toISOString(),
        });
        // 非HTTPS环境且非localhost,Safari会拒绝定位请求
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, 1500 - elapsed);

        setTimeout(() => {
          if (!isCurrentLocationSession()) return;
          locationModal.update({
            content: (
              <div
                style={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ width: "24px", height: "24px", flexShrink: 0 }}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="#f44336"
                        strokeWidth="2"
                        fill="none"
                      />
                      <path
                        d="M12 7V13M12 16H12.01"
                        stroke="#f44336"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div style={{ fontSize: 16, color: "#333", fontWeight: 500 }}>
                    無法使用定位服務
                  </div>
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#666",
                    lineHeight: "1.8",
                    textAlign: "center",
                    marginTop: "4px",
                  }}
                >
                  當前網站使用 HTTP 協議，Safari
                  瀏覽器不允許在非安全連接中使用定位功能。
                </div>

                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "8px",
                    padding: "16px",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      fontSize: 14,
                      color: "#00838f",
                      fontWeight: 500,
                      marginBottom: "12px",
                    }}
                  >
                    解決方案
                  </div>
                  <ol
                    style={{
                      margin: 0,
                      paddingLeft: "20px",
                      fontSize: 14,
                      color: "#333",
                      lineHeight: "1.8",
                    }}
                  >
                    <li style={{ marginBottom: "8px" }}>
                      使用 HTTPS 協議訪問網站
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      或使用其他支援 HTTP 定位的瀏覽器(如 Chrome)
                    </li>
                    <li>當前網址: {window.location.href}</li>
                  </ol>
                </div>

                <CustomButton
                  onClick={() => {
                    closeLocationModal();
                    locationRequestedRef.current = false;
                    setLocationLoading(false);
                    resolvePendingLocationRequest(false);
                  }}
                  block={true}
                  height="44px"
                  bgColor="#00bcd4"
                  color="#fff"
                  fontSize={16}
                  style={{ marginTop: "8px", borderRadius: "8px" }}
                >
                  我知道了
                </CustomButton>
              </div>
            ),
            footer: null,
          });
        }, remainingTime);

        setLocationLoading(false);
        return pendingPromise;
      }

      if (navigator.geolocation) {
        console.info("[OrderForm][location] navigator.geolocation available, starting watchPosition", {
          orderId,
          currentTopStep,
          timestamp: new Date().toISOString(),
        });

        const runAfterMinimumDelay = (callback: () => void) => {
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, 1500 - elapsed);

          window.setTimeout(() => {
            if (locationSettledRef.current || !isCurrentLocationSession()) {
              return;
            }

            callback();
          }, remainingTime);
        };

        const handleLocateByPosition = async (position: GeolocationPosition) => {
          if (!isCurrentLocationSession()) {
            return;
          }
          console.info("[OrderForm][location] handleLocateByPosition called", {
            orderId,
            currentTopStep,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            sampleCount: locationSampleCountRef.current,
            timestamp: new Date().toISOString(),
          });

          if (locationSettledRef.current || locationLocateInFlightRef.current) {
            console.info("[OrderForm][location] handleLocateByPosition ignored", {
              locationSettled: locationSettledRef.current,
              locateInFlight: locationLocateInFlightRef.current,
              timestamp: new Date().toISOString(),
            });
            return;
          }

          locationLocateInFlightRef.current = true;
          clearLocationTracking();

          try {
            const { latitude, longitude } = position.coords;
            console.info("[OrderForm][location] calling locate api", {
              orderId,
              currentTopStep,
              latitude,
              longitude,
              timestamp: new Date().toISOString(),
            });
            const res = await locate({ latitude, longitude });
            console.info("[OrderForm][location] locate api resolved", {
              orderId,
              currentTopStep,
              response: res,
              timestamp: new Date().toISOString(),
            });

            runAfterMinimumDelay(() => {
              locationSettledRef.current = true;
              locationLocateInFlightRef.current = false;
              closeLocationModal();

              const displayCity = res.otherCity || "其他地區";
              if (res.result) {
                if (res.isInHongkong) {
                  setLocationData("香港");
                  locationRequestedRef.current = true;
                  setHasLocationSucceeded(true);
                  message.success("定位成功：香港");
                  resolvePendingLocationRequest(true);
                } else {
                  const locationDisplay = res.isInWhitelist
                    ? "白名單區域"
                    : displayCity;
                  setLocationData(locationDisplay);
                  locationRequestedRef.current = true;
                  setHasLocationSucceeded(true);
                  message.success(`定位成功：${locationDisplay}`);
                  resolvePendingLocationRequest(true);
                }
              } else {
                const errorDisplay = res.isInHongkong ? "香港" : displayCity;
                setLocationData(`定位驗證失敗（${errorDisplay}）`);
                // 定位验证失败时不标记为已请求，允许重新进入时自动重试
                locationRequestedRef.current = false;
                message.error(
                  `定位驗證失敗，您當前位於${errorDisplay}，不符合簽署要求`,
                );
                resolvePendingLocationRequest(false);
              }
              setLocationLoading(false);
            });
          } catch (error) {
            console.error("[OrderForm][location] locate api threw", {
              orderId,
              currentTopStep,
              error,
              timestamp: new Date().toISOString(),
            });
            locationLocateInFlightRef.current = false;

            runAfterMinimumDelay(() => {
              locationSettledRef.current = true;
              closeLocationModal();
              console.error("Location API error:", error);
              setLocationData("定位失敗");
              // 定位失败时不标记为已请求，允许用户重试或自动重试
              // locationRequestedRef.current = true;
              message.error("獲取位置資訊失敗");
              setLocationLoading(false);
              resolvePendingLocationRequest(false);
            });
          }
        };

        const handleWatchPosition = (position: GeolocationPosition) => {
          if (!isCurrentLocationSession()) {
            return;
          }
          if (locationWatchWarmupTimeoutRef.current !== null) {
            window.clearTimeout(locationWatchWarmupTimeoutRef.current);
            locationWatchWarmupTimeoutRef.current = null;
          }

          console.info("[OrderForm][location] watchPosition callback", {
            orderId,
            currentTopStep,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            sampleCountBeforeIncrement: locationSampleCountRef.current,
            timestamp: new Date().toISOString(),
          });

          if (locationSettledRef.current || locationLocateInFlightRef.current) {
            console.info("[OrderForm][location] watchPosition ignored", {
              locationSettled: locationSettledRef.current,
              locateInFlight: locationLocateInFlightRef.current,
              timestamp: new Date().toISOString(),
            });
            return;
          }

          locationSampleCountRef.current += 1;

          if (
            !locationBestPositionRef.current ||
            position.coords.accuracy <
              locationBestPositionRef.current.coords.accuracy
          ) {
            locationBestPositionRef.current = position;
          }

          if (
            position.coords.accuracy <= 500 ||
            locationSampleCountRef.current >= 3
          ) {
            console.info("[OrderForm][location] watchPosition reached decision threshold", {
              accuracy: position.coords.accuracy,
              sampleCount: locationSampleCountRef.current,
              timestamp: new Date().toISOString(),
            });
            void handleLocateByPosition(
              locationBestPositionRef.current || position,
            );
            return;
          }

          if (locationDecisionTimerRef.current === null) {
            console.info("[OrderForm][location] start decision timer", {
              sampleCount: locationSampleCountRef.current,
              accuracy: position.coords.accuracy,
              timestamp: new Date().toISOString(),
            });
            locationDecisionTimerRef.current = window.setTimeout(() => {
              locationDecisionTimerRef.current = null;

              if (locationBestPositionRef.current) {
                console.info("[OrderForm][location] decision timer fired", {
                  bestAccuracy: locationBestPositionRef.current.coords.accuracy,
                  sampleCount: locationSampleCountRef.current,
                  timestamp: new Date().toISOString(),
                });
                void handleLocateByPosition(locationBestPositionRef.current);
              }
            }, 3000);
          }
        };

        const handleGeolocationError = (
          error: Pick<
            GeolocationPositionError,
            "code" | "message" | "PERMISSION_DENIED" | "POSITION_UNAVAILABLE" | "TIMEOUT"
          >,
        ) => {
          if (locationSettledRef.current || !isCurrentLocationSession()) {
            return;
          }

          clearLocationTracking();
          console.error("[OrderForm][location] watchPosition error", {
            orderId,
            currentTopStep,
            code: error.code,
            message: error.message,
            PERMISSION_DENIED: error.PERMISSION_DENIED,
            POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
            TIMEOUT: error.TIMEOUT,
            timestamp: new Date().toISOString(),
          });


            runAfterMinimumDelay(() => {
              // 不要先 destroy，根据错误类型决定是 update 还是 destroy
              let errorMsg = "無法獲取位置資訊";

              if (error.code === error.PERMISSION_DENIED) {
                // 检测浏览器类型和平台
                const userAgent = navigator.userAgent.toLowerCase();
                const isIPad =
                  /ipad|macintosh/.test(userAgent) && "ontouchend" in document;
                const isIOS = /iphone|ipad|ipod/.test(userAgent);
                const isMobile = isIPad || isIOS || /android/.test(userAgent);
                const isSafari =
                  /safari/.test(userAgent) && !/chrome/.test(userAgent);
                const isChrome =
                  /chrome/.test(userAgent) && !/edge/.test(userAgent);

                // 根据平台和浏览器生成对应的引导文案
                let guideSteps: string[];
                let guideTitle: string;

                if (isMobile) {
                  // iPad/iPhone 端：Safari 需要检查系统设置 + 网站权限
                  if (isSafari) {
                    guideTitle = "iPad Safari 定位設定教程";
                    guideSteps = [
                      "【步驟一：系統設定】",
                      "打開 iPad「設定」→「隱私權與安全性」→「定位服務」",
                      "確保「定位服務」總開關已開啟",
                      "在下方應用列表找到「Safari」，設定為「使用期間」並開啟「準確位置」",
                      "",
                      "【步驟二：Safari 網站權限】（重要！）",
                      "在 Safari 瀏覽器中，點擊地址欄左側的「aA」圖標",
                      "選擇「網站設定」",
                      "找到「位置」選項，確保設定為「允許」",
                      "",
                      "完成以上步驟後，點擊下方按鈕重新定位",
                    ];
                  } else {
                    // Chrome 使用系统设置
                    guideTitle = "iPad Chrome 定位設定教程";
                    guideSteps = [
                      "打開 iPad「設定」",
                      "選擇「隱私權與安全性」",
                      "點擊「定位服務」並確保開關已開啟",
                      "在下方應用列表找到「Chrome」",
                      "選擇「使用期間」並開啟「準確位置」",
                    ];
                  }
                } else {
                  // 桌面端：根据浏览器类型给出不同引导
                  if (isSafari) {
                    guideTitle = "Safari 定位設定教程";
                    guideSteps = [
                      "點擊 Safari 菜單欄「Safari」→「偏好設定」",
                      "選擇「網站」標籤頁",
                      "在左側選擇「定位服務」",
                      "找到當前網站，設定為「允許」",
                      "重新載入頁面以應用設定",
                    ];
                  } else if (isChrome) {
                    guideTitle = "Chrome 定位設定教程";
                    guideSteps = [
                      "點擊地址欄左側的鎖形圖標",
                      "找到「位置資訊」選項",
                      "選擇「允許」",
                      "如未出現，前往 chrome://settings/content/location",
                      "確保「網站可以請求獲取您的位置資訊」已開啟",
                    ];
                  } else {
                    // 其他浏览器（Edge、Firefox等）
                    guideTitle = "瀏覽器定位設定教程";
                    guideSteps = [
                      "點擊地址欄旁的網站資訊圖標",
                      "找到「定位」或「位置資訊」設定",
                      "選擇「允許」此網站存取位置資訊",
                      "重新載入頁面以應用設定",
                      "如仍無法定位，請檢查瀏覽器設定中的位置權限",
                    ];
                  }
                }

                // 权限被拒绝：不关闭弹窗，而是更新现有弹窗内容
                locationModal.update({
                  content: (
                    <div
                      style={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      {/* 标题行：图标 + 文字 */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          justifyContent: "center",
                        }}
                      >
                        {/* 左侧图标 */}
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            flexShrink: 0,
                          }}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="9"
                              stroke="#f44336"
                              strokeWidth="2"
                              fill="none"
                            />
                            <path
                              d="M12 7V13M12 16H12.01"
                              stroke="#f44336"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        {/* 标题文字 */}
                        <div
                          style={{
                            fontSize: 16,
                            color: "#333",
                            fontWeight: 500,
                          }}
                        >
                          系統沒有授權定位權限
                        </div>
                      </div>

                      {/* 主要说明 */}
                      <div
                        style={{
                          fontSize: 14,
                          color: "#666",
                          lineHeight: "1.8",
                          textAlign: "center",
                          marginTop: "4px",
                        }}
                      >
                        請依照以下操作開啟權限，以滿足香港保險業監管要求。
                      </div>

                      {/* 青色提示框 - 动态标题 */}
                      <div
                        style={{
                          width: "100%",
                          backgroundColor: "#F9FAFB",
                          borderRadius: "8px",
                          textAlign: "left",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 14,
                            color: "#00838f",
                            fontWeight: 500,
                            marginBottom: "12px",
                          }}
                        >
                          {guideTitle}
                        </div>
                        <ol
                          style={{
                            margin: 0,
                            paddingLeft: "20px",
                            fontSize: 14,
                            color: "#333",
                            lineHeight: "1.8",
                          }}
                        >
                          {guideSteps.map((step, index) => (
                            <li
                              key={index}
                              style={{
                                marginBottom:
                                  index < guideSteps.length - 1 ? "8px" : 0,
                              }}
                            >
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>

                      {/* 按钮 */}
                      <CustomButton
                        onClick={() => {
                          closeLocationModal();
                          resolvePendingLocationRequest(false);
                          // Safari 在首次拒绝权限后,需要刷新页面才能再次弹出权限请求
                          // 清除定位相关状态并刷新页面
                          locationRequestedRef.current = false;
                          localStorage.removeItem("locationRequested"); // 清除可能的持久化标记
                          window.location.reload();
                        }}
                        block={true}
                        height="44px"
                        bgColor="#00bcd4"
                        color="#fff"
                        fontSize={16}
                        style={{
                          marginTop: "8px",
                          borderRadius: "8px",
                        }}
                        icon={
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14 8C14 4.686 11.314 2 8 2C4.686 2 2 4.686 2 8C2 11.314 4.686 14 8 14"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <path
                              d="M14 11L14 8L11 8"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        }
                      >
                        我已開啟權限，刷新頁面
                      </CustomButton>
                    </div>
                  ),
                });
                errorMsg = "用戶拒絕了定位請求，請檢查設定";
                return; // 提前返回，保持弹窗状态
              } else if (error.code === error.TIMEOUT) {
                // 检测是否为 Windows 平台
                const isWindows = /windows|win32|win64/.test(
                  navigator.userAgent.toLowerCase(),
                );

                // 定位超时：不关闭弹窗，而是更新现有弹窗内容
                locationModal.update({
                  content: (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "32px 24px 24px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      {/* 顶部图标 */}
                      <div
                        style={{
                          width: "56px",
                          height: "56px",
                          borderRadius: "50%",
                          backgroundColor: "#fff3e0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="16"
                            cy="16"
                            r="12"
                            stroke="#ff9800"
                            strokeWidth="2"
                            fill="none"
                          />
                          <path
                            d="M16 8V16L20 18"
                            stroke="#ff9800"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      {/* 标题 */}
                      <div
                        style={{
                          fontSize: 16,
                          color: "#333",
                          fontWeight: 500,
                          marginTop: "4px",
                        }}
                      >
                        定位超時
                      </div>

                      {/* 主要说明 */}
                      <div
                        style={{
                          fontSize: 14,
                          color: "#333",
                          lineHeight: "1.8",
                          textAlign: "center",
                          marginTop: "4px",
                        }}
                      >
                        根據香港保險業監管要求，
                        保險投保及簽署程序須於香港境內完成。
                      </div>

                      {/* 灰色提示框 */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          padding: "12px 16px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            flexShrink: 0,
                            marginTop: "2px",
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="10"
                              cy="10"
                              r="8"
                              stroke="#999"
                              strokeWidth="1.5"
                              fill="none"
                            />
                            <path
                              d="M10 6V10.5M10 13.5H10.01"
                              stroke="#999"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: "#666",
                            lineHeight: "1.6",
                            flex: 1,
                          }}
                        >
                          系統將核實您處身香港，以確保相關保單受香港法律及保險業監管局（IA）監管。
                        </div>
                      </div>

                      {/* Windows 特殊提示框 */}
                      {isWindows && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            padding: "12px 16px",
                            backgroundColor: "#e3f2fd",
                            borderRadius: "8px",
                            width: "100%",
                            textAlign: "left",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 14,
                              color: "#1976d2",
                              fontWeight: 500,
                              marginBottom: "4px",
                            }}
                          >
                            Windows 定位設定
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#0d47a1",
                              lineHeight: "1.6",
                            }}
                          >
                            如果定位持續超時，請檢查以下設定：
                            <ol
                              style={{
                                margin: "8px 0 0 0",
                                paddingLeft: "20px",
                              }}
                            >
                              <li style={{ marginBottom: "4px" }}>
                                打開「設定」→「隱私權」→「位置」
                              </li>
                              <li style={{ marginBottom: "4px" }}>
                                確保「位置服務」已開啟
                              </li>
                              <li style={{ marginBottom: "4px" }}>
                                允許應用使用位置資訊
                              </li>
                              <li>在 Chrome 中允許此網站使用位置</li>
                            </ol>
                          </div>
                        </div>
                      )}

                      {/* 橙色警告框 */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          padding: "12px 16px",
                          backgroundColor: "#fff3e0",
                          borderRadius: "8px",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            flexShrink: 0,
                            marginTop: "2px",
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 1.5L18.66 17H1.34L10 1.5Z"
                              stroke="#ff9800"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              fill="none"
                            />
                            <path
                              d="M10 7V11M10 13.5H10.01"
                              stroke="#ff9800"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: "#e65100",
                            lineHeight: "1.6",
                            flex: 1,
                          }}
                        >
                          GPS信號較弱或環境受限，請嘗試移動到窗邊。
                        </div>
                      </div>

                      {/* 重新定位按钮 */}
                      <CustomButton
                        onClick={() => {
                          closeLocationModal();
                          locationRequestedRef.current = false;
                          setLocationLoading(false);
                          void handleGetLocation({ forceRestart: true });
                        }}
                        block={true}
                        height="44px"
                        bgColor="#00bcd4"
                        color="#fff"
                        fontSize={16}
                        style={{
                          marginTop: "8px",
                          borderRadius: "8px",
                        }}
                        icon={
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14 8C14 4.686 11.314 2 8 2C4.686 2 2 4.686 2 8C2 11.314 4.686 14 8 14"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <path
                              d="M14 11L14 8L11 8"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        }
                      >
                        重新定位
                      </CustomButton>
                    </div>
                  ),
                });
                // 不设置 locationData 和 loading，保持弹窗状态让用户可以重试
                return; // 提前返回，不执行后续的关闭逻辑
              } else {
                // 其他错误类型：关闭弹窗并显示错误消息
                locationSettledRef.current = true;
                closeLocationModal();

                switch (error.code) {
                  case error.POSITION_UNAVAILABLE:
                    errorMsg = "位置資訊不可用，請檢查設備定位服務是否開啟";
                    break;
                  default:
                    errorMsg = `定位失敗 (${error.message})`;
                }
                message.error(errorMsg);

                setLocationData("定位失敗");
                setLocationLoading(false);
                // 定位失败时重置标记，允许重试
                locationRequestedRef.current = false;
                resolvePendingLocationRequest(false);
              }
            });
        };

        locationWatchIdRef.current = navigator.geolocation.watchPosition(
          handleWatchPosition,
          handleGeolocationError,
          {
            enableHighAccuracy: false,
            maximumAge: 0,
          },
        );

        console.info("[OrderForm][location] watchPosition registered", {
          orderId,
          currentTopStep,
          watchId: locationWatchIdRef.current,
          timestamp: new Date().toISOString(),
        });

        locationWatchWarmupTimeoutRef.current = window.setTimeout(() => {
          if (!isCurrentLocationSession()) {
            return;
          }
          if (
            locationSettledRef.current ||
            locationLocateInFlightRef.current ||
            locationSampleCountRef.current > 0
          ) {
            return;
          }

          console.warn("[OrderForm][location] no geolocation callback during warmup window", {
            orderId,
            currentTopStep,
            timeoutMs: 5000,
            timestamp: new Date().toISOString(),
          });

          clearLocationTracking();
          closeLocationModal();
          setLocationLoading(false);
          setLocationData("定位暫時未返回，請點擊重新獲取");
          // 阻止 useEffect 立刻再次自動重試，改由使用者手動觸發
          locationRequestedRef.current = true;

          resolvePendingLocationRequest(false);

          message.warning("定位暫時未返回，請點擊重新獲取");
        }, 5000);

        console.info("[OrderForm][location] warmup timeout scheduled", {
          orderId,
          currentTopStep,
          timeoutMs: 5000,
          timestamp: new Date().toISOString(),
        });

        locationTimeoutRef.current = window.setTimeout(() => {
          if (!isCurrentLocationSession()) {
            return;
          }
          console.warn("[OrderForm][location] manual timeout fired", {
            orderId,
            currentTopStep,
            timeoutMs: 35000,
            timestamp: new Date().toISOString(),
          });
          handleGeolocationError({
            code: 3,
            message: "Geolocation timeout",
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3,
          });
        }, 35000);
      } else {
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, 1500 - elapsed);

        setTimeout(() => {
          if (!isCurrentLocationSession()) return;
          console.warn("[OrderForm][location] navigator.geolocation unavailable", {
            orderId,
            currentTopStep,
            timestamp: new Date().toISOString(),
          });
          closeLocationModal();
          modal.error({
            title: "瀏覽器不支援定位",
            content:
              "您的瀏覽器不支援定位功能，請使用最新版本的 Safari 或 Chrome 瀏覽器。",
            okText: "知道了",
            centered: true,
          });
          setLocationData("瀏覽器不支援定位");
          setLocationLoading(false);
          // 浏览器不支持时标记已请求，不再重试
          locationRequestedRef.current = true;
          resolvePendingLocationRequest(false);
        }, remainingTime);
      }

      return pendingPromise;
    },
    [
      clearLocationTracking,
      currentFinancialStep,
      currentTopStep,
      destroyActiveLocationModal,
      hasLocationSucceeded,
      isCorrectionMode,
      locationData,
      locationLoading,
      modal,
      message,
      needLocate,
      orderId,
      readOnly,
      resolvePendingLocationRequest,
    ],
  );

  // 包装函数：用于用户主动触发定位（重新获取）
  const handleManualGetLocation = useCallback(() => {
    console.info("[OrderForm][location] manual retry requested", {
      orderId,
      currentTopStep,
      currentFinancialStep,
      locationData,
      timestamp: new Date().toISOString(),
    });
    locationRequestedRef.current = false; // 重置标记，允许重新请求
    void handleGetLocation({ forceRestart: true });
  }, [currentFinancialStep, currentTopStep, handleGetLocation, locationData, orderId]);

  // 触发定位并返回 Promise，用于等待定位完成
  const triggerLocationAndWait = useCallback(() => {
    console.info("[OrderForm][location] triggerLocationAndWait called", {
      orderId,
      currentTopStep,
      currentFinancialStep,
      readOnly,
      isCorrectionMode,
      hasLocationSucceeded,
      locationData,
      timestamp: new Date().toISOString(),
    });

    return handleGetLocation();
  }, [
    currentFinancialStep,
    currentTopStep,
    handleGetLocation,
    hasLocationSucceeded,
    isCorrectionMode,
    locationData,
    needLocate,
    orderId,
    readOnly,
  ]);

  // 定位逻辑：只在进入财务需求分析(FNA)和签署文件步骤时触发
  useEffect(() => {
    // 只读模式或批改模式不触发定位
    if (readOnly || isCorrectionMode) {
      return;
    }

    // needLocate 配置未加载完成前，不触发自动定位
    if (needLocate === null) {
      return;
    }

    // 草稿状态或已完成状态不触发定位
    if (
      orderData?.orderStatus === OrderStatus.Draft ||
      orderData?.orderStatus === OrderStatus.PendingConfirmation ||
      orderData?.orderStatus === OrderStatus.Completed
    ) {
      return;
    }

    // 如果已经定位成功过，不再触发
    if (hasLocationSucceeded) {
      return;
    }

    // 条件1: 进入财务需求分析步骤 (currentTopStep === 1 且 currentFinancialStep === "fna")
    const isFnaStep =
      currentTopStep === 1 &&
      currentFinancialStep === "fna" &&
      orderData?.isFinancialInfoRequired;

    // 条件2: 进入签署文件步骤 (currentTopStep === 4)
    const isSignatureStep = currentTopStep === 4;
    const isStaleAutoLocateState =
      needLocate !== null &&
      locationRequestedRef.current &&
      locationLoading &&
      !hasActiveLocationTracking() &&
      !hasLocationSucceeded;

    // 只在满足条件时触发定位（无论 needLocate 配置如何）
    if (isFnaStep || isSignatureStep) {
      // 正常首轮自动触发：未请求过
      // 异常恢复：请求标记仍在，但实际 tracking 已被 cleanup 清掉
      if ((!locationLoading && !locationRequestedRef.current) || isStaleAutoLocateState) {
        if (isStaleAutoLocateState) {
          console.warn("[OrderForm][location] recovering stale auto-locate state after cleanup", {
            orderId,
            currentTopStep,
            currentFinancialStep,
            timestamp: new Date().toISOString(),
          });
        }
        console.info("[OrderForm][location] auto trigger locating from effect", {
          orderId,
          currentTopStep,
          currentFinancialStep,
          isFnaStep,
          isSignatureStep,
          isStaleAutoLocateState,
          needLocate,
          hasLocationSucceeded,
          locationData,
          timestamp: new Date().toISOString(),
        });
        handleGetLocation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needLocate,
    currentTopStep,
    currentFinancialStep,
    readOnly,
    orderData?.orderStatus,
    orderData?.isFinancialInfoRequired,
    hasLocationSucceeded,
    locationLoading,
  ]);

  // {
  // advisorName: "",
  // advisorTeamName: "",
  // policyholder: { ...INITIAL_POLICYHOLDER },
  // secondPolicyholder: INITIAL_POLICYHOLDER,
  // insured: { ...INITIAL_INSURED },
  // secondInsured: { ...INITIAL_INSURED },
  // beneficiaries: [],
  // financial: INITIAL_FINANCIAL,
  // productPlans: [], // Initialize with empty plan list
  // signInfo: INITIAL_SIGN_INFO, // Initialize signature info
  // }

  // Refs to track fetched IDs to prevent double requests (e.g. in StrictMode or re-renders)
  const fetchedOrderIdRef = useRef<string | null>(null);
  const fetchedCopyFromIdRef = useRef<string | null>(null);
  const fetchedCustomerIdRef = useRef<string | null>(null);

  // Helper to fetch and cache customer details for quick select
  const fetchCustomerCache = (cid: number) => {
    getCustomerDetail(cid)
      .then((fullCustomer: CustomerDTO) => {
        if (fullCustomer) {
          setCustomerDetailCache(fullCustomer);
          setCachedInsureds(fullCustomer.insureds || []);
          setCachedBeneficiaries(fullCustomer.beneficiaries || []);
        }
      })
      .catch((err) => console.error("Failed to fetch customer cache:", err));
  };

  // Handle incoming customer data from CustomerCreate page
  useEffect(() => {
    // 情况1: 从客户创建页面跳转，带有完整的 customerData
    if (location.state?.customerData) {
      const { customerData, customerId } = location.state;

      // 检查是否已经处理过这个 customerId，避免重复处理
      if (fetchedCustomerIdRef.current === String(customerId)) {
        return;
      }

      // Initial population from navigation state (fast)
      setOrderData((prev) => ({
        ...prev,
        policyholder: {
          ...prev?.policyholder,
          policyholderId: customerId,
          basic: {
            ...prev?.policyholder?.basic,
            chineseSurname: customerData.surnameZh,
            chineseGivenName: customerData.givenNameZh,
            englishSurname: customerData.surnameEn,
            englishGivenName: customerData.givenNameEn,
            gender: customerData.gender,
            birthDate: customerData.birthDate,
            idType: customerData.idType,
            idNumber: customerData.idNumber,
          },
          personal: {
            ...prev?.policyholder?.personal,
            salutation: customerData.salutation || customerData.gender, // 称谓优先使用 salutation，否则使用 gender
            phone:
              customerData.mobilePrefix && customerData.mobileNumber
                ? `${customerData.mobilePrefix} ${customerData.mobileNumber}`
                : customerData.mobileNumber,
          },
        },
      }));

      // If we have a customer ID, fetch full details to populate everything else
      if (customerId) {
        // Clear other refs to ensure fresh fetch if switching modes
        fetchedOrderIdRef.current = null;
        fetchedCopyFromIdRef.current = null;

        // 标记已处理此 customerId
        fetchedCustomerIdRef.current = String(customerId);

        setLoading(true);
        getCustomerDetail(customerId)
          .then((fullCustomer: CustomerDTO) => {
            if (fullCustomer) {
              setCustomerDetailCache(fullCustomer);
              setCachedInsureds(fullCustomer.insureds || []);
              setCachedBeneficiaries(fullCustomer.beneficiaries || []);

              // 应用默认值到投保人数据
              const policyholderWithDefaults = ensurePersonDefaults(
                {
                  ...fullCustomer.policyholder,
                  policyholderId:
                    fullCustomer.policyholder?.policyholderId ||
                    Number(customerId),
                },
                "PolicyholderForm",
              );

              setOrderData((prev) => ({
                ...prev,
                policyholder: {
                  ...prev?.policyholder,
                  ...policyholderWithDefaults,
                  policyholderId:
                    policyholderWithDefaults.policyholderId ||
                    prev?.policyholder?.policyholderId, // 确保 policyholderId 不丢失
                  basic: {
                    ...prev?.policyholder?.basic,
                    ...policyholderWithDefaults.basic,
                  },
                  personal: {
                    ...prev?.policyholder?.personal,
                    ...policyholderWithDefaults.personal,
                  },
                  work: {
                    ...prev?.policyholder?.work,
                    ...policyholderWithDefaults.work,
                  },
                  tax: {
                    ...prev?.policyholder?.tax,
                    ...policyholderWithDefaults.tax,
                  },
                  other: {
                    ...prev?.policyholder?.other,
                    ...policyholderWithDefaults.other,
                  },
                  familyMembers:
                    fullCustomer.familyMembers ||
                    prev?.policyholder?.familyMembers,
                },
              }));
            }
          })
          .catch((err) => {
            console.error("❌ 获取客户详细资料失败:", err);
            // message.error("獲取客戶詳細資料失敗，僅顯示基础資料");
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
    // 情况2: 从客户详情页面跳转，只有 customerId
    else if (location.state?.customerId && !location.state?.customerData) {
      const customerId = location.state.customerId;

      // 检查是否已经处理过这个 customerId，避免重复处理
      if (fetchedCustomerIdRef.current === String(customerId)) {
        return;
      }

      // 标记已处理此 customerId
      fetchedCustomerIdRef.current = String(customerId);

      // Clear other refs to ensure fresh fetch
      fetchedOrderIdRef.current = null;
      fetchedCopyFromIdRef.current = null;

      setLoading(true);
      getCustomerDetail(customerId)
        .then((fullCustomer: CustomerDTO) => {
          if (fullCustomer) {
            setCustomerDetailCache(fullCustomer);
            setCachedInsureds(fullCustomer.insureds || []);
            setCachedBeneficiaries(fullCustomer.beneficiaries || []);

            // 应用默认值到投保人数据
            const policyholderWithDefaults = ensurePersonDefaults(
              {
                ...fullCustomer.policyholder,
                policyholderId:
                  fullCustomer.policyholder?.policyholderId ||
                  Number(customerId),
              },
              "PolicyholderForm",
            );

            setOrderData((prev) => ({
              ...prev,
              policyholder: {
                ...prev?.policyholder,
                ...policyholderWithDefaults,
                policyholderId:
                  policyholderWithDefaults.policyholderId ||
                  prev?.policyholder?.policyholderId, // 确保 policyholderId 不丢失
                basic: {
                  ...prev?.policyholder?.basic,
                  ...policyholderWithDefaults.basic,
                },
                personal: {
                  ...prev?.policyholder?.personal,
                  ...policyholderWithDefaults.personal,
                },
                work: {
                  ...prev?.policyholder?.work,
                  ...policyholderWithDefaults.work,
                },
                tax: {
                  ...prev?.policyholder?.tax,
                  ...policyholderWithDefaults.tax,
                },
                other: {
                  ...prev?.policyholder?.other,
                  ...policyholderWithDefaults.other,
                },
                familyMembers:
                  fullCustomer.familyMembers ||
                  prev?.policyholder?.familyMembers,
              },
            }));
          }
        })
        .catch((err) => {
          console.error("❌ 获取客户详细资料失败:", err);
          // message.error("獲取客戶詳細資料失敗");
        })
        .finally(() => {
          setLoading(false);
        });
    }

    // Clear state to avoid re-populating on refresh if desired,
    // or keep it. For now, we leave it.
  }, [location.state]);

  const normalizeLoadedFinancial = useCallback(
    (financial: any, policyholder?: PolicyholderVO) => {
      if (!financial || !isZuuFinancialStepMode) {
        return financial;
      }

      const normalizedFinancial = normalizeOrderFinancialForDisplay(financial);
      return ensureZuuStep2DefaultValues(normalizedFinancial, {
        showRetirementFields: shouldShowZuuRetirementFields(policyholder),
      });
    },
    [isZuuFinancialStepMode],
  );

  // Handle copyFrom parameter for Re-insure
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const copyFromId = searchParams.get("copyFrom");

    if (copyFromId && !orderId) {
      // Clear other refs to ensure fresh fetch if switching modes
      fetchedOrderIdRef.current = null;
      fetchedCustomerIdRef.current = null;

      if (fetchedCopyFromIdRef.current === copyFromId) return;
      fetchedCopyFromIdRef.current = copyFromId;

      // Reset steps to ensure we start from Policyholder for re-insurance
      setCurrentTopStep(0);
      setCurrentSidebarStep("policyholder");

      setLoading(true);
      getOrderDetail(copyFromId)
        .then((res: any) => {
          // Clean up data for new order - Remove fields that should not be copied
          const cleanData = { ...res };
          delete cleanData.orderId;
          delete cleanData.orderNo;
          delete cleanData.orderStatus;
          delete cleanData.orderAuditStatus;
          delete cleanData.complianceAuditStatus;
          delete cleanData.signInfo;
          delete cleanData.signTime;
          delete cleanData.submitTime;
          delete cleanData.policyNo;
          delete cleanData.policyStatus;
          delete cleanData.createTime;
          delete cleanData.updateTime;

          // Deep clean sub-entities to ensure fresh creation
          if (cleanData.financial) {
            delete cleanData.financial.financialId;
            delete cleanData.financial.orderId;
          }
          if (cleanData.customerId) delete cleanData.customerId;
          if (cleanData.policyholder) {
            cleanData.policyholder.policyholderId =
              cleanData.policyholder.sourceId;
            delete cleanData.policyholder.sourceId;
          }
          if (cleanData.secondPolicyholder) {
            delete cleanData.secondPolicyholder.policyholderId;
            delete cleanData.secondPolicyholder.sourceId;
          }
          if (cleanData.insured) {
            cleanData.insured.insuredId = cleanData.insured.sourceId;
            delete cleanData.insured.sourceId;
          }
          if (cleanData.secondInsured) {
            cleanData.secondInsured.insuredId =
              cleanData.secondInsured.sourceId;
            delete cleanData.secondInsured.sourceId;
          }

          if (cleanData.beneficiaries) {
            cleanData.beneficiaries = cleanData.beneficiaries.map((b: any) => {
              const newB = { ...b };
              newB.beneficiaryId = newB.sourceId;
              delete newB.sourceId;
              return newB;
            });
          }

          const productPlans = (cleanData.productPlans || []).map(
            (plan: any) => {
              const newPlan = { ...plan };
              delete newPlan.id;
              delete newPlan.orderId;
              return newPlan;
            },
          );
          if (productPlans.length === 0) {
            // productPlans.push({});
          }

          // Reset signatures (重保时需要重新签名)
          const _uiSignatures = {
            policyholderSignData: null,
            advisorSignData: null,
            insuredSignData: null,
          };

          const newData = {
            ...cleanData,
            financial: normalizeLoadedFinancial(
              cleanData.financial,
              cleanData.policyholder,
            ),
            productPlans,
            _uiSignatures,
            signInfo: {}, // Clear sign info object
            advisorUid: res.referrerUid ?? res.advisorUid,
            advisorTid: res.referrerTid ?? res.advisorTid,
            advisorName: res.referrerName || res.advisorName || "",
            advisorTeamName:
              res.referrerTeamName || res.advisorTeamName || "",
            // Ensure defaults for persons with formRequiredConfig (重保时也应用默认值)
            policyholder: ensurePersonDefaults(
              cleanData.policyholder,
              "PolicyholderForm",
            ),
            insured: ensurePersonDefaults(cleanData.insured, "InsuredForm"),
            secondPolicyholder: ensurePersonDefaults(
              cleanData.secondPolicyholder,
              "PolicyholderForm",
            ),
            secondInsured: ensurePersonDefaults(
              cleanData.secondInsured,
              "InsuredForm",
            ),
          };

          // 设置初始化标志，防止应用默认值时标记为已修改
          isInitializingRef.current = true;

          setOrderData(newData);
          syncSavedAdvisorUid(newData);

          // 重置所有 change 状态
          resetAllChanges();

          // Fetch customer cache for quick select
          if (res.customerId) {
            fetchCustomerCache(res.customerId);
          }

          // 延迟重置初始化标志，给 PersonForm/FinancialForm useEffect 足够时间应用默认值
          // 使用两次 requestAnimationFrame 确保所有副作用都已执行
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isInitializingRef.current = false;
            });
          });

          // message.success("已載入訂單資料");
        })
        .catch((err) => {
          console.error(err);
          // message.error("獲取來源訂單詳情失敗");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [location.search, normalizeLoadedFinancial, orderId, syncSavedAdvisorUid]);

  // Fetch order detail if orderId exists
  const refreshOrder = () => {
    if (!orderId) return Promise.resolve();
    setLoading(true);
    return fetchCurrentOrderDetail(orderId)
      .then((res: any) => {
        const productPlans = (res.productPlans || []).filter(
          (p: any) => p.productCode || p.productName,
        );

        // 签名数据处理：
        // - 在签署步骤（第 5 个可见步骤，索引为 4）时，保留现有的 _uiSignatures（由 getSignatureConfig 提供）
        // - 在其他步骤时，使用 getOrderDetail 返回的 signInfo
        let _uiSignatures;
        if (currentTopStep === ORDER_FORM_SIGNATURE_STEP_INDEX) {
          // 保留现有签名数据，不覆盖
          _uiSignatures = orderData?._uiSignatures || {};
        } else {
          // 使用旧格式的签名数据（兼容性）
          _uiSignatures = {
            policyholderSignData: res.signInfo?.policyholderSignData,
            advisorSignData: res.signInfo?.advisorSignData,
            insuredSignData: res.signInfo?.insuredSignData,
          };
        }

        const newData = {
          ...res,
          financial: normalizeLoadedFinancial(res.financial, res.policyholder),
          productPlans,
          _uiSignatures,
          advisorUid: res.referrerUid ?? res.advisorUid,
          advisorTid: res.referrerTid ?? res.advisorTid,
          advisorName: res.referrerName || res.advisorName || "",
          advisorTeamName:
            res.referrerTeamName || res.advisorTeamName || "",
          // Ensure defaults for persons with formRequiredConfig
          policyholder: ensurePersonDefaults(
            res.policyholder,
            "PolicyholderForm",
          ),
          insured: ensurePersonDefaults(res.insured, "InsuredForm"),
          secondPolicyholder: ensurePersonDefaults(
            res.secondPolicyholder,
            "PolicyholderForm",
          ),
          secondInsured: ensurePersonDefaults(res.secondInsured, "InsuredForm"),
        };

        // 设置初始化标志，防止应用默认值时标记为已修改
        isInitializingRef.current = true;
        initCallCountRef.current = 0; // 重置计数器

        setOrderData(newData);
        syncSavedAdvisorUid(newData);
        cacheOrderPageStatus(orderId, {
          orderStatus: newData.orderStatus,
          orderAuditStatus: newData.orderAuditStatus,
        });

        // 重置所有 change 状态
        resetAllChanges();

        // Fetch customer cache for quick select
        if (res.customerId) {
          fetchCustomerCache(res.customerId);
        }

        // 延迟重置初始化标志，给 PersonForm/FinancialForm useEffect 足够时间应用默认值
        // 使用两次 requestAnimationFrame 确保所有副作用都已执行
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            isInitializingRef.current = false;
          });
        });

        return newData;
      })
      .catch((err) => {
        console.error(err);
        // message.error("刷新訂單失敗");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Helper to apply formRequiredConfig defaultValues to a person object
  const applyConfigDefaultsToPerson = useCallback(
    (person: any, formKey: string) => {
      if (!person) return person;

      const { formRequiredConfig } = useMetadataStore.getState();
      const config = formRequiredConfig.find((c) => c.key === formKey);
      if (!config || !config.base) return person;

      // Deep clone to prevent shared references
      const result = {
        ...person,
        basic: person.basic ? { ...person.basic } : undefined,
        personal: person.personal ? { ...person.personal } : {},
        work: person.work
          ? {
              ...person.work,
              employer: person.work.employer
                ? { ...person.work.employer }
                : undefined,
            }
          : undefined,
        tax: person.tax ? { ...person.tax } : {},
        other: person.other ? { ...person.other } : {},
      };

      // 应用 formRequiredConfig 中的默认值
      config.base.forEach((field) => {
        if (field.path && field.defaultValue !== undefined) {
          const pathParts = field.path.split(".");
          let current: any = result;

          // 导航到目标字段的父对象
          for (let i = 0; i < pathParts.length - 1; i++) {
            const key = pathParts[i];
            if (
              current[key] === null ||
              current[key] === undefined ||
              typeof current[key] !== "object"
            ) {
              current[key] = {};
            }
            current = current[key];
          }

          // 获取最后一级字段名
          const finalKey = pathParts[pathParts.length - 1];
          // 只在当前值为 undefined、null 或空字符串时应用默认值
          if (
            current[finalKey] === undefined ||
            current[finalKey] === null ||
            current[finalKey] === ""
          ) {
            current[finalKey] = field.defaultValue;
          }
        }
      });

      return result;
    },
    [],
  );

  // Helper to ensure boolean fields have defaults (false) instead of null/undefined
  // IMPORTANT: Deep clone nested objects to prevent shared references between policyholder and insured
  const ensurePersonDefaults = useCallback(
    (person: any, formKey?: string) => {
      if (!person) return person;

      // 首先应用 formRequiredConfig 的默认值（如果提供了 formKey）
      let newPerson = formKey
        ? applyConfigDefaultsToPerson(person, formKey)
        : person;

      // Deep clone to prevent shared references
      newPerson = {
        ...newPerson,
        basic: newPerson.basic ? { ...newPerson.basic } : undefined,
        personal: newPerson.personal ? { ...newPerson.personal } : {},
        work: newPerson.work
          ? {
              ...newPerson.work,
              employer: newPerson.work.employer
                ? { ...newPerson.work.employer }
                : undefined,
            }
          : undefined,
        tax: newPerson.tax ? { ...newPerson.tax } : {},
        other: newPerson.other ? { ...newPerson.other } : {},
      };

      // 根据证件类型自动填充国家和手机区号（仅在字段为空时）
      if (newPerson.basic?.idType) {
        const defaults = getDefaultsByIdType(newPerson.basic.idType);

        // 1. 手机区号（仅在电话号码为空时）
        if (defaults.phoneCode && !newPerson.personal?.phone) {
          newPerson.personal.phone = `${defaults.phoneCode} `;
        }

        // 2. 出生国家（仅在为空时）
        if (defaults.countryCode && !newPerson.tax?.countryOfBirth) {
          newPerson.tax.countryOfBirth = defaults.countryCode;
        }

        // 3. 通讯地址国家（仅在为空时）
        if (defaults.countryCode && !(newPerson.personal as any)?.country) {
          (newPerson.personal as any).country = defaults.countryCode;
        }

        // 4. 国籍（仅在为空时）
        if (defaults.countryCode && !newPerson.tax?.nationality) {
          newPerson.tax.nationality = defaults.countryCode;
        }

        // 5. 税务国家（仅在为空时）
        if (defaults.countryCode && !(newPerson.tax as any)?.taxCountry) {
          (newPerson.tax as any).taxCountry = defaults.countryCode;
        }

        // 6. 是否持有香港身份证（仅在为空时）
        if (
          newPerson.tax?.hasHKIDCard === undefined ||
          newPerson.tax?.hasHKIDCard === null
        ) {
          // 如果证件类型是香港身份证，自动勾选
          // 支持多种香港身份证的枚举值格式
          const isHongKongId =
            newPerson.basic.idType === "HK_ID" ||
            newPerson.basic.idType === "HONGKONG_ID" ||
            newPerson.basic.idType === IdType.HongkongID;

          if (isHongKongId) {
            (newPerson.tax as any).hasHKIDCard = "YES";
          } else {
            (newPerson.tax as any).hasHKIDCard = "NO";
          }
        }
      }

      // 根据身份证号码自动解析并填充出生日期、性别和称谓（仅在字段为空时）
      if (newPerson.basic?.idType && newPerson.basic?.idNumber) {
        const parseResult = parseIdCard(
          newPerson.basic.idNumber,
          newPerson.basic.idType,
        );

        if (parseResult.success) {
          // 1. 出生日期（仅在为空时）
          if (parseResult.birthDate && !newPerson.basic.birthDate) {
            newPerson.basic.birthDate = parseResult.birthDate;
          }

          // 2. 性别（仅在为空时）
          if (parseResult.gender && !newPerson.basic.gender) {
            newPerson.basic.gender = parseResult.gender;
          }

          // 3. 称谓（仅在为空时，称谓与性别相同）
          if (
            parseResult.gender &&
            !newPerson.personal?.salutation &&
            shouldAutoFillSalutation
          ) {
            newPerson.personal.salutation = parseResult.gender;
            // 同步到 basic.salutation
            if (newPerson.basic) {
              newPerson.basic.salutation = parseResult.gender;
            }
          }
        }
      }

      // 根据性别自动填充称谓（仅在称谓为空时）- 作为后备方案
      if (
        !newPerson.personal?.salutation &&
        newPerson.basic?.gender &&
        shouldAutoFillSalutation
      ) {
        newPerson.personal.salutation = newPerson.basic.gender;
        // 同步到 basic.salutation
        newPerson.basic.salutation = newPerson.basic.gender;
      }

      // 确保 basic.salutation 和 personal.salutation 保持同步
      // 如果 personal.salutation 有值但 basic.salutation 为空,则同步
      if (newPerson.personal?.salutation && !newPerson.basic?.salutation) {
        if (newPerson.basic) {
          newPerson.basic.salutation = newPerson.personal.salutation;
        }
      }
      // 如果 basic.salutation 有值但 personal.salutation 为空,则同步
      if (newPerson.basic?.salutation && !newPerson.personal?.salutation) {
        newPerson.personal.salutation = newPerson.basic.salutation;
      }

      // Other fields - initialize other object
      if (!newPerson.other) newPerson.other = {} as any;
      if (
        newPerson.other.isVulnerableCustomer === undefined ||
        newPerson.other.isVulnerableCustomer === null
      ) {
        newPerson.other.isVulnerableCustomer = false;
      }
      if (
        newPerson.other.isProfessionalInvestor === undefined ||
        newPerson.other.isProfessionalInvestor === null
      ) {
        newPerson.other.isProfessionalInvestor = false;
      }

      // Root booleans
      if (newPerson.isVulnerableCustomer == null)
        newPerson.isVulnerableCustomer = false;
      if (newPerson.isProfessionalInvestor == null)
        newPerson.isProfessionalInvestor = false;

      return newPerson;
    },
    [applyConfigDefaultsToPerson, shouldAutoFillSalutation],
  );

  // 根据证件类型获取默认国家和手机区号
  const getDefaultsByIdType = (idType?: string) => {
    switch (idType) {
      case "HK_ID":
      case "HONGKONG_ID":
        return {
          countryCode: "HONGKONG", // 香港
          phoneCode: "852",
        };
      case "ID_CARD":
      case "CHINA_ID_CARD":
        return {
          countryCode: "CHINESE", // 中国
          phoneCode: "86",
        };
      case "MACAO_ID":
        return {
          countryCode: "MACAO", // 澳门
          phoneCode: "853",
        };
      case "TAIWAN_ID":
      case "TAIWAN_ID_CARD":
        return {
          countryCode: "TAIWAN", // 台湾
          phoneCode: "886",
        };
      default:
        return {
          countryCode: undefined,
          phoneCode: undefined,
        };
    }
  };

  useEffect(() => {
    if (orderId) {
      // If returning from ProductList with a selection, use restored state instead of fetching
      if (location.state?.selectedProduct && initialState?.orderData) {
        setOrderData(initialState.orderData);
        syncSavedAdvisorUid(initialState.orderData);
        return;
      }

      // Clear other refs to ensure fresh fetch if switching modes
      fetchedCopyFromIdRef.current = null;
      fetchedCustomerIdRef.current = null;

      if (fetchedOrderIdRef.current === orderId) return;
      fetchedOrderIdRef.current = orderId;

      setLoading(true);
      fetchCurrentOrderDetail(orderId)
        .then((res: any) => {
          const productPlans = (res.productPlans || []).filter(
            (p: any) => p.productCode || p.productName,
          );

          // 使用统一的签名数据键名
          const _uiSignatures = {
            policyholderSignData: res.signInfo?.policyholderSignData,
            advisorSignData: res.signInfo?.advisorSignData,
            insuredSignData: res.signInfo?.insuredSignData,
          };

          const newData = {
            ...res,
            financial: normalizeLoadedFinancial(res.financial, res.policyholder),
            productPlans,
            _uiSignatures,
            advisorUid: res.referrerUid ?? res.advisorUid,
            advisorTid: res.referrerTid ?? res.advisorTid,
            advisorName: res.referrerName || res.advisorName || "",
            advisorTeamName:
              res.referrerTeamName || res.advisorTeamName || "",
            // Ensure defaults for persons with formRequiredConfig
            policyholder: ensurePersonDefaults(
              res.policyholder,
              "PolicyholderForm",
            ),
            insured: ensurePersonDefaults(res.insured, "InsuredForm"),
            secondPolicyholder: ensurePersonDefaults(
              res.secondPolicyholder,
              "PolicyholderForm",
            ),
            secondInsured: ensurePersonDefaults(
              res.secondInsured,
              "InsuredForm",
            ),
          };

          // 设置初始化标志，防止应用默认值时标记为已修改
          isInitializingRef.current = true;

          setOrderData(newData);
          syncSavedAdvisorUid(newData);
          cacheOrderPageStatus(orderId, {
            orderStatus: newData.orderStatus,
            orderAuditStatus: newData.orderAuditStatus,
          });

          // 重置所有 change 状态
          resetAllChanges();

          // Fetch customer cache for quick select
          if (res.customerId) {
            fetchCustomerCache(res.customerId);
          }

          // 延迟重置初始化标志，给 PersonForm/FinancialForm useEffect 足够时间应用默认值
          // 使用两次 requestAnimationFrame 确保所有副作用都已执行
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isInitializingRef.current = false;
            });
          });
        })
        .catch((err) => {
          console.error(err);
          // message.error("獲取訂單詳情失敗");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [normalizeLoadedFinancial, orderId, syncSavedAdvisorUid]);

  // Helper to update nested state
  const updateOrderData = (field: keyof OrderVO, value: any) => {
    setOrderData((prev: OrderVO | undefined) => ({ ...prev, [field]: value }));

    // 只有在非初始化状态下才标记为已修改
    if (isInitializingRef.current) {
      initCallCountRef.current += 1;
      // 当所有预期的初始化调用都完成后，重置标志
      // 通常: policyholder + insured + 可能的 secondPolicyholder + secondInsured = 最多4次
      // 加上 PersonForm 内部的默认值应用调用
      if (initCallCountRef.current >= 8) {
        isInitializingRef.current = false;
        initCallCountRef.current = 0;
      }
      return;
    }
    // 标记对应的部分为已修改
    if (field === "policyholder") {
      markSectionChanged("policyholder", true);
    } else if (field === "secondPolicyholder") {
      markSectionChanged("policyholder2", true);
    } else if (field === "insured") {
      markSectionChanged("insured", true);
    } else if (field === "secondInsured") {
      markSectionChanged("insured2", true);
    } else if (field === "beneficiaries") {
      markSectionChanged("beneficiary", true);
    } else if (field === "financial") {
      markSectionChanged("financial", true);
    }
  };

  useEffect(() => {
    if (
      !isZuuFinancialStepMode ||
      currentTopStep !== 1 ||
      currentFinancialStep !== "financial-info" ||
      !orderData?.isFinancialInfoRequired
    ) {
      return;
    }

    const nextFinancial = ensureZuuStep2DefaultValues(orderData.financial, {
      showRetirementFields: shouldShowZuuRetirementFields(
        orderData.policyholder,
      ),
    });

    if (nextFinancial === orderData.financial) {
      return;
    }

    isInitializingRef.current = true;
    initCallCountRef.current = 0;
    updateOrderData("financial", nextFinancial as any);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isInitializingRef.current = false;
        initCallCountRef.current = 0;
      });
    });
  }, [
    currentFinancialStep,
    currentTopStep,
    isZuuFinancialStepMode,
    orderData?.financial,
    orderData?.isFinancialInfoRequired,
    orderData?.policyholder,
  ]);

  const handleReferenceLatestZuuFna = useCallback(() => {
    const referencedFinancial = customerDetailCache?.financial;
    if (!referencedFinancial) {
      message.warning("未找到可引用的歷史 FNA 資料");
      return;
    }

    confirm({
      title: "引用最近一份 FNA",
      content:
        "將以客戶最近一份 FNA 覆蓋目前分析內容，產品介紹及選擇會保留不變，是否繼續？",
      okText: "確認引用",
      cancelText: "取消",
      onOk: () => {
        const referenced = extractFnaData(referencedFinancial);
        const nextFinancial = {
          ...(orderData.financial || {}),
          ...referenced,
          chooseProducts: orderData.financial?.chooseProducts || [],
          onlyProvideOneProduct:
            orderData.financial?.onlyProvideOneProduct ?? false,
          onlyProvideOneProductReason:
            orderData.financial?.onlyProvideOneProductReason,
        };

        updateOrderData("financial", nextFinancial as any);
        message.success("已引用最近一份 FNA");
      },
    });
  }, [confirm, customerDetailCache, message, orderData?.financial]);

  const handleAdvisorChange = useCallback(
    (uid: number) => {
      const selectedAdvisor = advisors.find((advisor) => advisor.uid === uid);

      setOrderData((prev) => ({
        ...prev,
        advisorUid: uid,
        advisorName: selectedAdvisor?.name || prev?.advisorName || "",
        advisorTeamName:
          selectedAdvisor?.teamName || prev?.advisorTeamName || "",
        advisorTid: selectedAdvisor?.teamId,
        referrerUid: uid,
        referrerId: uid,
        referrerName:
          selectedAdvisor?.name || (prev as any)?.referrerName || "",
        referrerTeamName:
          selectedAdvisor?.teamName || (prev as any)?.referrerTeamName || "",
        referrerTid: selectedAdvisor?.teamId,
      }));

      if (!isInitializingRef.current) {
        markSectionChanged("policyholder", true);
      }
    },
    [advisors, markSectionChanged],
  );

  const savePolicyholderWithAdvisor = useCallback(
    async (
      numericOrderId: number,
      options: { draft: boolean; submit: boolean },
    ) => {
      if (!orderData?.policyholder) return;

      await updatePolicyholder(
        numericOrderId,
        convertEmptyStringToNull({
          ...orderData.policyholder,
          orderId: numericOrderId,
          draft: options.draft,
          submit: options.submit,
        }),
      );

      const nextAdvisorUid = getAdvisorUid(orderData);

      if (
        nextAdvisorUid != null &&
        nextAdvisorUid !== savedAdvisorUid
      ) {
        await updateAdvisorOrderReferrer(numericOrderId, nextAdvisorUid);
      }

      setSavedAdvisorUid(nextAdvisorUid);
    },
    [getAdvisorUid, orderData, savedAdvisorUid],
  );

  const fetchCurrentOrderDetail = useCallback(
    (targetOrderId: string | number) =>
      getOrderDetailForPage(targetOrderId, {
        mode: "edit",
        isCorrectionEdit: isCorrectionEditRoute || isCorrectionMode,
        orderStatus: pageOrderStatus,
        orderAuditStatus: pageOrderAuditStatus,
      }),
    [
      isCorrectionEditRoute,
      isCorrectionMode,
      pageOrderAuditStatus,
      pageOrderStatus,
    ],
  );

  // Special helper for signatures which might not match OrderVO structure exactly yet
  // Mapping UI role to OrderVO signInfo fields
  const updateSignature = (role: string, dataUrl: string | null) => {
    let fieldToUpdate = "";
    if (role === "policyholder") fieldToUpdate = "policyholderSignData";
    else if (role === "advisor") fieldToUpdate = "advisorSignData";
    else if (role === "insured") fieldToUpdate = "insuredSignData"; // Note: insuredSignData not in OrderSignInfo yet, might need extension or map to custom field

    if (fieldToUpdate) {
      setOrderData((prev) => ({
        ...prev,
        signInfo: {
          ...prev.signInfo,
          [fieldToUpdate]: dataUrl,
        },
      }));
    }

    // For UI state persistence (since OrderSignInfo might be strict), we can also store in a separate state if needed,
    // but for now let's assume we can extend OrderVO or use 'any' property.
    // Let's store a local UI-friendly signature object in orderData as a custom property for easier mapping back to UI
    setOrderData((prev) => ({
      ...prev,
      _uiSignatures: {
        ...(prev["_uiSignatures"] || {}),
        [role]: dataUrl,
      },
    }));
  };

  // Top Steps Configuration
  const allSteps: OrderStepItem[] = [
    {
      title: "訂單資料",
      icon: <FileTextOutlined />,
    },
    {
      title: isZuuFinancialStepMode ? "財務資料" : "財務需求分析",
      icon: <BankOutlined />,
    },
    {
      title: "產品投保",
      icon: <SafetyCertificateOutlined />,
    },
    {
      title: "訂單附件",
      icon: <PaperClipOutlined />,
    },
    {
      title: "簽署檔案",
      icon: <FormOutlined />,
    },
  ];

  // 步骤配置逻辑：
  // 1. 草稿状态：只显示第一步（订单资料）
  // 2. 订单审核状态：显示前3个步骤（订单资料、财务需求分析、产品投保）
  // 3. 批改模式：显示前3个步骤（订单资料、财务需求分析、产品投保）
  // 4. 新订单模式：只显示第一步
  // 5. 正常编辑模式：显示所有步骤
  const topSteps = isDraftMode
    ? [allSteps[0]]
    : isOrderAuditMode
      ? allSteps.slice(0, 3)
      : !orderId
        ? [allSteps[0]]
        : isCorrectionMode
          ? allSteps.slice(0, 3)
          : allSteps;

  // Helper to get value from nested object path
  const getValueByPath = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    const keys = path.split(".");
    let current = obj;
    for (const key of keys) {
      if (current === null || current === undefined) return undefined;
      current = current[key];
    }
    return current;
  };

  // Status Logic Helper
  const getPolicyholderStatus = (
    data?: PolicyholderVO,
    isRequired: boolean = false,
    role?: string,
  ) => {
    const { formRequiredConfig } = useMetadataStore.getState();
    const config = formRequiredConfig.find((c) => c.key === role);

    if (!config) {
      // Fallback to old logic if no config found
      return { status: isRequired ? "warn" : "optional" };
    }

    // Collect all required fields (base + conditional)
    const requiredFields = new Set<string>();

    // Add base required fields
    config.base?.forEach((field) => {
      if (field.required && field.path) {
        requiredFields.add(field.path);
      }
    });

    // Evaluate conditional rules
    config.rules?.forEach((rule) => {
      if (!rule.source || !rule.type || rule.value === undefined) return;

      const sourceValue = getValueByPath(data, rule.source);
      let match = false;

      const strSource = String(
        sourceValue === undefined || sourceValue === null ? "" : sourceValue,
      );
      const strRule = String(rule.value);

      switch (rule.type) {
        case "EQ":
          match = strSource === strRule;
          break;
        case "NE":
          match = strSource !== strRule;
          break;
        case "CONTAINS":
          match = strSource.includes(strRule);
          break;
        default:
          match = strSource === strRule;
      }

      if (match) {
        rule.fields?.forEach((field) => {
          if (field.required && field.path) {
            requiredFields.add(field.path);
          }
        });
      }
    });

    // Check if data is touched (has any meaningful content)
    const basic = data?.basic || {};
    const personal = data?.personal || {};
    const work = data?.work || {};
    const tax = data?.tax || {};

    const hasValue = (obj: any) =>
      Object.values(obj).some((v) => v !== undefined && v !== null && v !== "");
    const isTouched =
      data &&
      (hasValue(basic) ||
        hasValue(personal) ||
        hasValue(work) ||
        hasValue(tax));

    if (!isTouched && !isRequired) {
      return { status: "optional" };
    }

    // Check all required fields
    const allFieldsFilled = Array.from(requiredFields).every((path) => {
      const value = getValueByPath(data, path);
      // For boolean fields, accept false as valid
      if (typeof value === "boolean") return true;
      // For numbers, accept 0 as valid
      if (typeof value === "number") return true;
      // For strings, accept empty string as valid (only reject null/undefined)
      const isFilled = value !== null && value !== undefined;

      return isFilled;
    });

    if (allFieldsFilled && requiredFields.size > 0) {
      return { status: "done" };
    }

    return { status: "warn" };
  };

  const getBeneficiaryStatus = (
    data?: BeneficiaryVO[],
    isRequired: boolean = false,
  ) => {
    if ((!data || data.length === 0) && !isRequired)
      return { status: "optional" };
    if ((!data || data.length === 0) && isRequired) return { status: "warn" };

    const { formRequiredConfig } = useMetadataStore.getState();
    const config = formRequiredConfig.find((c) => c.key === "BeneficiaryForm");

    if (!config) {
      // Fallback logic if no config
      const isComplete = data!.every((b) => {
        const hasName = b.basic?.chineseGivenName || b.basic?.englishGivenName;
        const hasID = !!b.basic?.idNumber;
        const hasRel = !!b.relationship;
        const hasShare = !!(b as any).sharePercentage;
        return hasName && hasID && hasRel && hasShare;
      });
      return isComplete ? { status: "done" } : { status: "warn" };
    }

    // Collect base required fields
    const requiredFields = new Set<string>();
    config.base?.forEach((field) => {
      if (field.required && field.path) {
        requiredFields.add(field.path);
      }
    });

    // Check each beneficiary
    const isComplete = data!.every((beneficiary) => {
      return Array.from(requiredFields).every((path) => {
        const value = getValueByPath(beneficiary, path);
        // For boolean fields, accept false as valid
        if (typeof value === "boolean") return true;
        // For numbers, accept 0 as valid
        if (typeof value === "number") return true;
        // For other types, check for truthy value
        return !!value;
      });
    });

    return isComplete ? { status: "done" } : { status: "warn" };
  };

  const isEmptyPolicyholder = (data?: any) => {
    if (!data) return true;
    const basic = data.basic || {};
    const personal = data.personal || {};
    const work = data.work || {};
    const tax = data.tax || {};

    const hasValue = (obj: any) =>
      Object.values(obj).some((v) => v !== undefined && v !== null && v !== "");
    return !(
      hasValue(basic) ||
      hasValue(personal) ||
      hasValue(work) ||
      hasValue(tax)
    );
  };

  if (!orderData) {
    return (
      <div
        className={styles["order-form-container"]}
        style={{
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" tip="載入中..." />
      </div>
    );
  }

  // Sidebar Configuration
  const policyholderStatus = getPolicyholderStatus(
    orderData.policyholder,
    true,
    "PolicyholderForm",
  );
  const insuredStatus = getPolicyholderStatus(
    orderData.insured as PolicyholderVO,
    true,
    "InsuredForm",
  ); // Reusing policyholder logic
  const beneficiaryStatus = getBeneficiaryStatus(
    orderData.beneficiaries,
    false,
  );
  // const financialStatus = getFinancialStatus(orderData.financial, orderData.isFinancialInfoRequired);

  const sidebarItems = [
    { key: "policyholder", label: "投保人", ...policyholderStatus },
    { key: "insured", label: "受保人", ...insuredStatus },
    { key: "beneficiary", label: "受益人", ...beneficiaryStatus },
    {
      key: "policyholder2",
      label: "第二投保人",
      ...getPolicyholderStatus(orderData.secondPolicyholder, false),
    },
    {
      key: "insured2",
      label: "第二受保人",
      ...getPolicyholderStatus(
        orderData.secondInsured as PolicyholderVO,
        false,
      ),
    },
  ];

  const validateCurrentStep = async () => {
    if (readOnly) return true;

    try {
      if (currentTopStep === 0) {
        // Step 0: Sidebar forms
        if (
          ["policyholder", "policyholder2", "insured", "insured2"].includes(
            currentSidebarStep,
          )
        ) {
          // 草稿状态下，第二受保人不需要验证
          const isDraftMode = orderData?.orderStatus === OrderStatus.Draft;
          if (isDraftMode && currentSidebarStep === "insured2") {
            return true;
          }
          await personFormRef.current?.validate();
        } else if (currentSidebarStep === "beneficiary") {
          await beneficiaryFormRef.current?.validate();
        }
      } else if (currentTopStep === 1) {
        // Step 1: Financial
        await financialFormRef.current?.validate();
      } else if (currentTopStep === 2) {
        // Step 2: Product Insurance
        await productInsuranceFormRef.current?.validate();
      }
      return true;
    } catch (error: any) {
      console.error("Validation failed:", error);

      // 显示第一个错误信息
      if (error?.errorFields && error.errorFields.length > 0) {
        const firstError = error.errorFields[0];
        const errorMessage = firstError.errors?.[0] || "請完善必填項";
        message.error(errorMessage);
      } else if (error?.message) {
        message.error(error.message);
      } else {
        message.error("請完善必填項");
      }

      return false;
    }
  };

  const handleSidebarClick = async (key: string) => {
    if (loading) return;

    // If clicking current tab, scroll to top and return
    if (key === currentSidebarStep) {
      if (formAreaRef.current) {
        formAreaRef.current.scrollTop = 0;
      }
      return;
    }

    const performNavigation = (targetKey: string) => {
      // 切换 section 时临时设置初始化标志，防止 PersonForm 重新应用默认值时被标记为修改
      isInitializingRef.current = true;
      initCallCountRef.current = 0;

      setCurrentSidebarStep(targetKey);

      // 使用 requestAnimationFrame 等待 PersonForm 的 useEffect 完成
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isInitializingRef.current = false;
          initCallCountRef.current = 0;
        });
      });

      // Scroll to top when switching sidebar items
      if (formAreaRef.current) {
        formAreaRef.current.scrollTop = 0;
      }
    };

    if (orderId) {
      const numericOrderId = parseInt(orderId, 10);

      // 使用新的 change 状态检查是否有修改
      const isChanged =
        sectionChanges[currentSidebarStep as keyof typeof sectionChanges] ||
        false;
      if (isChanged) {
        confirm({
          title: "資料已修改",
          content: "目前資料已修改，尚未儲存，是否儲存？",
          okText: "確認",
          cancelText: "取消",
          onOk: async () => {
            // 切换时保存不进行表单校验
            setLoading(true);
            try {
              switch (currentSidebarStep) {
                case "policyholder":
                  if (orderData.policyholder)
                    await savePolicyholderWithAdvisor(numericOrderId, {
                      draft: false,
                      submit: false,
                    });
                  break;
                case "policyholder2":
                  if (
                    enablePolicyholder2 &&
                    orderData.secondPolicyholder &&
                    !isEmptyPolicyholder(orderData.secondPolicyholder)
                  ) {
                    await updateSecondaryPolicyholder(
                      numericOrderId,
                      convertEmptyStringToNull({
                        ...orderData.secondPolicyholder,
                        orderId: numericOrderId,
                        draft: false,
                        submit: false,
                      }),
                    );
                  }
                  break;
                case "insured":
                  if (orderData.insured)
                    await updateInsured(
                      numericOrderId,
                      convertEmptyStringToNull({
                        ...orderData.insured,
                        orderId: numericOrderId,
                        draft: false,
                        submit: false,
                      }),
                    );
                  break;
                case "insured2":
                  if (
                    enableInsured2 &&
                    orderData.secondInsured &&
                    !isEmptyPolicyholder(orderData.secondInsured)
                  ) {
                    // 草稿状态下，第二受保人不需要确认就可以切换页面
                    const isDraftMode =
                      orderData?.orderStatus === OrderStatus.Draft;
                    await updateSecondInsured(
                      numericOrderId,
                      convertEmptyStringToNull({
                        ...orderData.secondInsured,
                        orderId: numericOrderId,
                        draft: isDraftMode, // 草稿状态下保持 draft: true
                        submit: false,
                      }),
                    );
                  }
                  break;
                case "beneficiary":
                  await submitOrder(numericOrderId);
                  break;
                case "financial":
                  // 根据当前财务子步骤保存对应数据
                  if (currentFinancialStep === "financial-info") {
                    // 只保存财务资料基本信息（排除FNA字段）
                    const financialBasicData = excludeFnaData(
                      orderData.financial,
                    );
                    await updateFinancial(
                      numericOrderId,
                      convertEmptyStringToNull({
                        ...financialBasicData,
                        isFinancialInfoRequired:
                          orderData.isFinancialInfoRequired,
                        orderId: numericOrderId,
                        draft: false,
                        submit: false,
                      }) as any,
                    );
                  } else if (currentFinancialStep === "fna") {
                    // 只保存財務需要分析(FNA)
                    if (orderData.isFinancialInfoRequired) {
                      const fnaData = sanitizeFnaData(orderData.financial);
                      await updateFinancialAnalysis(
                        numericOrderId,
                        convertEmptyStringToNull({
                          ...fnaData,
                          orderId: numericOrderId,
                          draft: false,
                          submit: false,
                        }) as any,
                      );
                    }
                  }
                  break;
              }
              await refreshOrder(); // 重新加载订单数据
              message.success("儲存成功");
              // 重置当前步骤的 change 状态
              markSectionChanged(
                currentSidebarStep as keyof typeof sectionChanges,
                false,
              );
              // 等待 refreshOrder 的初始化延迟完成后再导航，避免再次触发 change
              // 使用 requestAnimationFrame 等待副作用完成
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  performNavigation(key);
                });
              });
            } catch (error) {
              console.error("Update failed:", error);
              // message.error("保存失敗");
            } finally {
              setLoading(false);
            }
          },
          onCancel: () => {
            // 重置当前步骤的 change 状态
            markSectionChanged(
              currentSidebarStep as keyof typeof sectionChanges,
              false,
            );

            // 重新加载订单数据以放弃当前修改
            if (orderId) {
              refreshOrder();
            }

            // 直接导航
            performNavigation(key);
          },
        });
        return;
      }
    }

    performNavigation(key);
  };

  const extractFnaData = (financial: any) => {
    return extractFnaDataByVariant(financial, {
      isZuuMode: isZuuFinancialStepMode,
    });
  };
  const latestCustomerHistoryFna = (() => {
    const historyList = customerDetailCache?.historyFnaList || [];
    if (!historyList.length) {
      return null;
    }

    return [...historyList].sort((left, right) => {
      const leftTime = left?.signTime ? new Date(left.signTime).getTime() : 0;
      const rightTime = right?.signTime
        ? new Date(right.signTime).getTime()
        : 0;
      return rightTime - leftTime;
    })[0];
  })();

  const getQuestionValueFromList = (
    list: any[],
    questionKey: string,
    type: "RADIO" | "CHECKBOX" | "INPUT" = "RADIO",
  ) =>
    getQuestionValueFromListByVariant(list, questionKey, type, {
      isZuuMode: isZuuFinancialStepMode,
    });

  const isFnaComplete = (data?: OrderFinancialVO) => {
    if (!data) return false;

    const financialAnalysis = isZuuFinancialStepMode
      ? normalizeZuuFnaQuestionList(
          Array.isArray(data.financialAnalysis) ? data.financialAnalysis : [],
        )
      : Array.isArray(data.financialAnalysis)
        ? data.financialAnalysis.filter((question) => question?.question)
        : [];
    const suggestionsAndReasons = Array.isArray(data.suggestionsAndReasons)
      ? data.suggestionsAndReasons.filter((question) => question?.question)
      : [];

    const isFilled = (value: any) =>
      value !== undefined && value !== null && value !== "";
    const isNonEmptyString = (value: any) =>
      typeof value === "string" ? value.trim().length > 0 : isFilled(value);
    const isPositiveNumber = (value: any) => Number(value) > 0;
    const isNonNegativeInteger = (value: any) =>
      Number.isInteger(Number(value)) && Number(value) >= 0;

    const objectives = (getQuestionValueFromList(
      financialAnalysis,
      "insurancePurchaseObjectives",
      "CHECKBOX",
    ) || []) as string[];
    if (objectives.length === 0) return false;

    if (isZuuFinancialStepMode) {
      if (objectives.includes("A")) {
        const option = getQuestionValueFromList(
          financialAnalysis,
          "additionalLifeCoverageAmountOption",
          "RADIO",
        );
        if (!isFilled(option)) return false;
        if (option === "A") {
          const amount = getQuestionValueFromList(
            financialAnalysis,
            "additionalLifeCoverageAmountHKD",
            "INPUT",
          );
          if (!isPositiveNumber(amount)) return false;
        }
        if (option === "B") {
          const amount = getQuestionValueFromList(
            financialAnalysis,
            "additionalLifeCoverageAmountUSD",
            "INPUT",
          );
          if (!isPositiveNumber(amount)) return false;
        }
      }

      if (objectives.includes("B")) {
        const medicalObjective = getQuestionValueFromList(
          financialAnalysis,
          "insurancePurchaseMedicalObjectives",
          "RADIO",
        );
        if (!isFilled(medicalObjective)) return false;

        const option = getQuestionValueFromList(
          financialAnalysis,
          "additionalCriticalIllnessCoverageAmountOption",
          "RADIO",
        );
        if (!isFilled(option)) return false;
        if (option === "A") {
          const amount = getQuestionValueFromList(
            financialAnalysis,
            "additionalCriticalIllnessCoverageAmountHKD",
            "INPUT",
          );
          if (!isPositiveNumber(amount)) return false;
        }
        if (option === "B") {
          const amount = getQuestionValueFromList(
            financialAnalysis,
            "additionalCriticalIllnessCoverageAmountUSD",
            "INPUT",
          );
          if (!isPositiveNumber(amount)) return false;
        }
      }

      if (
        objectives.includes("C") ||
        objectives.includes("D") ||
        objectives.includes("E")
      ) {
        const option = getQuestionValueFromList(
          financialAnalysis,
          "additionalTargetAmountOption",
          "RADIO",
        );
        if (!isFilled(option)) return false;
        if (option === "A") {
          const amount = getQuestionValueFromList(
            financialAnalysis,
            "additionalTargetAmountHKD",
            "INPUT",
          );
          if (!isPositiveNumber(amount)) return false;
        }
        if (option === "B") {
          const amount = getQuestionValueFromList(
            financialAnalysis,
            "additionalTargetAmountUSD",
            "INPUT",
          );
          if (!isPositiveNumber(amount)) return false;
        }

        const years = getQuestionValueFromList(
          financialAnalysis,
          "additionalTargetYears",
          "INPUT",
        );
        if (!isNonNegativeInteger(years)) {
          return false;
        }
      }

      if (objectives.includes("E")) {
        const preference = getQuestionValueFromList(
          financialAnalysis,
          "investmentOptionManagementPreference",
          "RADIO",
        );
        if (!isFilled(preference)) return false;
      }

      if (objectives.includes("F")) {
        const otherOption = getQuestionValueFromList(
          financialAnalysis,
          "insurancePurchaseOtherObjectiveOptions",
          "RADIO",
        );
        if (!isFilled(otherOption)) return false;
        if (otherOption === "E") {
          const otherText = getQuestionValueFromList(
            financialAnalysis,
            "insurancePurchaseOtherObjectives",
            "INPUT",
          );
          if (!isNonEmptyString(otherText)) return false;
        }
      }
    } else {
      if (objectives.includes("A")) {
        const v = getQuestionValueFromList(
          financialAnalysis,
          "additionalLifeCoverageAmount",
          "INPUT",
        );
        if (!isFilled(v)) return false;
      }
      if (objectives.includes("B")) {
        const v = getQuestionValueFromList(
          financialAnalysis,
          "additionalCriticalIllnessCoverageAmount",
          "INPUT",
        );
        if (!isFilled(v)) return false;
      }
      if (objectives.includes("C") || objectives.includes("D")) {
        const p = getQuestionValueFromList(
          financialAnalysis,
          "additionalSavingsTargetReturnPeriod",
          "INPUT",
        );
        const a = getQuestionValueFromList(
          financialAnalysis,
          "additionalSavingsTargetReturnAmount",
          "INPUT",
        );
        if (!isFilled(p) || !isFilled(a)) return false;
      }
      if (objectives.includes("E")) {
        const p = getQuestionValueFromList(
          financialAnalysis,
          "additionalInvestmentTargetReturnPeriod",
          "INPUT",
        );
        const a = getQuestionValueFromList(
          financialAnalysis,
          "additionalInvestmentTargetReturnAmount",
          "INPUT",
        );
        if (!isFilled(p) || !isFilled(a)) return false;

        const pref = getQuestionValueFromList(
          financialAnalysis,
          "investmentOptionManagementPreference",
          "RADIO",
        );
        if (!isFilled(pref)) return false;
        if (pref === "A") {
          const w = getQuestionValueFromList(
            financialAnalysis,
            "investmentWithdrawalStartTime",
            "RADIO",
          );
          const g = getQuestionValueFromList(
            financialAnalysis,
            "investmentLongTermGoal",
            "RADIO",
          );
          const e = getQuestionValueFromList(
            financialAnalysis,
            "investmentExpectationIfMarketPoor",
            "RADIO",
          );
          if (!isFilled(w) || !isFilled(g) || !isFilled(e)) return false;
        }
      }
      if (objectives.includes("F")) {
        const v = getQuestionValueFromList(
          financialAnalysis,
          "insurancePurchaseOtherObjectives",
          "INPUT",
        );
        if (!isNonEmptyString(v)) return false;
      }
    }

    const policyTargetExpectedTime = getQuestionValueFromList(
      financialAnalysis,
      "policyTargetExpectedTime",
      "RADIO",
    );
    if (!isFilled(policyTargetExpectedTime)) return false;

    const premiumToDisposableIncomeRatio = getQuestionValueFromList(
      financialAnalysis,
      "premiumToDisposableIncomeRatio",
      "RADIO",
    );
    if (!isFilled(premiumToDisposableIncomeRatio)) return false;
    if (!isZuuFinancialStepMode) {
      if (premiumToDisposableIncomeRatio === "A") {
        const rate = getQuestionValueFromList(
          financialAnalysis,
          "premiumToDisposableIncomeRatio_A_rate",
          "INPUT",
        );
        if (!isFilled(rate)) return false;
      }
      if (premiumToDisposableIncomeRatio === "F") {
        const rate = getQuestionValueFromList(
          financialAnalysis,
          "premiumToDisposableIncomeRatio_F_rate",
          "INPUT",
        );
        if (!isFilled(rate)) return false;
      }
      if (premiumToDisposableIncomeRatio === "G") {
        const reason = getQuestionValueFromList(
          financialAnalysis,
          "premiumToIncomeRatioNotApplicableReason",
          "INPUT",
        );
        if (!isNonEmptyString(reason)) return false;
      }
    }

    const premiumToLiquidAssetsRatio = getQuestionValueFromList(
      financialAnalysis,
      "premiumToLiquidAssetsRatio",
      "RADIO",
    );
    if (!isFilled(premiumToLiquidAssetsRatio)) return false;
    if (!isZuuFinancialStepMode) {
      if (premiumToLiquidAssetsRatio === "A") {
        const rate = getQuestionValueFromList(
          financialAnalysis,
          "premiumToLiquidAssetsRatio_A_rate",
          "INPUT",
        );
        if (!isFilled(rate)) return false;
      }
      if (premiumToLiquidAssetsRatio === "F") {
        const rate = getQuestionValueFromList(
          financialAnalysis,
          "premiumToLiquidAssetsRatio_F_rate",
          "INPUT",
        );
        if (!isFilled(rate)) return false;
      }
    }

    if (!isZuuFinancialStepMode) {
      const intendedPremiumPaymentMethods = (getQuestionValueFromList(
        financialAnalysis,
        "intendedPremiumPaymentMethods",
        "CHECKBOX",
      ) || []) as string[];
      if (intendedPremiumPaymentMethods.length === 0) return false;
    }

    const willingPremiumPaymentPeriodRange = getQuestionValueFromList(
      financialAnalysis,
      "willingPremiumPaymentPeriodRange",
      "RADIO",
    );
    if (!isFilled(willingPremiumPaymentPeriodRange)) return false;
    if (willingPremiumPaymentPeriodRange === "G") {
      const singlePaymentLimit = getQuestionValueFromList(
        financialAnalysis,
        "singlePremiumPaymentLimit",
        "INPUT",
      );
      if (!isFilled(singlePaymentLimit) || singlePaymentLimit === 0)
        return false;
    }

    if (typeof data.isPremiumFinanced !== "boolean") return false;
    if (
      isZuuFinancialStepMode &&
      data.isPremiumFinanced &&
      validateZuuPremiumFinancing(data).length > 0
    ) {
      return false;
    }
    if (!isZuuFinancialStepMode && typeof data.hasExistingPolicyAsMortgage !== "boolean")
      return false;

    if (!isZuuFinancialStepMode) {
      const transferInsuranceDeclaration = Array.isArray(
        data.transferInsuranceDeclaration,
      )
        ? data.transferInsuranceDeclaration
        : [];
      const usingExistingPolicyFundsForNewPolicy = getQuestionValueFromList(
        transferInsuranceDeclaration,
        "usingExistingPolicyFundsForNewPolicy",
        "RADIO",
      );
      if (!isFilled(usingExistingPolicyFundsForNewPolicy)) return false;
      if (
        usingExistingPolicyFundsForNewPolicy === "A" ||
        usingExistingPolicyFundsForNewPolicy === "B"
      ) {
        const comments = getQuestionValueFromList(
          transferInsuranceDeclaration,
          "intermediaryAssessmentAndComments",
          "RADIO",
        );
        if (!isFilled(comments)) return false;
        if (comments === "B") {
          const other = getQuestionValueFromList(
            transferInsuranceDeclaration,
            "intermediaryAssessmentAndCommentsOther",
            "INPUT",
          );
          if (!isNonEmptyString(other)) return false;
        }
      }
    }

    const validProducts = (data.chooseProducts || []).filter(
      (p: any) => p?.productCode,
    );
    if (validProducts.length < 1) return false;

    const selectedProductsWithCompany = (data.chooseProducts || []).filter(
      (p: any) => p?.companyCode,
    );
    if (selectedProductsWithCompany.length >= 1) {
      const firstCompanyCode = selectedProductsWithCompany[0].companyCode;
      const shouldShowReason = selectedProductsWithCompany.every(
        (p: any) => p.companyCode === firstCompanyCode,
      );
      if (
        shouldShowReason &&
        !isNonEmptyString(data.onlyProvideOneProductReason)
      )
        return false;
    }

    if (isZuuFinancialStepMode) {
      const reasonForTheRecommendation = getQuestionValueFromList(
        suggestionsAndReasons,
        "reasonForTheRecommendation",
        "RADIO",
      );
      if (!isFilled(reasonForTheRecommendation)) return false;
      if (reasonForTheRecommendation === "B") {
        const recommendationReasons = (getQuestionValueFromList(
          suggestionsAndReasons,
          "reasonForTheRecommendation_B",
          "CHECKBOX",
        ) || []) as string[];
        if (recommendationReasons.length === 0) return false;
        if (recommendationReasons.includes("B1_G")) {
          const remark = getQuestionValueFromList(
            suggestionsAndReasons,
            "reasonForTheRecommendation_B_B1_G_remark",
            "INPUT",
          );
          if (!isNonEmptyString(remark)) return false;
        }
        if (recommendationReasons.includes("B2_E")) {
          const remark = getQuestionValueFromList(
            suggestionsAndReasons,
            "reasonForTheRecommendation_B_B2_E_remark",
            "INPUT",
          );
          if (!isNonEmptyString(remark)) return false;
        }
      }
    } else {
      const suitabilityDeviationOccurred = getQuestionValueFromList(
        suggestionsAndReasons,
        "suitabilityDeviationOccurred",
        "RADIO",
      );
      if (!isFilled(suitabilityDeviationOccurred)) return false;
      if (suitabilityDeviationOccurred === "A") {
        const followUpYes = getQuestionValueFromList(
          suggestionsAndReasons,
          "suitabilityDeviationFollowUpYesOptions",
          "RADIO",
        );
        if (!isFilled(followUpYes)) return false;

        const reason = getQuestionValueFromList(
          suggestionsAndReasons,
          "reasonForRecommendingDespiteDeviation",
          "RADIO",
        );
        if (!isFilled(reason)) return false;
        if (reason === "B") {
          const other = getQuestionValueFromList(
            suggestionsAndReasons,
            "otherReasonForRecommendation",
            "INPUT",
          );
          if (!isNonEmptyString(other)) return false;
        }
      }
      if (suitabilityDeviationOccurred === "B") {
        const followUpNo = getQuestionValueFromList(
          suggestionsAndReasons,
          "suitabilityDeviationFollowUpNoOptions",
          "RADIO",
        );
        if (!isFilled(followUpNo)) return false;
      }
    }

    return true;
  };

  const getFinancialStatus = (
    section: "financial-info" | "fna",
    data?: OrderFinancialVO,
    isFinancialInfoRequired?: boolean,
  ) => {
    if (section === "financial-info") {
      if (!isFinancialInfoRequired) {
        return { status: "optional" };
      }
      if (!isZuuFinancialStepMode) {
        return { status: "done" };
      }
      const hasErrors = validateZuuFinancialInfoStep2(data || {}, {
        isFinancialInfoRequired,
        showRetirementFields: shouldShowZuuRetirementFields(
          orderData?.policyholder,
        ),
      }).length > 0;
      return { status: hasErrors ? "warn" : "done" };
    }

    // 财务需要分析：如果开启了则全部必填
    if (section === "fna") {
      // 如果没有开启财务资料要求，返回 optional（不显示）
      if (!isFinancialInfoRequired) {
        return { status: "optional" };
      }

      return isFnaComplete(data) ? { status: "done" } : { status: "warn" };
    }

    return { status: "optional" };
  };

  const sanitizeFnaData = (financial: any) => {
    if (isZuuFinancialStepMode) {
      return sanitizeZuuFnaData(financial);
    }
    return sanitizeLegacyFnaData(financial);
  };

  // 辅助函数：从 financial 数据中排除 FNA 相关字段（保留财务资料基本信息）
  const excludeFnaData = (financial: any) => {
    if (!financial) return {};
    const rest = { ...financial };
    delete rest.financialAnalysis;
    delete rest.transferInsuranceDeclaration;
    delete rest.suggestionsAndReasons;
    delete rest.chooseProducts;
    delete rest.isPremiumFinanced;
    delete rest.hasExistingPolicyAsMortgage;
    delete rest.premiumFinanceAnalysis;
    delete rest.premiumFinancingRepaymentSources;
    delete rest.premiumFinancingRepaymentSourceOther;
    delete rest.outstandingMortgageLoanAmount;
    delete rest.outstandingOtherLoanAmount;
    delete rest.premiumFinancingEstimateConsent;
    delete rest.premiumFinancing;
    delete rest.premiumFinancingImportantDisclosureConfirmed;
    delete rest.premiumFinancingRepaymentConfirmed;
    delete rest.monthlyIncomesTotal;
    delete rest.monthlyExpensesTotal;
    delete rest.liquidAssetsTotal;
    delete rest.monthlySurplus;
    delete rest.additionalLiquidAssetsAtRetirement;
    delete rest.zuuStep2Draft;
    delete rest.onlyProvideOneProduct;
    delete rest.onlyProvideOneProductReason;
    return rest;
  };

  const handleProductTabChange = async (newIndex: number) => {
    const currentIndex = Number(activeProductKey);
    if (currentIndex === newIndex) return;

    const currentPlan = orderData?.productPlans?.[currentIndex];

    // Check if modified
    if (
      currentPlan &&
      originalProductPlan &&
      !isEqual(currentPlan, originalProductPlan)
    ) {
      confirm({
        title: "提示",
        content: "當前產品資料已修改，切換前是否儲存？",
        okText: "儲存",
        cancelText: "不儲存",
        onOk: async () => {
          try {
            setLoading(true);
            const numericOrderId = parseInt(orderId || "0", 10);
            if (numericOrderId) {
              await updateOrderProduct(
                convertEmptyStringToNull({
                  ...currentPlan,
                  orderId: numericOrderId,
                }),
              );
              message.success("產品資料已儲存");
              // 更新基准值，防止后续逻辑误判
              setOriginalProductPlan(cloneDeep(currentPlan));
            }
            setActiveProductKey(String(newIndex));
          } catch (error) {
            console.error("Save failed during product switch:", error);
          } finally {
            setLoading(false);
          }
        },
        onCancel: () => {
          setActiveProductKey(String(newIndex));
        },
      });
    } else {
      setActiveProductKey(String(newIndex));
    }
  };

  const handleSubmit = async (isDraft: boolean) => {
    if (loading) return;

    // Only validate when submitting (not saving as draft)
    if (!isDraft && !(await validateCurrentStep())) return;

    setLoading(true);
    try {
      if (orderId) {
        // Edit Mode - Update individual components based on current step
        const numericOrderId = parseInt(orderId, 10);
        const promises: Promise<any>[] = [];

        if (currentTopStep === 0) {
          switch (currentSidebarStep) {
            case "policyholder":
              promises.push(
                savePolicyholderWithAdvisor(numericOrderId, {
                  draft: isDraft,
                  submit: !isDraft,
                }),
              );
              break;
            case "policyholder2":
              if (enablePolicyholder2 && orderData.secondPolicyholder) {
                promises.push(
                  updateSecondaryPolicyholder(
                    numericOrderId,
                    convertEmptyStringToNull({
                      ...orderData.secondPolicyholder,
                      draft: isDraft,
                      submit: !isDraft,
                    }),
                  ),
                );
              }
              break;
            case "insured":
              promises.push(
                updateInsured(
                  numericOrderId,
                  convertEmptyStringToNull({
                    ...orderData.insured,
                    draft: isDraft,
                    submit: !isDraft,
                  }),
                ),
              );

              break;
            case "insured2":
              if (enableInsured2 && orderData.secondInsured) {
                promises.push(
                  updateSecondInsured(
                    numericOrderId,
                    convertEmptyStringToNull({
                      ...orderData.secondInsured,
                      draft: isDraft,
                      submit: !isDraft,
                    }),
                  ),
                );
              }
              break;
              // case "financial":
              //   if (orderData.financial || orderData.isFinancialInfoRequired != null) {
              //     promises.push(
              //       updateFinancial(
              //         numericOrderId,
              //         convertEmptyStringToNull({
              //           ...orderData.financial,
              //           isFinancialInfoRequired: orderData.isFinancialInfoRequired,
              //           orderId: numericOrderId,
              //           draft: isDraft,
              //           submit: !isDraft,
              //         }) as any
              //       )
              //     );
              //   }
              //   break;
              break;
          }
        } else if (currentTopStep === 1) {
          // Financial - 根据当前子步骤保存对应数据
          const numericOrderId = parseInt(orderId, 10);

          if (currentFinancialStep === "financial-info") {
            // 只保存财务资料基本信息（排除FNA字段）
            const financialBasicData = excludeFnaData(orderData.financial);
            promises.push(
              updateFinancial(
                numericOrderId,
                convertEmptyStringToNull({
                  ...financialBasicData,
                  isFinancialInfoRequired: orderData.isFinancialInfoRequired,
                  orderId: numericOrderId,
                  draft: isDraft,
                  submit: !isDraft,
                }) as any,
              ),
            );
          } else if (currentFinancialStep === "fna") {
            // 只保存財務需要分析(FNA)
            if (orderData.isFinancialInfoRequired) {
              const fnaData = sanitizeFnaData(orderData.financial);
              promises.push(
                updateFinancialAnalysis(
                  numericOrderId,
                  convertEmptyStringToNull({
                    ...fnaData,
                    orderId: numericOrderId,
                    draft: isDraft,
                    submit: !isDraft,
                  }) as any,
                ),
              );
            }
          }
        } else if (currentTopStep === 2) {
          // Products
          if (
            orderData.productPlans &&
            orderData.productPlans.length > 0 &&
            orderData.productPlans[0].productCode
          ) {
            orderData.productPlans.forEach((plan) => {
              // Inject orderId if needed
              const planData = { ...plan, orderId: numericOrderId };
              promises.push(
                updateOrderProduct(convertEmptyStringToNull(planData)),
              );
            });
          }
        } else if (currentTopStep === 3) {
          // Step 3: Attachments - Refresh order detail before going to Signature Step
          if (orderId) {
            await fetchCurrentOrderDetail(orderId).then((res: any) => {
              setOrderData((prev) => {
                if (!prev) return prev;
                // Merge latest data but preserve some local UI state if needed
                // For signature, we mostly want the latest server state especially attachments status
                const _uiSignatures = {
                  policyholder: res.signInfo?.policyholderSignData,
                  advisor: res.signInfo?.advisorSignData,
                  insured: res.signInfo?.insuredSignData,
                };
                return {
                  ...prev,
                  ...res,
                  financial: normalizeLoadedFinancial(
                    res.financial,
                    res.policyholder,
                  ),
                  _uiSignatures,
                };
              });
            });
          }
          return setCurrentTopStep(4);
        }
        await Promise.all(promises);

        if (
          ((currentTopStep === 0 && currentSidebarStep === "beneficiary") ||
            currentTopStep === 1) &&
          !isDraft &&
          isDraftMode
        ) {
          await submitOrder(numericOrderId);
        }

        if (isDraft) {
          await refreshOrder();
          message.success("訂單更新成功 (草稿)");
        } else {
          if (isDraftMode) {
            // 刷新订单获取最新数据
            const refreshedData = await refreshOrder();
            const orderNo = refreshedData?.orderNo || orderData?.orderNo;

            // 跳转到提交成功页
            navigate(`/orders/${orderId}/submit-success`, {
              state: {
                orderNo,
                fromCreate: false,
              },
            });
          } else {
            // Determine next step and message based on current step
            let nextStep: number;
            let content: string;

            if (currentTopStep === 0) {
              nextStep = 1;
              content = "您的訂單已成功更新，將進入財務資料步驟。";
            } else if (currentTopStep === 1) {
              nextStep = 2;
              content = "您的訂單已成功更新，將進入產品投保步驟。";
            } else if (currentTopStep === 2) {
              nextStep = 3;
              content = "您的訂單已成功更新，將進入訂單附件步驟。";
            } else {
              // For other steps, don't auto-navigate
              nextStep = currentTopStep;
              content = "您的訂單已成功更新。";
            }

            // Refresh order first to get latest completion status
            // IMPORTANT: Must await refreshOrder before showing modal to ensure data is up-to-date
            const refreshedData = await refreshOrder();

            if (
              currentTopStep === 1 &&
              (refreshedData?.financialCompleted === false ||
                refreshedData?.fnaCompleted === false)
            ) {
              message.warning("請先完成財務需求分析");
              return;
            }

            // For product step (Step 2), add extra delay to ensure backend has processed the data
            if (currentTopStep === 2) {
              // Wait a bit for backend to update productCompleted status
              await new Promise((resolve) => setTimeout(resolve, 500));
              // Fetch again to get the most recent status
              const finalData = await refreshOrder();

              modal.success({
                title: "訂單更新成功",
                content: content,
                onOk: () => {
                  // Move to next step with skipProductValidation flag to bypass validation
                  // Since we just saved the product data, we know it's valid
                  handleTopStepChange(nextStep, finalData, true);
                },
              });
            } else {
              modal.success({
                title: "訂單更新成功",
                content: content,
                onOk: () => {
                  // Move to next step with refreshed data to pass validation
                  handleTopStepChange(nextStep, refreshedData);
                },
              });
            }
          }
        }
      } else {
        // API Call
        const payload: OrderBasicDTO = {
          ...orderData,
          secondPolicyholder: enablePolicyholder2
            ? orderData.secondPolicyholder
            : undefined,
          secondInsured: enableInsured2 ? orderData.secondInsured : undefined,
          draft: isDraft,
          submit: !isDraft,
          financialData: {
            ...orderData.financial,
            isFinancialInfoRequired: orderData.isFinancialInfoRequired,
          } as any,
          referrerUid: getAdvisorUid(orderData),
        };
        const createdOrderId = await createOrder(
          convertEmptyStringToNull(payload),
        );

        if (isDraft) {
          message.success("草稿儲存成功");
          navigate("/orders");
        } else {
          // 获取创建的订单详情
          try {
            const orderDetail = await getOrderDetail(String(createdOrderId));
            const orderNo = orderDetail?.orderNo;

            // 跳转到提交成功页
            navigate(`/orders/${createdOrderId}/submit-success`, {
              state: {
                orderNo,
                fromCreate: true,
              },
            });
          } catch (error) {
            console.error("Failed to fetch order detail:", error);
            // 如果获取订单详情失败，仍然显示成功提示并返回列表
            modal.success({
              title: "訂單提交成功",
              content: "您的訂單已成功提交。",
              onOk: () => {
                navigate("/orders");
              },
            });
          }
        }
      }
    } catch (error) {
      console.error("Order submission failed:", error);
      // message.error(isDraft ? "保存失敗" : (error as any)?.response?.data?.message || (error as Error).message || "操作失敗");
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureSuccess = () => {
    setIsSignatureUploaded(true);
    // 签名提交成功后，重新获取签名配置和 PDF
    fetchSignatureConfig();
    // message.success("簽署文件已上傳，請點擊下方按鈕提交訂單");
  };

  const handleSignatureSubmit = async () => {
    if (orderId) {
      try {
        setLoading(true);
        await confirmOrderSigned(parseInt(orderId, 10));
        navigate(`/orders/${orderId}/success`);
      } catch (error) {
        console.error("Failed to confirm order:", error);
        message.error("訂單提交失敗");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleNext = async () => {
    if (readOnly) {
      if (currentTopStep === 0) {
        let nextKey = null;
        const currentIndex = sidebarItems.findIndex(
          (item) => item.key === currentSidebarStep,
        );
        for (let i = currentIndex + 1; i < sidebarItems.length; i++) {
          const item = sidebarItems[i];
          const isEnabled =
            item.key === "policyholder2"
              ? enablePolicyholder2
              : item.key === "insured2"
                ? enableInsured2
                : true;
          if (isEnabled) {
            nextKey = item.key;
            break;
          }
        }

        if (nextKey) {
          setCurrentSidebarStep(nextKey);
        } else {
          handleTopStepChange(currentTopStep + 1);
        }
      } else if (currentTopStep < topSteps.length - 1) {
        handleTopStepChange(currentTopStep + 1);
      }
      return;
    }

    if (!(await validateCurrentStep())) return;

    // Validate current step before proceeding
    if (currentTopStep === 0) {
      // If editing an existing order, save current sidebar section before moving
      if (orderId) {
        setLoading(true);
        try {
          const numericOrderId = parseInt(orderId, 10);
          switch (currentSidebarStep) {
            case "policyholder":
              await savePolicyholderWithAdvisor(numericOrderId, {
                draft: false,
                submit: false,
              });
              break;
            case "policyholder2":
              if (
                enablePolicyholder2 &&
                orderData.secondPolicyholder &&
                !isEmptyPolicyholder(orderData.secondPolicyholder)
              ) {
                await updateSecondaryPolicyholder(
                  numericOrderId,
                  convertEmptyStringToNull({
                    ...orderData.secondPolicyholder,
                    orderId: numericOrderId,
                    draft: false,
                    submit: false,
                  }),
                );
              }
              break;
            case "insured":
              await updateInsured(
                numericOrderId,
                convertEmptyStringToNull({
                  ...orderData.insured,
                  orderId: numericOrderId,
                  draft: false,
                  submit: false,
                }),
              );
              break;
            case "insured2":
              if (
                enableInsured2 &&
                orderData.secondInsured &&
                !isEmptyPolicyholder(orderData.secondInsured)
              ) {
                // 草稿状态下，第二受保人不需要确认就可以进入下一页
                const isDraftMode =
                  orderData?.orderStatus === OrderStatus.Draft;
                await updateSecondInsured(
                  numericOrderId,
                  convertEmptyStringToNull({
                    ...orderData.secondInsured,
                    orderId: numericOrderId,
                    draft: isDraftMode, // 草稿状态下保持 draft: true
                    submit: false,
                  }),
                );
              }
              break;
          }
          message.success("儲存成功");
          await refreshOrder();
        } catch (error) {
          console.error("Update failed:", error);
          // message.error("保存失敗");
          setLoading(false);
          return;
        } finally {
          setLoading(false);
        }
      }

      let nextKey = null;
      const currentIndex = sidebarItems.findIndex(
        (item) => item.key === currentSidebarStep,
      );
      for (let i = currentIndex + 1; i < sidebarItems.length; i++) {
        const item = sidebarItems[i];
        const isEnabled =
          item.key === "policyholder2"
            ? enablePolicyholder2
            : item.key === "insured2"
              ? enableInsured2
              : true;
        if (isEnabled) {
          nextKey = item.key;
          break;
        }
      }

      if (nextKey) {
        setCurrentSidebarStep(nextKey);
      } else {
        // We are at the last sidebar step.
        if (!orderId) {
          // New Order: Create and navigate to Step 1 (Financial)
          setLoading(true);
          try {
            const payload: OrderBasicDTO = {
              ...orderData,
              secondPolicyholder: enablePolicyholder2
                ? orderData?.secondPolicyholder
                : undefined,
              secondInsured: enableInsured2
                ? orderData?.secondInsured
                : undefined,
              draft: false, // Create as draft
              submit: true,
              financialData: {
                ...orderData?.financial,
                isFinancialInfoRequired: orderData?.isFinancialInfoRequired,
              } as any,
              referrerUid: getAdvisorUid(orderData),
            };
            const res: any = await createOrder(
              convertEmptyStringToNull(payload),
            );
            const newOrderId = res.orderId || res;
            message.success("已創建訂單");
            // Navigate to same page with new ID and step 1
            navigate(`/orders/${newOrderId}/submit-success`, {
              state: { initialStep: 1 },
            });
          } catch (error) {
            console.error("Order creation failed:", error);
            // message.error("創建訂單失敗");
          } finally {
            setLoading(false);
          }
        } else {
          // Edit Order: Determine next action based on order status and current sidebar step
          const isDraftMode = orderData?.orderStatus === OrderStatus.Draft;

          if (
            isDraftMode &&
            (currentSidebarStep === "beneficiary" ||
              currentSidebarStep === "policyholder2" ||
              currentSidebarStep === "insured2")
          ) {
            // 草稿模式下，在受益人步骤点击"确认,下一步"时，提交订单
            setLoading(true);
            try {
              const numericOrderId = parseInt(orderId, 10);
              await submitOrder(numericOrderId);

              // 刷新订单获取最新数据
              const refreshedData = await refreshOrder();
              const orderNo = refreshedData?.orderNo || orderData?.orderNo;

              // 跳转到提交成功页
              navigate(`/orders/${orderId}/submit-success`, {
                state: {
                  orderNo,
                  fromCreate: false,
                },
              });
            } catch (e) {
              console.error("Failed to submit order:", e);
            } finally {
              setLoading(false);
            }
          } else if (isDraftMode) {
            // 草稿模式下，其他步骤只保存，不跳转
            message.success("已儲存，請繼續完善訂單資料");
            setLoading(false);
          } else {
            // Non-draft mode: Move to next Top Step (Financial)
            setLoading(true);
            try {
              await handleTopStepChange(1);
            } catch (e) {
              console.error("Failed to update before next step:", e);
            } finally {
              setLoading(false);
            }
          }
        }
      }
    } else if (currentTopStep === 1) {
      // Step 1: Financial Info - 区分子步骤
      if (orderId) {
        setLoading(true);
        try {
          const numericOrderId = parseInt(orderId, 10);

          if (currentFinancialStep === "financial-info") {
            // 財務資料子步骤 - 保存后切换到財務需要分析或下一步
            if (
              orderData?.financial ||
              orderData?.isFinancialInfoRequired != null
            ) {
              const financialBasicData = excludeFnaData(orderData.financial);
              await updateFinancial(
                numericOrderId,
                convertEmptyStringToNull({
                  ...financialBasicData,
                  isFinancialInfoRequired: orderData.isFinancialInfoRequired,
                  orderId: numericOrderId,
                  draft: false,
                  submit: false,
                }) as any,
              );
              message.success("儲存成功");
              const latestOrderData = await refreshOrder();

              // 清除 financial 的修改标记，因为已经保存成功
              markSectionChanged("financial", false);

              // 等待 refreshOrder 的初始化完成（两次 requestAnimationFrame + 额外延迟）
              await new Promise((resolve) => {
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    setTimeout(resolve, 50);
                  });
                });
              });

              // 如果需要填写FNA，切换到FNA子步骤，否则进入下一步
              if (orderData.isFinancialInfoRequired) {
                // 进入 FNA 前检查定位状态（批改模式下跳过定位检查）
                if (!isCorrectionMode && !canEnterLocationRequiredModule()) {
                  const canProceed = await triggerLocationAndWait();
                  if (!canProceed) {
                    message.warning("請先完成定位驗證後再進入財務需要分析");
                    return;
                  }
                }
                setCurrentFinancialStep("fna");
              } else {
                const canProceed =
                  (latestOrderData?.financialCompleted ??
                    orderData.financialCompleted) !== false;
                if (!canProceed) {
                  message.warning("請先完成財務資料");
                  return;
                }
                await handleTopStepChange(2);
              }
            } else {
              // 没有财务资料，直接进入下一步
              if (orderData.isFinancialInfoRequired) {
                // 进入 FNA 前检查定位状态（批改模式下跳过定位检查）
                if (!isCorrectionMode && !canEnterLocationRequiredModule()) {
                  const canProceed = await triggerLocationAndWait();
                  if (!canProceed) {
                    message.warning("請先完成定位驗證後再進入財務需要分析");
                    return;
                  }
                }
                setCurrentFinancialStep("fna");
              } else {
                await handleTopStepChange(2);
              }
            }
          } else if (currentFinancialStep === "fna") {
            // 財務需要分析子步骤 - 保存 FNA 数据后进入下一步
            const fnaData = sanitizeFnaData(orderData.financial);
            await updateFinancialAnalysis(
              numericOrderId,
              convertEmptyStringToNull({
                ...fnaData,
                orderId: numericOrderId,
                draft: false,
                submit: false,
              }) as any,
            );
            message.success("儲存成功");

            // 清除 financial 的修改标记，因为已经保存成功
            markSectionChanged("financial", false);

            // 轮询刷新订单状态，确保 fnaCompleted 已更新
            let retryCount = 0;
            const maxRetries = 5;
            let latestOrderData = null;

            while (retryCount < maxRetries) {
              await new Promise((resolve) => setTimeout(resolve, 200));

              // 重新获取最新的 orderData
              latestOrderData = await fetchCurrentOrderDetail(orderId);
              if (latestOrderData.fnaCompleted) {
                break;
              }
              retryCount++;
            }

            // 使用最新的订单数据更新状态
            if (latestOrderData) {
              await refreshOrder();
            }

            // 等待 refreshOrder 的初始化完成（两次 requestAnimationFrame + 额外延迟）
            await new Promise((resolve) => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  setTimeout(resolve, 50);
                });
              });
            });

            const canProceed =
              (latestOrderData?.fnaCompleted ?? orderData.fnaCompleted) !==
              false;
            if (!canProceed) {
              message.warning("請先完成財務需要分析");
              return;
            }

            // 进入下一步（产品投保），传递最新的订单数据以绕过验证
            await handleTopStepChange(2, latestOrderData || orderData);
          }
        } catch (error) {
          console.error("Failed to save financial info:", error);
        } finally {
          setLoading(false);
        }
      } else {
        // 新订单逻辑
        if (
          currentFinancialStep === "financial-info" &&
          orderData.isFinancialInfoRequired
        ) {
          // 进入 FNA 前检查定位状态（批改模式下跳过定位检查）
          if (!isCorrectionMode && !canEnterLocationRequiredModule()) {
            const canProceed = await triggerLocationAndWait();
            if (!canProceed) {
              message.warning("請先完成定位驗證後再進入財務需要分析");
              return;
            }
          }
          setCurrentFinancialStep("fna");
        } else {
          handleTopStepChange(2);
        }
      }
    } else if (currentTopStep === 2) {
      if (!orderData.productPlans || orderData.productPlans.length === 0) {
        message.warning("請至少新增一個產品計劃");
        return;
      }

      const validPlans = orderData.productPlans.filter(
        (p) => p.productCode || p.productName,
      );
      if (validPlans.length === 0) {
        message.warning("請至少新增一個有效的產品計劃");
        return;
      }

      const currentPlanIndex = Number(activeProductKey);
      const isLastPlan = currentPlanIndex >= orderData.productPlans.length - 1;

      if (orderId) {
        setLoading(true);
        try {
          const numericOrderId = parseInt(orderId, 10);
          const currentPlan = orderData.productPlans[currentPlanIndex];
          if (currentPlan) {
            await updateOrderProduct(
              convertEmptyStringToNull({
                ...currentPlan,
                orderId: numericOrderId,
              }),
            );
          }

          message.success("產品資料已儲存");

          if (isCorrectionMode) {
            navigate(`/orders/${orderId}/correction`);
            return;
          }

          if (!isLastPlan) {
            setActiveProductKey(String(currentPlanIndex + 1));
            return;
          }

          let retryCount = 0;
          const maxRetries = 5;
          let latestOrderData = null;

          while (retryCount < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 200));
            latestOrderData = await fetchCurrentOrderDetail(orderId);
            if (latestOrderData.productCompleted) {
              break;
            }
            retryCount++;
          }

          if (latestOrderData) {
            await refreshOrder();
          }

          const isAllCompleted =
            latestOrderData?.productCompleted ?? orderData.productCompleted;
          if (isAllCompleted === false) {
            message.warning("請先完成所有產品計劃");
            return;
          }

          handleTopStepChange(3, latestOrderData || orderData);
        } catch (error) {
          console.error("Update failed:", error);
        } finally {
          setLoading(false);
        }
      } else {
        if (!isLastPlan) {
          setActiveProductKey(String(currentPlanIndex + 1));
          return;
        }

        const isAllCompleted = orderData.productPlans.every((plan) => {
          const hasSumAssured =
            plan.sumAssured !== undefined &&
            plan.sumAssured !== null &&
            plan.sumAssured !== "";
          return (
            plan.productCode &&
            plan.companyCode &&
            plan.currency &&
            hasSumAssured
          );
        });

        if (!isAllCompleted) {
          message.warning("請先完成所有產品計劃");
          return;
        }

        handleTopStepChange(3);
      }
    } else if (currentTopStep === 3) {
      // Step 3: Attachments - Move to signature step
      handleTopStepChange(4);
    } else {
      // Top Step logic for other steps
      // if (currentTopStep === 4) {
      //   // Signature Step - handled by UI button opening modal
      //   return;
      // }

      if (currentTopStep < topSteps.length - 1) {
        handleTopStepChange(currentTopStep + 1);
      } else {
        // Final Submission
        handleSubmit(false);
      }
    }
  };

  // const validateStep = (stepIndex: number) => {
  //   if (stepIndex === 0) {
  //     const phStatus = getPolicyholderStatus(orderData.policyholder, true);
  //     const insStatus = getPolicyholderStatus(orderData.insured as PolicyholderVO, true);
  //     const benStatus = getBeneficiaryStatus(orderData.beneficiaries, false);
  //     const finStatus = getFinancialStatus(orderData.financial, false);

  //     // Check secondary persons (optional but must be valid if filled)
  //     const ph2Status = getPolicyholderStatus(orderData.secondPolicyholder, false);
  //     const ins2Status = getPolicyholderStatus(orderData.secondInsured as PolicyholderVO, false);

  //     return phStatus.status === "done" && insStatus.status === "done" && benStatus.status !== "warn" && finStatus.status !== "warn" && ph2Status.status !== "warn" && ins2Status.status !== "warn";
  //   }
  //   if (stepIndex === 1) {
  //     // Product Plans - at least one plan with basic info
  //     // Simplified check: check if productPlans has items
  //     return orderData.productPlans && orderData.productPlans.length > 0;
  //   }
  //   if (stepIndex === 2) {
  //     // Attachments
  //     const attachmentConfigs = orderData["attachmentFiles"] as OrderAttachmentConfig[] | undefined;
  //     // If we haven't loaded attachments yet, we might allow passing for now or block.
  //     if (!attachmentConfigs) return true;

  //     const allRequiredDone = attachmentConfigs.every((cat) => {
  //       if (!cat.required) return true;
  //       const allItemsFilled = cat.items?.every((item) => {
  //         if (!item.required) return true;
  //         // Check for files
  //         const validFiles = item.files?.filter((f: any) => f.status === "done" || !f.status);
  //         return validFiles && validFiles.length > 0;
  //       });
  //       return allItemsFilled;
  //     });
  //     return allRequiredDone;
  //   }
  //   // Step 3 (Signatures) might be optional or handled elsewhere
  //   return true;
  // };

  const handleTopStepChange = async (
    newStep: number,
    overrideData?: OrderVO,
    skipProductValidation = false,
  ) => {
    // 标记用户已手动切换步骤,禁用自动步骤判断
    hasManuallyChangedStepRef.current = true;

    // Scroll to top
    if (formAreaRef.current) {
      formAreaRef.current.scrollTop = 0;
    }

    if (newStep === currentTopStep) return;

    // 进入签署文件步骤（Step 4）时，检查定位状态
    if (newStep === 4 && !readOnly) {
      if (!canEnterLocationRequiredModule()) {
        // 触发定位流程
        const canProceed = await triggerLocationAndWait();
        if (!canProceed) {
          // message.warning("請先完成定位驗證後再進入簽署文件");
          return;
        }
      }
    }

    // Use a local variable to hold the latest data for validation
    let currentOrderData = overrideData || orderData;

    // If entering Signature Step (4), sync data from backend first
    // This ensures we have the latest PDF status and signature info
    if (newStep === 4 && orderId) {
      setLoading(true);
      try {
        const res: any = await fetchCurrentOrderDetail(orderId);

        // Note: 签名数据回显由 fetchConfig (useEffect) 统一处理
        // 这里只同步基本订单信息,不覆盖 _uiSignatures
        const newData = {
          ...currentOrderData, // keep existing client-side state if needed
          ...res,
          financial: normalizeLoadedFinancial(res.financial, res.policyholder),
          _uiSignatures: currentOrderData?._uiSignatures || {}, // 保留现有签名数据,不覆盖
        };

        setOrderData(newData);
        syncSavedAdvisorUid(newData);
        currentOrderData = newData; // Update local reference for validation below
      } catch (error) {
        console.error("Failed to sync order data:", error);
      } finally {
        setLoading(false);
      }
    }

    if (currentOrderData) {
      // 切换到产品投保步骤(步骤2)时,检查财务资料是否完成
      if (
        newStep === 2 &&
        (currentOrderData.financialCompleted === false ||
          currentOrderData.fnaCompleted === false)
      ) {
        message.warning("請先完成財務需求分析");
        return;
      }
      // 切换到签署文件步骤(步骤4)时,检查产品投保是否完成
      // 步骤2(产品投保)和步骤3(订单附件)不判断产品完成状态,允许直接访问
      // skipProductValidation 用于从步骤2保存后直接跳转到步骤3的场景
      if (
        newStep === 4 &&
        !skipProductValidation &&
        currentOrderData.productCompleted === false
      ) {
        message.warning("請先完成產品投保");
        return;
      }
      // 切换到签署文件步骤(步骤4)时,检查订单附件是否完成
      if (newStep === 4 && currentOrderData.attachmentCompleted === false) {
        message.warning("請先完成訂單附件");
        return;
      }
    }

    // If entering Product Step (2) from Financial Step (1), sync products from backend
    if (currentTopStep === 1 && newStep === 2) {
      if (orderId) {
        setLoading(true);
        try {
          const res: any = await fetchCurrentOrderDetail(orderId);
          // Only update productPlans if they exist in backend response
          if (res.productPlans) {
            setOrderData((prev) => {
              if (!prev) return undefined;
              return {
                ...prev,
                // Ensure we use productPlans from backend, filtered for valid entries
                productPlans: (res.productPlans || []).filter(
                  (p: any) => p.productCode || p.productName,
                ),
                // Sync financial info requirement status
                isFinancialInfoRequired: res.isFinancialInfoRequired,
              };
            });
          }
        } catch (error) {
          console.error("Failed to sync order data:", error);
        } finally {
          setLoading(false);
        }
      }
    }

    // 设置初始化标志,防止步骤切换时触发修改标记
    isInitializingRef.current = true;
    initCallCountRef.current = 0;

    setCurrentTopStep(newStep);

    // 延迟重置初始化标志
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isInitializingRef.current = false;
        initCallCountRef.current = 0;
      });
    });
  };

  // 处理财务子步骤切换
  const handleFinancialStepClick = async (
    targetStep: "financial-info" | "fna",
  ) => {
    if (loading) return;

    // 如果点击当前步骤，滚动到顶部并返回
    if (targetStep === currentFinancialStep) {
      if (formAreaRef.current) {
        formAreaRef.current.scrollTop = 0;
      }
      return;
    }

    const performNavigation = (step: "financial-info" | "fna") => {
      // Step切换会触发 FinancialForm 的初始化副作用，临时屏蔽这些自动写回的置脏。
      isInitializingRef.current = true;
      initCallCountRef.current = 0;

      setCurrentFinancialStep(step);
      if (formAreaRef.current) {
        formAreaRef.current.scrollTop = 0;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isInitializingRef.current = false;
          initCallCountRef.current = 0;
        });
      });
    };

    // 检查是否有未保存的修改
    if (orderId && sectionChanges.financial) {
      const numericOrderId = parseInt(orderId, 10);

      confirm({
        title: "資料已修改",
        content: "目前資料已修改，尚未儲存，是否儲存？",
        okText: "確認",
        cancelText: "取消",
        onOk: async () => {
          setLoading(true);
          try {
            // 根据当前财务子步骤保存对应数据
            if (currentFinancialStep === "financial-info") {
              // 只保存财务资料基本信息（排除FNA字段）
              const financialBasicData = excludeFnaData(orderData.financial);
              await updateFinancial(
                numericOrderId,
                convertEmptyStringToNull({
                  ...financialBasicData,
                  isFinancialInfoRequired: orderData.isFinancialInfoRequired,
                  orderId: numericOrderId,
                  draft: false,
                  submit: false,
                }) as any,
              );
            } else if (currentFinancialStep === "fna") {
              // 只保存財務需要分析(FNA)
              if (orderData.isFinancialInfoRequired) {
                const fnaData = sanitizeFnaData(orderData.financial);
                await updateFinancialAnalysis(
                  numericOrderId,
                  convertEmptyStringToNull({
                    ...fnaData,
                    orderId: numericOrderId,
                    draft: false,
                    submit: false,
                  }) as any,
                );
              }
            }
            await refreshOrder(); // 重新加载订单数据
            message.success("儲存成功");
            // 重置财务步骤的 change 状态
            markSectionChanged("financial", false);

            // 继续执行步骤切换
            performNavigation(targetStep);
          } catch (error) {
            console.error("Update failed:", error);
          } finally {
            setLoading(false);
          }
        },
        onCancel: () => {
          // 重置财务步骤的 change 状态
          markSectionChanged("financial", false);

          // 重新加载订单数据以放弃当前修改
          if (orderId) {
            refreshOrder().then(() => {
              // 等待数据加载和初始化完成后再切换步骤
              // 使用 setTimeout 确保 requestAnimationFrame 完成
              setTimeout(() => {
                performNavigation(targetStep);
              }, 100);
            });
          } else {
            // 没有 orderId，直接切换
            performNavigation(targetStep);
          }
        },
      });
      return;
    }

    // 没有修改，直接切换
    performNavigation(targetStep);
  };

  const renderSidebarIcon = (status: string, badge?: string) => {
    if (status === "done" || status === "success") {
      return (
        <CheckCircleOutlined
          className={`${styles["status-icon"]} ${styles["status-check"]}`}
        />
      );
    } else if (status === "warn") {
      return (
        <ExclamationCircleOutlined
          className={`${styles["status-icon"]} ${styles["status-warn"]}`}
        />
      );
    } else if (badge) {
      return <span className={styles["status-badge"]}>{badge}</span>;
    }
    return null;
  };

  const renderFormContent = () => {
    if (currentTopStep === 1) {
      return (
        <FinancialForm
          ref={financialFormRef}
          data={orderData.financial || {}}
          onChange={(newData) => updateOrderData("financial", newData)}
          isFinancialInfoRequired={orderData.isFinancialInfoRequired}
          readOnly={readOnly}
          onToggleFinancialRequirement={handleFinancialRequirementToggle}
          canToggleFinancialRequirement={!isCorrectionMode}
          onOpenSelector={handleOpenSelector}
          activeSection={currentFinancialStep}
          orderData={orderData}
          zuuReferenceLatestFna={
            isZuuFinancialStepMode && currentFinancialStep === "fna"
              ? {
                  orderNo: latestCustomerHistoryFna?.orderNo,
                  signTime: latestCustomerHistoryFna?.signTime,
                  onReference: handleReferenceLatestZuuFna,
                }
              : null
          }
        />
      );
    } else if (currentTopStep === 2) {
      // Check if products were chosen in Financial Step based on isFinancialInfoRequired
      // If isFinancialInfoRequired is true, it means FNA is required, so product selection is restricted by FNA result.
      // If false, user can add/remove products freely.
      const isRestrictedByFna = orderData.isFinancialInfoRequired === true;

      return (
        <ProductInsuranceForm
          ref={productInsuranceFormRef}
          data={orderData.productPlans || []}
          onChange={(newData) => updateOrderData("productPlans", newData)}
          disableAddRemove={readOnly || isRestrictedByFna}
          readOnly={readOnly}
          onOpenSelector={handleOpenSelector}
          activeIndex={parseInt(activeProductKey)}
          onRemove={removeProductPlan}
          isFinancialInfoRequired={orderData.isFinancialInfoRequired}
          isCorrectionMode={isCorrectionMode}
          allowMainProductSelection={
            allowMainProductSelectionInOrderAuditEdit
          }
        />
      );
    } else if (currentTopStep === 3) {
      return (
        <OrderAttachmentForm
          orderData={orderData}
          onChange={(files) => updateOrderData("attachmentFiles", files)}
          readOnly={readOnly}
        />
      );
    } else if (currentTopStep === 4) {
      return (
        <OrderSignatureForm
          ref={signatureFormRef}
          signatures={orderData["_uiSignatures"] || {}}
          ossSignatures={orderData["_ossSignatures"] || {}}
          onSignatureChange={updateSignature}
          onSubmitOrder={handleSignatureSuccess}
          readOnly={readOnly}
          onSignStateChange={setSignatureState}
          scrollContainerRef={formAreaRef}
          location={locationData}
          locationLoading={locationLoading}
          onGetLocation={handleManualGetLocation}
          documents={sigDocuments}
          activeDocKey={sigActiveDocKey}
          onActiveDocChange={setSigActiveDocKey}
          readDocs={sigReadDocs}
          onReadDoc={handleSigMarkRead}
          signatureTypes={sigTypes}
          existingPhoto={sigPhoto}
          existingOssPhoto={sigOssPhoto}
          orderNo={orderData.orderNo}
          signatureNames={signatureNames}
        />
      );
    }

    switch (currentSidebarStep) {
      case "policyholder":
        return (
          <PersonForm
            ref={personFormRef}
            title="投保人"
            data={orderData.policyholder || createInitialPolicyholder()}
            onChange={(newData) => updateOrderData("policyholder", newData)}
            readOnly={readOnly}
            scrollContainer={formAreaRef.current}
            advisorValue={currentAdvisorUid}
            advisorOptions={advisorOptions}
            advisorEditable={canEditAdvisor}
            advisorLoading={advisorLoading}
            onAdvisorChange={handleAdvisorChange}
          />
        );
      case "policyholder2":
        if (!enablePolicyholder2) return null;
        return (
          <PersonForm
            ref={personFormRef}
            title="第二投保人"
            data={orderData.secondPolicyholder || createInitialPolicyholder()}
            onChange={(newData) =>
              updateOrderData("secondPolicyholder", newData)
            }
            readOnly={readOnly}
            scrollContainer={formAreaRef.current}
          />
        );
      case "insured":
        return (
          <PersonForm
            ref={personFormRef}
            title="受保人"
            data={orderData.insured || createInitialInsured()}
            onChange={(newData) => updateOrderData("insured", newData)}
            policyholder={orderData.policyholder}
            existingPersons={cachedInsureds}
            readOnly={readOnly}
            scrollContainer={formAreaRef.current}
          />
        );
      case "insured2":
        if (!enableInsured2) return null;
        return (
          <PersonForm
            ref={personFormRef}
            title="第二受保人"
            data={orderData.secondInsured || createInitialInsured()}
            onChange={(newData) => updateOrderData("secondInsured", newData)}
            policyholder={orderData.policyholder}
            existingPersons={cachedInsureds}
            readOnly={readOnly}
            scrollContainer={formAreaRef.current}
          />
        );
      case "beneficiary":
        return (
          <BeneficiaryForm
            ref={beneficiaryFormRef}
            data={orderData.beneficiaries || []}
            onChange={(newData) => updateOrderData("beneficiaries", newData)}
            existingBeneficiaries={cachedBeneficiaries}
            orderId={orderId ? parseInt(orderId, 10) : undefined}
            onRefresh={refreshOrder}
            readOnly={readOnly}
            policyholder={orderData.policyholder}
          />
        );
      default:
        return null;
    }
  };

  // Calculate enabled sidebar items to determine if current step is the last one
  const enabledSidebarItems = sidebarItems.filter((item) => {
    if (item.key === "policyholder2") return enablePolicyholder2;
    if (item.key === "insured2") return enableInsured2;
    return true; // policyholder, insured, beneficiary are always enabled
  });
  const isLastSidebarStep =
    enabledSidebarItems.length > 0 &&
    currentSidebarStep ===
      enabledSidebarItems[enabledSidebarItems.length - 1].key;
  const isFirstPage =
    currentTopStep === 0 &&
    enabledSidebarItems.length > 0 &&
    currentSidebarStep === enabledSidebarItems[0].key;
  const isAttachmentStep = currentTopStep === 3;
  const isSignatureStep = currentTopStep === 4;

  let nextButtonText = "確認，下一頁 ";
  if (readOnly) {
    nextButtonText = "下一頁";
  }
  // 批改模式下的产品投保步骤，显示"完成批改"
  else if (isCorrectionMode && currentTopStep === 2) {
    nextButtonText = "確認修改內容";
  }
  // else if (isDraftMode) {
  //   nextButtonText = "提交訂單";
  // }
  else if (currentTopStep === 0 && isLastSidebarStep && !orderId)
    nextButtonText = "提交訂單";
  // 草稿状态下，第二受保人是最后一项时，只显示"確認"
  // else if (isDraftMode && currentTopStep === 0 && currentSidebarStep === "insured2" && isLastSidebarStep) nextButtonText = "確認";
  else if (currentTopStep === 0 && isLastSidebarStep && orderId)
    nextButtonText = "確認，下一頁"; // 明确指定
  else if (isAttachmentStep) nextButtonText = "確認附件，去簽署";
  else if (isSignatureStep) {
    if (isSignatureUploaded) {
      nextButtonText = "提交已簽署訂單";
    } else if (signatureState.isLastDoc) {
      if (signatureState.countdown > 0) {
        nextButtonText = `請閱讀檔案 (${signatureState.countdown}s)`;
      } else if (!signatureState.isCurrentDocRead) {
        nextButtonText = "請滑動到底部";
      } else if (!signatureState.allDocsRead) {
        nextButtonText = "請閱讀所有文件";
      } else {
        nextButtonText = "簽署";
      }
    } else {
      if (signatureState.countdown > 0) {
        nextButtonText = `請閱讀檔案 (${signatureState.countdown}s)`;
      } else if (!signatureState.isCurrentDocRead) {
        nextButtonText = "請滑動到底部";
      } else {
        nextButtonText = "下一份";
      }
    }
  }

  return (
    <div className={styles["order-form-container"]}>
      {/* Fixed Header Section */}
      <div className={styles["sticky-header-container"]}>
        {/* Top Steps - 草稿状态和批改模式下不显示步骤条，订单审核状态显示步骤条 */}
        {!!orderId && !isDraftMode && (
          <div
            style={{ background: "#fff", borderBottom: "1px solid #f0f0f0" }}
          >
            <OrderSteps
              current={currentTopStep}
              steps={topSteps}
              onChange={handleTopStepChange}
            />
          </div>
        )}

        {/* Header */}
        {/* <header className={styles["order-header"]} style={{ height: "auto", minHeight: 50, padding: "8px 24px", borderBottom: "1px solid #eee" }}>
          <div className={styles["header-left"]}>
            <div className={styles["header-title"]}>
              <span className={styles["header-breadcrumb"]} onClick={() => navigate("/orders")}>
                訂單
              </span>
              <RightOutlined className={styles["header-separator"]} style={{ fontSize: "12px" }} />
              <span>{topSteps[currentTopStep]?.title || "新增訂單"}</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            {orderId && (
              <div className={styles["header-info"]}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                  <span className={`${styles["status-tag"]} ${styles[`status-${orderData.orderStatus || OrderStatus.Draft}`]}`}>
                    {ORDER_STATUS_DICT[orderData.orderStatus as OrderStatus] || "草稿"}
                  </span>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span className={styles["info-item"]} style={{ fontSize: 14 }}>
                      <span className={styles["info-label"]}>編號:</span> {orderData.orderNo || "-"}
                    </span>
                    {orderData.orderNo && (
                      <img
                        src={copyIcon}
                        alt="copy"
                        style={{ marginLeft: 4, cursor: "pointer", width: 14, height: 14 }}
                        onClick={() => {
                          navigator.clipboard.writeText(orderData.orderNo || "");
                          message.success("已複製");
                        }}
                      />
                    )}
                  </div>
                </div>
                <div style={{ width: 1, height: 32, background: "#E8E8E8", margin: "0 24px" }} />
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <span className={styles["info-item"]} style={{ fontSize: 14, lineHeight: "14px", color: "#999" }}>
                    投保人 / 受保人 :
                  </span>
                  <span className={styles["info-item"]} style={{ fontSize: 16, lineHeight: "20px", fontWeight: 500, color: "#333" }}>
                    {orderData.policyholder?.basic?.chineseSurname || "-"}
                    {orderData.policyholder?.basic?.chineseGivenName || "-"} <span style={{ color: "#E8E8E8", margin: "0 8px" }}>/</span> {orderData.insured?.basic?.chineseSurname || ""}{" "}
                    {orderData.insured?.basic?.chineseGivenName || "-"}
                  </span>
                </div>
                <div style={{ width: 1, height: 32, background: "#E8E8E8", marginLeft: 24 }} />
              </div>
            )}
            <div className={styles["header-close"]} onClick={handleBack} style={{ marginLeft: 16 }}>
              <CloseOutlined />
            </div>
          </div>
        </header> */}
      </div>

      {/* Main Layout */}
      <div
        className={
          currentTopStep === 2
            ? styles["main-layout-product"]
            : styles["main-layout"]
        }
      >
        {/* Sidebar - Only show for Step 0 */}
        {currentTopStep === 0 && (
          <div
            className={styles["sidebar"]}
            style={isDraftMode || isCorrectionMode ? { top: 21 } : undefined}
          >
            {sidebarItems.map((item) => {
              const isSwitchable =
                item.key === "policyholder2" || item.key === "insured2";
              const isEnabled =
                item.key === "policyholder2"
                  ? enablePolicyholder2
                  : item.key === "insured2"
                    ? enableInsured2
                    : true;

              return (
                <div
                  key={item.key}
                  className={`${styles["sidebar-item"]} ${currentSidebarStep === item.key ? styles["active"] : ""}`}
                  style={!isEnabled ? { opacity: 0.6 } : undefined}
                  onClick={() => {
                    if (isEnabled) handleSidebarClick(item.key);
                  }}
                >
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {isSwitchable && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Switch
                        size="large"
                        checked={isEnabled}
                        onChange={async (checked) => {
                          if (item.key === "policyholder2") {
                            setEnablePolicyholder2(checked);
                            if (checked) {
                              handleSidebarClick("policyholder2");
                              if (formAreaRef.current)
                                formAreaRef.current.scrollTop = 0;
                            } else {
                              if (orderId) {
                                try {
                                  setLoading(true);
                                  const numericOrderId = parseInt(orderId, 10);
                                  await updateSecondaryPolicyholder(
                                    numericOrderId,
                                    {
                                      draft: false,
                                      submit: false,
                                    },
                                    true,
                                  );
                                  // message.success("已清除第二投保人資料");
                                  await refreshOrder();
                                } catch (e) {
                                  console.error(e);
                                  // message.error("清除失敗");
                                } finally {
                                  setLoading(false);
                                }
                              }
                              // Auto-switch to beneficiary when second policyholder is disabled
                              setCurrentSidebarStep("beneficiary");
                              if (formAreaRef.current)
                                formAreaRef.current.scrollTop = 0;
                            }
                          } else {
                            setEnableInsured2(checked);
                            if (checked) {
                              handleSidebarClick("insured2");
                              if (formAreaRef.current)
                                formAreaRef.current.scrollTop = 0;
                            } else {
                              if (orderId) {
                                try {
                                  setLoading(true);
                                  const numericOrderId = parseInt(orderId, 10);
                                  await updateSecondInsured(
                                    numericOrderId,
                                    {
                                      draft: false,
                                      submit: false,
                                    },
                                    true,
                                  );
                                  // message.success("已清除第二受保人資料");
                                  await refreshOrder();
                                } catch (e) {
                                  console.error(e);
                                  // message.error("清除失敗");
                                } finally {
                                  setLoading(false);
                                }
                              }
                              // Auto-switch to beneficiary when second insured is disabled
                              setCurrentSidebarStep("beneficiary");
                              if (formAreaRef.current)
                                formAreaRef.current.scrollTop = 0;
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                  {isEnabled &&
                    !isSwitchable &&
                    renderSidebarIcon(item.status as string, item.badge)}
                </div>
              );
            })}
          </div>
        )}

        {/* Sidebar - Financial Info (Step 1) */}
        {currentTopStep === 1 &&
          (() => {
            // 计算财务两个部分的完成状态
            const financialInfoStatus = getFinancialStatus(
              "financial-info",
              orderData?.financial,
              orderData?.isFinancialInfoRequired,
            );
            const fnaStatus = getFinancialStatus(
              "fna",
              orderData?.financial,
              orderData?.isFinancialInfoRequired,
            );

            return (
              <div
                className={`${styles["sidebar"]} ${isZuuFinancialStepMode ? styles.zuuFinancialSidebar : ""}`}
                style={
                  isDraftMode || isCorrectionMode ? { top: 21 } : undefined
                }
              >
                {!isZuuFinancialStepMode && (
                  <div
                    className={styles["sidebar-item"]}
                    style={{ cursor: "default" }}
                  >
                    <span style={{ flex: 1, fontSize: 14 }}>
                      是否需要填寫財務資料
                    </span>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={!!orderData?.isFinancialInfoRequired}
                        onChange={handleFinancialRequirementToggle}
                        disabled={readOnly || isCorrectionMode}
                      />
                    </div>
                  </div>
                )}

                {(isZuuFinancialStepMode ||
                  orderData?.isFinancialInfoRequired) && (
                  <>
                    <div
                      className={`${styles["sidebar-item"]} ${isZuuFinancialStepMode ? styles.zuuFinancialSidebarItem : ""} ${currentFinancialStep === "financial-info" ? styles["active"] : ""}`}
                      onClick={() => handleFinancialStepClick("financial-info")}
                    >
                      <div className={styles["sidebar-item-content"]}>
                        <span className={`${styles["product-name"]} ${isZuuFinancialStepMode ? styles.zuuFinancialSidebarLabel : ""}`}>
                          {isZuuFinancialStepMode ? "財務資料" : "基本財務資料"}
                        </span>
                      </div>
                      {renderSidebarIcon(financialInfoStatus.status)}
                    </div>

                    {orderData?.isFinancialInfoRequired && (
                      <div
                        className={`${styles["sidebar-item"]} ${isZuuFinancialStepMode ? styles.zuuFinancialSidebarItem : ""} ${currentFinancialStep === "fna" ? styles["active"] : ""}`}
                        onClick={async () => {
                          if (needLocate === null) {
                            message.info("正在載入定位配置，請稍候");
                            return;
                          }

                          if (!readOnly && !isCorrectionMode && needLocate && !hasLocationSucceeded) {
                            const locationSuccess =
                              await triggerLocationAndWait();
                            if (!locationSuccess) {
                              return;
                            }
                          }
                          handleFinancialStepClick("fna");
                        }}
                      >
                        <div className={styles["sidebar-item-content"]}>
                          <span className={`${styles["product-name"]} ${isZuuFinancialStepMode ? styles.zuuFinancialSidebarLabel : ""}`}>
                            {isZuuFinancialStepMode ? "FNA" : "財務需要分析"}
                          </span>
                        </div>
                        {renderSidebarIcon(fnaStatus.status)}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

        {/* Sidebar - Product Plans (Step 2) */}
        {currentTopStep === 2 && (
          <div className={styles["product-sidebar"]}>
            {(orderData?.productPlans || []).map((plan, index) => {
              // 判断产品计划是否完成
              const isCompleted =
                plan.productCode &&
                plan.companyCode &&
                plan.currency &&
                plan.sumAssured !== undefined &&
                plan.sumAssured !== null &&
                plan.sumAssured !== "";

              return (
                <div
                  key={index}
                  className={`${styles["product-sidebar-item"]} ${activeProductKey === String(index) ? styles["product-sidebar-item-active"] : ""}`}
                  onClick={() => handleProductTabChange(index)}
                >
                  <div className={styles["product-sidebar-item-content"]}>
                    <span className={styles["product-sidebar-name"]}>
                      {plan.productName || `計劃 ${index + 1}`}
                    </span>
                    <div className="flex">
                      {plan.companyName && (
                        <span className={styles["product-sidebar-sub-info"]}>
                          {plan.companyName}
                        </span>
                      )}{" "}
                      {
                        <span className={styles["product-sidebar-sub-info"]}>
                          {getCountryName(plan.region)}
                        </span>
                      }
                    </div>
                  </div>
                  {/* 显示完成状态图标 */}
                  {isCompleted ? (
                    <CheckCircleOutlined
                      className={`${styles["status-icon"]} ${styles["status-check"]}`}
                    />
                  ) : (
                    <ExclamationCircleOutlined
                      className={`${styles["status-icon"]} ${styles["status-warn"]}`}
                    />
                  )}
                </div>
              );
            })}
            {!readOnly && !(orderData?.isFinancialInfoRequired === true) && !isCorrectionMode && (
              <div
                className={styles["product-sidebar-add-btn"]}
                onClick={handleAddProduct}
              >
                <PlusOutlined style={{ fontSize: "24px" }} />
              </div>
            )}
          </div>
        )}

        {/* Sidebar - Signature (Step 4) */}
        {currentTopStep === 4 && (
          <div
            className={styles["sidebar"]}
            style={isDraftMode || isCorrectionMode ? { top: 21 } : undefined}
          >
            {sigLoadingConfig ? (
              <div style={{ padding: 20, textAlign: "center" }}>
                <Spin />
              </div>
            ) : (
              sigDocuments.map((doc, index) => {
                const docKey = doc.key || doc.formType || doc.type || "";
                const isSelected = docKey === sigActiveDocKey;
                const isRead = sigReadDocs.has(docKey);
                const status = isRead ? "done" : "warn";

                // Determine accessibility:
                // 1. First doc always accessible
                // 2. Or previous doc is read
                // 3. Or current doc is already read (re-visiting)
                // 4. Or ReadOnly mode
                const prevDocKey =
                  index > 0
                    ? sigDocuments[index - 1].key ||
                      sigDocuments[index - 1].formType ||
                      sigDocuments[index - 1].type ||
                      ""
                    : "";
                const isAccessible =
                  readOnly ||
                  index === 0 ||
                  sigReadDocs.has(prevDocKey) ||
                  isRead;

                return (
                  <div
                    key={docKey}
                    className={`${styles["sidebar-item"]} ${isSelected ? styles["active"] : ""}`}
                    style={
                      !isAccessible
                        ? { opacity: 0.6, cursor: "not-allowed" }
                        : undefined
                    }
                    onClick={() => {
                      if (isAccessible) setSigActiveDocKey(docKey);
                      else message.warning("請先閱讀完上一份文件");
                    }}
                  >
                    <div className={styles["sidebar-item-content"]}>
                      {doc.name}
                    </div>
                    {!readOnly && renderSidebarIcon(status)}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Content Area */}
        <div
          className={styles["form-area"]}
          ref={formAreaRef}
          style={currentTopStep === 4 ? { overflow: "hidden" } : undefined}
        >
          {/* {currentTopStep === 0 && <div className={styles["warning-banner"]}>不確定的部分，請選擇或者填寫 "待確認" ；帶 * 的為必填項。</div>} */}

          {currentTopStep === 1 && isZuuFinancialStepMode && (
            <>
              <div className={styles.zuuFinancialNotice}>
                不確定的部分，請選擇或者填寫 "待確認"；帶 * 的為必填項。
              </div>
              {currentFinancialStep === "financial-info" && (
                <div className={styles.zuuFinancialRequirementRow}>
                  <div className={styles.zuuFinancialRequirementLabel}>
                    是否需要填寫財務資料
                  </div>
                  <Switch
                    checked={!!orderData?.isFinancialInfoRequired}
                    onChange={handleFinancialRequirementToggle}
                    disabled={readOnly || isCorrectionMode}
                  />
                </div>
              )}
            </>
          )}

          {currentTopStep === 2 ||
          currentTopStep === 3 ||
          currentTopStep === 4 ? (
            renderFormContent()
          ) : (
            <div className={styles["form-content"]}>{renderFormContent()}</div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        className={styles["footer-bar"]}
        data-correction-mode={isCorrectionMode}
        data-draft-mode={isDraftMode}
      >
        <div className={styles["footer-left"]}>
          <CustomButton
            bgColor="#fff"
            color="#666"
            style={{ border: "1px solid #d9d9d9" }}
            height={35}
            onClick={handleClose}
            disabled={loading}
          >
            關閉
          </CustomButton>
          {!readOnly &&
            (!orderId || orderData?.orderStatus === OrderStatus.Draft) &&
            !isSignatureStep &&
            currentTopStep !== 1 && (
              <CustomButton
                bgColor="#fff"
                color="#666"
                style={{ border: "1px solid #d9d9d9" }}
                height={35}
                onClick={() => handleSubmit(true)}
                loading={loading}
              >
                {currentTopStep === 2 ? "儲存草稿" : "儲存"}
              </CustomButton>
            )}
          {currentTopStep === 1 &&
            currentFinancialStep === "fna" &&
            !readOnly && (
              <CustomButton
                bgColor="#fff"
                color="#666"
                style={{ border: "1px solid #d9d9d9" }}
                height={35}
                onClick={async () => {
                  handleSubmit(true);
                }}
                loading={loading}
              >
                暫存
              </CustomButton>
            )}
          {!isFirstPage && (
            <CustomButton
              bgColor="#fff"
              color="#666"
              style={{ border: "1px solid #d9d9d9" }}
              height={35}
              onClick={handleBack}
              disabled={loading}
            >
              返回上一頁
            </CustomButton>
          )}
        </div>
        <div className={styles["footer-right"]}>
          {/* Signature Step: Show both "Sign" and "Complete" buttons */}
          {isSignatureStep && !readOnly ? (
            <>
              <CustomButton height={35} onClick={handleDownloadAllSignedFiles}>
                下載所有已簽署文件
              </CustomButton>
              {/* Button 1: Sign / Next Document */}
              <CustomButton
                height={35}
                bgColor="#fff"
                color="var(--theme-primary)"
                style={{ border: "1px solid var(--theme-primary)" }}
                onClick={
                  signatureState.isLastDoc
                    ? () => signatureFormRef.current?.openSignModal()
                    : () => signatureFormRef.current?.nextDoc()
                }
                loading={loading}
                disabled={
                  loading ||
                  (signatureState.isLastDoc
                    ? !signatureState.canSign
                    : !signatureState.isCurrentDocRead)
                }
              >
                {signatureState.isLastDoc
                  ? signatureState.countdown > 0
                    ? `請閱讀檔案 (${signatureState.countdown}s)`
                    : !signatureState.isCurrentDocRead
                      ? "請滑動到底部"
                      : !signatureState.allDocsRead
                        ? "請閱讀所有文件"
                        : "簽署"
                  : signatureState.countdown > 0
                    ? `請閱讀檔案 (${signatureState.countdown}s)`
                    : !signatureState.isCurrentDocRead
                      ? "請滑動到底部"
                      : "下一份"}
              </CustomButton>

              {/* Button 2: Complete Signature (only show when signature is uploaded) */}
              {isSignatureUploaded && (
                <CustomButton
                  height={35}
                  onClick={handleSignatureSubmit}
                  loading={loading}
                  disabled={loading}
                >
                  提交已簽署訂單
                </CustomButton>
              )}
            </>
          ) : (
            /* Other steps: Original button logic */
            <>
              {/* 財務資料步骤 */}
              {currentTopStep === 1 && !readOnly && (
                <>
                  {/* financial-info 页面 */}
                  {currentFinancialStep === "financial-info" && (
                    <>
                      {/* 如果有 FNA 需求，显示"確認，下一頁"进入 FNA，同时显示"保存"按钮 */}
                      {orderData?.isFinancialInfoRequired && (
                        <>
                          <CustomButton
                            height={35}
                            onClick={handleNext}
                            loading={loading}
                            disabled={loading}
                          >
                            確認，下一頁
                          </CustomButton>
                          <CustomButton
                            height={35}
                            onClick={async () => {
                              handleSubmit(false);
                            }}
                            loading={loading}
                          >
                            {isDraftMode ? "提交訂單" : "儲存"}
                          </CustomButton>
                        </>
                      )}
                      {/* 如果没有 FNA 需求，这是最后一个子页面，只显示"確認，下一頁"按钮（会调用保存逻辑） */}
                      {!orderData?.isFinancialInfoRequired && (
                        <CustomButton
                          height={35}
                          onClick={handleNext}
                          loading={loading}
                          disabled={loading}
                        >
                          確認，下一頁
                        </CustomButton>
                      )}
                    </>
                  )}

                  {/* fna 页面 */}
                  {currentFinancialStep === "fna" && (
                    <>
                      <CustomButton
                        height={35}
                        onClick={handleNext}
                        loading={loading}
                        disabled={loading}
                      >
                        確認，下一頁
                      </CustomButton>
                      <CustomButton
                        height={35}
                        onClick={async () => {
                          handleSubmit(false);
                        }}
                        loading={loading}
                      >
                        儲存
                      </CustomButton>
                    </>
                  )}
                </>
              )}
              {/* 其他步骤（Step 0 和 Step 2）的按钮 */}
              {currentTopStep !== 1 && !readOnly && (
                <>
                  {/* 下一步按钮 - Step 0 和 Step 2/3 */}
                  <CustomButton
                    height={35}
                    onClick={handleNext}
                    loading={loading}
                    disabled={loading}
                  >
                    {nextButtonText}
                  </CustomButton>

                  {/* 提交订单/暂存按钮 - Step 0 和 Step 2 */}
                  {/* 只在非最后一个子流程时显示保存按钮 */}
                  {currentTopStep <= 2 && (
                    <>
                      {/* Step 0: 只在非最后一个侧边栏项目时显示 */}
                      {currentTopStep === 0 && !isLastSidebarStep && (
                        <CustomButton
                          height={35}
                          onClick={async () => {
                            handleSubmit(false);
                          }}
                          loading={loading}
                        >
                          {isCorrectionMode ? "提交訂單" : "儲存"}
                        </CustomButton>
                      )}
                      {/* Step 2: 不显示保存按钮（已经是最后一步，通过"確認附件，去簽署"保存） */}
                    </>
                  )}
                </>
              )}

              {/* ReadOnly模式的下一步按钮 - 排除财务资料步骤 */}
              {readOnly &&
                currentTopStep !== 1 &&
                currentTopStep < topSteps.length - 1 && (
                  <CustomButton
                    height={35}
                    onClick={handleNext}
                    loading={loading}
                    disabled={loading}
                  >
                    下一頁
                  </CustomButton>
                )}
            </>
          )}
        </div>
      </div>

      {/* Product Selection Modal */}
      <Modal
        open={productSelectorVisible}
        onCancel={() => setProductSelectorVisible(false)}
        footer={null}
        width={1000}
        centered
        styles={{
          body:
            selectorContext?.type !== "financial"
              ? { height: "80vh", padding: 0, overflow: "hidden" }
              : { height: "calc(80vh - 40px)", padding: 0, overflow: "hidden" },
          content:
            selectorContext?.type !== "financial"
              ? null
              : { borderRadius: "12px", height: "80vh" },
        }}
        destroyOnHidden
        closeIcon={
          <span style={{ fontSize: 20, marginRight: 16, marginTop: 16 }}>
            ✕
          </span>
        }
      >
        <ProductListContent
          selectMode
          onSelect={handleModalSelect}
          productType={selectorProductType}
          hideHeader={false}
          companyCode={
            // 如果是附加险选择，传递主险的保险公司代码
            selectorContext?.type === "rider" &&
            selectorContext.index !== undefined
              ? orderData?.productPlans?.[selectorContext.index]?.companyCode
              : undefined
          }
          orderFnaDTO={financialSelectorOrderFnaDTO}
          isFna={selectorContext?.type === "financial"}
          showFnaTip={selectorContext?.type === "financial"}
        />
      </Modal>
    </div>
  );
}
